/**
 * L1J Web Client - Game Scene
 * 메인 게임 씬 - 아이소메트릭(2:1 다이아몬드) 타일 기반 게임 화면
 *
 * 좌표 체계 규칙 (반드시 지킬 것):
 *  - 위치의 "진실"은 언제나 서버 타일 좌표(정수)다. 플레이어/엔티티는 논리
 *    타일 좌표(tileX, tileY)를 필드로 들고 있고, Phaser 스프라이트의 x/y는
 *    tileToWorld()로 계산한 파생값에 불과하다.
 *  - 서버로 보내는 좌표는 절대 화면 픽셀이 아니다. 항상 타일 좌표.
 *  - 화면 좌표 -> 타일 변환은 반드시 camera.getWorldPoint()로 월드 좌표를
 *    구한 뒤 worldToTile()을 쓴다.
 */

import Phaser from 'phaser';
import { ConnectionManager } from '../../network/ConnectionManager';
import { ServerConfig } from '../../config';
import { gameEvents, networkEvents } from '../../core/EventEmitter';
import { gameState } from '../../core/GameState';
import { PlayerDetailStats } from '../../core/types';
import { AttackResultPayload } from '../../types/network';
import { storageManager, StoredQuickSlot } from '../../core/StorageManager';
import { mapCollision, PathNode, FindPathOptions } from '../../core/MapCollision';
import { StatusBar } from '../../ui/components/StatusBar';
import { MiniMap } from '../../ui/components/MiniMap';
import { ChatBox } from '../../ui/components/ChatBox';
import { Inventory, InventoryItem } from '../../ui/components/Inventory';
import { CharacterStatsPanel, CharacterStatsData } from '../../ui/components/CharacterStatsPanel';
import { CharacterDetailPanel } from '../../ui/components/CharacterDetailPanel';
import { EquipmentPanel } from '../../ui/components/EquipmentPanel';
import { HtmlDialog } from '../../ui/components/HtmlDialog';
import { TargetPrompt } from '../../ui/components/TargetPrompt';
import { BookmarkDialog } from '../../ui/components/BookmarkDialog';
import { IdentifyDialog } from '../../ui/components/IdentifyDialog';
import { QuantityDialog } from '../../ui/components/QuantityDialog';
import { BookmarkEntry, IdentifyDescPayload } from '../../types/network';
import { serverChatChannelName } from '../../network/PacketHandler';
import { equippedWeaponType, isEquippedName, stripEquippedSuffix } from '../../utils/equipment';
import { QuickSlotBar, QuickSlotItem } from '../../ui/components/QuickSlotBar';
import { MenuButton } from '../../ui/components/MenuButton';
import { MapTheme, mapTheme } from '../../data/mapThemes';
import {
  HALF_TILE_HEIGHT,
  HALF_TILE_WIDTH,
  HEADING_VECTORS,
  headingFromTileDelta,
  tileDepth,
  tileDiamondPoints,
  tileDistance,
  tileLineDistance,
  tileToWorld,
  worldToTile,
  worldToTileFloat,
} from '../../utils/isometric';
// 이름/메시지 해석은 utils/nameResolver 한 곳에서만 한다.
// data/serverMessages의 동명 함수와 달리 인자에 섞인 `$숫자` 이름 ID까지 치환한다
// ("서버 메시지 #129", "$23"처럼 날것으로 찍히던 문제).
import {
  formatServerMessage,
  resolveItemName,
  resolveMessageArg,
  resolveNpcName,
  stripCountSuffix,
  stripNpcTitle,
} from '../../utils/nameResolver';
// gfxId → 한글 이름 폴백표(자동 생성). 서버가 보내는 name_id가 비어 있는 NPC가
// 179종 있어 그대로 두면 화면에 'M' 한 글자만 남는다. resolveEntityLabels() 참고.
import { NPC_NAME_BY_GFX } from '../../data/npcGfxNames';
// 바닥에 놓인 아이템도 인벤토리와 같은 23종 카테고리 아이콘을 쓴다 — 같은 물건이
// 손에 있을 때와 바닥에 있을 때 다른 그림이면 알아볼 수 없다. 다만 **조회 표는
// 다르다**: 서버가 S_DropItem에 싣는 번호는 inv_gfx_id가 아니라 grd_gfx_id라서
// 인벤토리 표로 찾으면 값만 겹치는 엉뚱한 아이콘이 나온다(groundItemIconKind).
import { groundItemIconKind, itemIconTextureKey, loadItemIcon } from '../../data/itemIcons';
import {
  CharacterId,
  DEATH_FRAME_COUNT,
  DEATH_FRAME_DURATION_MS,
  Direction,
  FRAME_COUNT,
  WeaponType,
  calcDeathFrame,
  headingToDirection,
  inferCharacter,
  inferWeapon,
} from '../../utils/spriteLoader';
import {
  MobFamily,
  inferMobFamily,
  loadMobImage,
  loadMobSprite,
  mobImageFor,
  mobImageFrameIndex,
  mobImageTextureKey,
  mobTextureKey,
  mobTint,
  preloadMobFamily,
} from '../../utils/mobSprites';
import type { MobImageMeta } from '../../data/mobImages';
import {
  applyWalkSprite,
  applyDeathSprite,
  directionNeedsFlip,
  loadCharWalkAtlas,
  loadCharDeathAtlas,
} from '../../utils/atlasLoader';
import { SPR_SPEEDS, attackSpeedOf, moveSpeedOf } from '../../data/sprSpeeds';
import { logger } from '../../utils/logger';
import { introOverlay } from '../../ui/IntroOverlay';

// L1J 월드 좌표는 맵마다 0부터 시작하지 않고 절대 타일 좌표(최대 65535, 2바이트)를 쓴다.
// 예: 이 서버의 마을 스폰 지점은 (33087, 33396) 근처 — 8192 같은 작은 값으로
// clamp하면 이동 시 좌표가 맵 밖으로 순간이동한 것처럼 잘려나간다.
const MAX_TILE = 65535;

/**
 * 원본 라니지1이 한 화면에 보여주는 월드 영역(px). 이 넓이만큼의 타일이 보이도록
 * 카메라 줌을 계산한다. 캔버스 해상도가 달라져도 보이는 타일 수가 유지되도록,
 * 줌 값을 직접 하드코딩하지 말고 언제나 viewZoomFor()로 계산할 것.
 *
 * 값은 원본 스크린샷에서 측정했다. 지형과 밀집 대형은 타일 격자에 정렬되므로
 * 가로 방향 자기상관에 주기가 남는다 — 20.5px 간격(20/41/61px)이 검출됐고,
 * 이는 타일 가로 간격 24px에 대응하므로 스크린샷은 원본의 0.854배, 즉 원본
 * 해상도는 640x480이다. 같은 스크린샷에서 하단 UI 바가 시작하는 지점이
 * 전체 높이의 324/412이므로, UI에 가려지지 않는 월드 영역은 640x377이 된다.
 *
 * 이 넓이는 타일 576px²(48x24 마름모) 기준 약 419타일이고, 화면 구석까지의
 * 맨해튼 거리가 약 17.7타일이라 서버가 데이터를 보내주는 19타일 안에 들어온다
 * (Point.isInScreen). 종전 기준이던 800x600은 세로로 25타일까지 벌어져서
 * 화면 위아래가 "서버가 아무것도 보내주지 않는 죽은 영역"이었다.
 */
const REFERENCE_VIEW_WIDTH = 640;
const REFERENCE_VIEW_HEIGHT = 377;

/**
 * 서버가 엔티티/채팅을 전달해주는 최대 맨해튼 타일 거리.
 * jp/l1j/server/types/Point.java isInScreen(): dist > 19 이면 보이지 않는다.
 * 이 범위 밖의 타일은 그리드를 흐리게 그려 사용자가 인지할 수 있게 한다.
 */
const SERVER_VISIBLE_TILE_RANGE = 19;

/**
 * 한 타일을 걷는 데 걸리는 시간(ms).
 *
 * 서버에는 타일 스텝을 시작할 때마다 C_MoveChar를 1회 보내므로 이 값이 그대로
 * 이동 패킷 전송 주기가 되고, **서버의 가속기 검사가 이 주기를 본다.**
 * 그래서 임의로 정할 수 없다 — 서버가 기대하는 값에서 역산해야 한다.
 *
 * ```
 * jp/l1j/server/datatables/SprTable.java
 *   calcActionSpeed = frameCount * 40 * (24 / frameRate)
 *   → spr 61(왕자) ACTION_Walk: 16 * 40 * (24/24) = 640ms      ← DB spr_actions
 *
 * jp/l1j/server/model/AcceleratorChecker.java
 *   CHECK_STRICTNESS = (Config.CHECK_STRICTNESS - 5) / 100 = (200-5)/100 = 1.95
 *   통과 조건: 실제간격 * 1.95 >= 640  →  실제간격 >= 328.2ms
 * ```
 *
 * **이 값은 서버 설정 `CheckStrictness`에 매달려 있다.** 서버가 102(=0.97)이던
 * 시절의 하한은 660ms여서 이 상수도 680이었다. 200(=1.95)으로 완화하면서 하한이
 * 328.2ms로 반토막 났고, 그에 맞춰 340으로 내렸다 — 원작 체감 속도에 훨씬 가깝다.
 * 서버를 되돌리면 여기도 반드시 되돌려야 한다 (`SERVER_CHECK_STRICTNESS` 주석 참조).
 *
 * 280ms였을 때는 매 스텝이 위반(rate 0.42)이라 `InjusticeCount`(10)가 차는
 * 10타일째부터 "Overspeed detected"가 떴고, GM이 아닌 계정은 `PunishmentType 0`에
 * 따라 10초 뒤 강제 접속 종료됐다. 지금 값은 하한 328.2ms에 약 3.6%(11.8ms)의
 * 여유를 둔 것이다. 비율은 예전(680/660 = 3.0%)보다 낫지만 **절대 여유는
 * 20.2ms → 11.8ms로 줄었다.** 송신 간격을 벽시계로 재는 이유가 그만큼 더
 * 중요해졌다 (`stepStartedAt` 주석 참조).
 *
 * 헤이스트/브레이브는 서버 쪽 기준을 0.75배(480ms → 하한 246.2ms)로 낮추므로,
 * 이 값으로 걷는 한 어떤 버프 상태에서도 검사에 걸리지 않는다
 * (대신 버프를 받아도 빨라지지는 않는다).
 */
const MS_PER_TILE = 340;

/**
 * ==================== 서버 가속기 검사(AcceleratorChecker) 대응 ====================
 *
 * `MS_PER_TILE`이 이동 쪽 답이라면, 이 아래는 **공격 쪽 답**이다.
 *
 * 서버는 MOVE / ATTACK / SPELL을 각각 따로 재지만, 위반 카운터
 * (`_injusticeCount`)는 **셋이 공용**이다 (AcceleratorChecker.checkInterval).
 * 그래서 걸음이 아무리 규정을 지켜도 공격이 빠르면 같은 카운터가 차고,
 * 똑같이 "Overspeed detected. Disconnect the connection after 10 seconds."가
 * 뜬 뒤 비-GM 계정은 10초 만에 끊긴다.
 *
 * 실측: 걷기는 60초 84스텝 최소 간격 682ms로 위반 0건이었지만, 같은 캐릭터로
 * 0.4초 간격 공격을 21회 하자 곧바로 위 메시지가 왔다. 클라이언트에 공격 송신
 * 간격 제한이 아예 없었기 때문이다.
 *
 * 서버 판정 (AcceleratorChecker.java):
 * ```
 *   interval = 도착시각 - 직전 도착시각
 *   rate     = (interval * CHECK_STRICTNESS) / rightInterval
 *   rate < 1.0 이면 위반 1회, 10회 누적되면 처벌 (정상 4회 연속이면 리셋)
 * ```
 * 즉 통과 조건은 `interval >= rightInterval / CHECK_STRICTNESS`다.
 * `rightInterval`은 DB `spr_actions`에서 나온 값으로 `src/data/sprSpeeds.ts`에
 * 그대로 담아 두었다 (공격 act_id = 무기코드 + 1, 이동 act_id = 무기코드).
 */
/**
 * `(Config.CHECK_STRICTNESS - 5) / 100` = `(200 - 5) / 100` = 1.95.
 *
 * ⚠️ **서버 `config/server.properties.xml`의 `CheckStrictness`와 반드시 같이 움직여야 한다.**
 * 이 상수는 서버 값의 *미러*일 뿐 클라이언트가 정하는 값이 아니다. 한쪽만 고치면:
 *
 * - 서버를 낮췄는데(엄격해졌는데) 여기를 안 낮추면 → 게이트가 서버 하한보다 헐거워져
 *   이동·공격이 규정보다 빨리 나가고, 위반 10회 누적 후 **강제 접속 종료**된다.
 * - 서버를 올렸는데 여기를 안 올리면 → 손해는 없지만 필요 이상으로 굼떠진다.
 *
 * 값이 **클수록 느슨하다**(rate = interval * STRICTNESS / rightInterval, rate >= 1이 통과).
 * 서버가 이 값을 클라이언트에게 알려주는 패킷은 없으므로 자동 동기화가 불가능하다 —
 * 서버 설정을 고칠 때 이 파일도 같이 고치는 수밖에 없다.
 */
const SERVER_CHECK_STRICTNESS = 1.95;

/**
 * 공격 게이트의 여유분.
 *
 * 서버는 **우리가 보낸 시각이 아니라 도착 시각**으로 잰다. 앞 패킷이 늦게 도착하고
 * 뒤 패킷이 제때 도착하면 두 패킷이 압축되어 규정을 지켰는데도 위반이 된다.
 * 따라서 여유분은 "흡수할 수 있는 지터의 크기"와 같다.
 *
 * 15%로 잡은 근거 (수치는 서버 `CheckStrictness = 200`, 즉 STRICTNESS 1.95 기준):
 *  1. 가장 빠른 공격(기본값 800ms)에서도 절대 여유가 800/1.95*0.15 ≈ 62ms가 되어
 *     터널 경유 배포(Cloudflare Quick Tunnel — web-client/CLAUDE.md 참조)나 SVG
 *     텍스처 디코드로 메인 스레드가 잠깐 멎는 정도는 삼킨다.
 *     (STRICTNESS가 0.97이던 시절에는 같은 15%가 124ms였다. 서버가 느슨해지면서
 *      요구 간격 자체가 절반이 되었으므로 **같은 비율이라도 절대 여유는 절반이다.**)
 *  2. 위반 카운터가 이동과 공용인데 이동 쪽 여유는 340/328.2 = 3.6%(11.8ms)뿐이다.
 *     공격이 그 카운터에 한 칸도 보태지 않아야 한다.
 *  3. 이동의 3%대는 "실측으로 위반 0건"이 확인된 값이지만, 그건 로컬 직결에서다.
 *     공격은 그런 실측 근거가 없으므로 더 두껍게 잡는다.
 *  4. 대가는 공격 속도 15% 손해뿐이고, 반대쪽 대가는 강제 접속 종료다.
 *
 * **Slow는 덮지 못한다.** 서버는 슬로우 상태에서 `interval /= 0.75`로 요구를
 * 1.33배 늘리는데(getRightInterval), 그러려면 여유가 33% 이상이어야 한다.
 * 그런데 클라이언트는 자기가 슬로우인지 알 방법이 없다 — GameState가 들고 있는
 * 상태이상은 S_Paralysis(마비/수면/빙결/스턴/속박)뿐이고 버프/디버프 목록은
 * 받지도 저장하지도 않는다(`add_skill`을 구독하는 코드가 없다).
 * 게다가 슬로우 중에는 **이동 쪽이 먼저 걸린다** — 요구가 640/0.75/1.95 ≈ 438ms로
 * 늘어 340ms인 MS_PER_TILE로는 어차피 매 걸음이 위반이다(서버가 0.97이던 시절에도
 * 880ms 대 680ms로 똑같이 걸렸다 — 완화해도 이 관계는 바뀌지 않는다).
 * 공격만 33% 더 늦춰 봐야 사는 게 없으므로 여기서는 덮지 않는다. 슬로우까지
 * 막으려면 서버가 버프 상태를 알려주는 경로(S_SkillHaste/S_PacketBox 등)를
 * 게이트웨이가 번역해 줘야 한다.
 */
const ATTACK_GATE_MARGIN = 1.15;

/**
 * 이동 게이트의 여유분.
 *
 * 3%는 서버가 엄격(STRICTNESS 0.97)하던 시절 60초 연속 이동에서 위반 0건으로
 * 실측된 값이라 그대로 유지한다. 서버가 느슨해졌다고 여유를 줄일 이유는 없다 —
 * 이 배율이 흡수하는 것은 서버의 엄격도가 아니라 **패킷 도착 지터**이기 때문이다.
 *
 * 확인된 플레이어 gfx는 전부 보행 640ms이므로 하한은 640/1.95*1.03 = 338.1ms가
 * 되고, MS_PER_TILE(340)이 그보다 크므로 **실효값은 여전히 MS_PER_TILE이다.**
 * (여유 1.9ms. 서버가 0.97이던 시절에는 679.6 대 680으로 0.4ms 차였으니 오히려
 *  조금 늘었다.) 앞으로 보행 속도가 640보다 느린 gfx가 들어오면 그때만 하한이
 * MS_PER_TILE을 밀어올린다.
 */
const MOVE_GATE_MARGIN = 1.03;

/**
 * 표에 없는 gfx일 때 쓸 안전 폴백(ms) — 표 전체에서 **가장 느린** 값.
 *
 * 모르는 값을 빠른 쪽으로 폴백하면 그대로 강제 접속 종료다. 반대로 느린 쪽으로
 * 폴백하면 손해는 공격/이동이 잠깐 굼떠지는 것뿐이다.
 * `attackSpeedOf`/`moveSpeedOf`가 null을 돌려주는 경우는 두 가지다 —
 * 표에 없는 spr, 그리고 해당 spr에 ACTION_Attack(1)/ACTION_Walk(0)조차 없는 경우.
 * (서버는 이 자리에서 0을 돌려 사실상 무제한이 되지만, 클라이언트가 그걸 흉내내면
 *  안 된다. sprSpeeds.ts의 주석 참조.)
 */
const SLOWEST_ATTACK_MS = slowestOf('attack');
const SLOWEST_MOVE_MS = slowestOf('move');

/**
 * 인벤토리에서 알아낸 무기 종류(WeaponType) → 서버 무기코드(`getCurrentWeapon()`) 후보.
 *
 * **`gameState.getPlayerWeapon()`만 믿으면 안 된다.** 그 값의 유일한 출처는
 * `char_visual_update`(S_CharVisualUpdate, opcode 113)인데, 서버는 **장착/해제
 * 시점에 이 패킷을 보내지 않는다** — 보내는 곳은 텔레포트(Teleportation.java:106),
 * 리스타트(C_Restart), 낚시, 변신뿐이다. `L1EquipmentSlot`은
 * `setCurrentWeapon()`만 하고 아무 패킷도 태우지 않는다. 그래서 활을 든 채
 * 접속하면 세션 내내 `getPlayerWeapon()`이 0(맨손)으로 남고, 게이트는 맨손
 * 공격속도(spr 61 기준 800ms)로 계산된다. 서버는 활 공격속도(act 21 = 1440ms)를
 * 요구하므로 **모든 공격이 위반**이 되어 10회마다 강제 접속 종료가 뜬다.
 * (2026-08-12 실측: 활 장착 상태에서 간격 952~1371ms로 30여 회 공격 → Overspeed 2회.)
 *
 * 그래서 씬이 이미 스프라이트·사거리 판정에 쓰고 있는 인벤토리 유래 값
 * (`equippedWeaponType` — 표시명의 `($9)` 접미사)을 같이 본다. 다만 이쪽은
 * 원본 그림이 네 갈래뿐이라 근접 무기가 전부 'sword'로 접혀 있어 정확한
 * 무기코드를 알 수 없다. 그러니 **후보 전부에서 가장 느린 값**을 고른다 —
 * 느린 쪽으로 틀리면 공격이 굼떠질 뿐이고, 빠른 쪽으로 틀리면 접속이 끊긴다.
 *
 * 숫자는 서버 `ActionCodes`의 걷기 act_id이자 곧 무기 type1이다
 * (`L1EquipmentSlot`: `setCurrentWeapon(weapon.getItem().getType1())`).
 * 공격 act_id는 여기에 +1 한 값이다.
 */
const WEAPON_CODE_CANDIDATES: Readonly<Record<WeaponType, readonly number[]>> = {
  // 맨손. ACTION_Walk = 0 → ACTION_Attack = 1.
  none: [0],
  // 활. ACTION_BowWalk = 20 → ACTION_BowAttack = 21.
  bow: [20],
  // 지팡이. ACTION_StaffWalk = 40 → ACTION_StaffAttack = 41.
  staff: [40],
  // 'sword'는 "근접 무기인데 어느 것인지 모른다"는 뜻이다 (weaponTypes.ts가
  // 창·도끼·단검·양손검을 전부 검 그림으로 접는다). 근접 계열 전부를 후보로
  // 두고 가장 느린 것을 고른다.
  sword: [4, 11, 24, 46, 50, 54, 58, 62],
};

function slowestOf(kind: 'attack' | 'move'): number {
  let slowest = 0;
  for (const spr of Object.values(SPR_SPEEDS)) {
    for (const speed of Object.values(spr[kind])) {
      if (speed > slowest) slowest = speed;
    }
  }
  return slowest;
}

/**
 * 깊이 정렬 배율/기준점.
 *
 * 아이소메트릭에서는 (tx + ty)가 클수록 화면 아래 = 카메라에 가까우므로 나중에
 * 그려야 한다. 다만 tileDepth()의 절대값은 최대 131070까지 커서 그대로 depth로
 * 쓰면 UI(depth 90~200)를 덮어버린다. 그래서 "플레이어 타일의 깊이"를 원점으로
 * 삼아 상대값으로 환산하고(DEPTH_SCALE), 안전 범위로 clamp한다.
 * 플레이어가 한 타일 움직일 때마다 원점이 갱신되므로 절대 좌표가 아무리 커도
 * 엔티티 depth는 항상 0 근처에 머문다.
 */
const DEPTH_SCALE = 0.01;
const DEPTH_LIMIT = 50;
/** 이름표/HP바는 자기 본체보다 살짝 위(= 큰 depth)에 그린다. 한 타일 간격보다 작아야 한다. */
const LABEL_DEPTH_BONUS = DEPTH_SCALE / 2;

/** 타일 그리드의 depth (모든 엔티티보다 확실히 아래). */
const TILE_GRID_DEPTH = -100;

/*
 * 벽 블록 (2.5D 입체감).
 *
 * 맵 데이터에는 높이 정보가 전혀 없다 — `maps/*.txt`는 타일별 통행 비트뿐이다.
 * 그래서 실제 지형의 높낮이를 재현할 수는 없고, **통행 불가 타일을 일정 높이의
 * 육면체로 세우는 것**으로 입체감을 만든다. 원본의 벽 모양과 같지는 않지만,
 * 바닥과 같은 평면 마름모로 칠하던 종전보다 벽이 벽으로 읽힌다.
 *
 * 한 칸을 정육면체로 보면 높이는 TILE_WIDTH/2 = 24px이다. 그보다 살짝 낮게 잡아
 * 벽 너머 바닥이 조금은 보이게 한다 — 꽉 채우면 좁은 통로가 벽으로 메워져 보인다.
 */
/** 벽 블록이 바닥에서 솟는 높이(px). */
const WALL_HEIGHT_PX = 20;
/** 측면 밝기 배율. 좌우를 다르게 줘야 광원이 한쪽에 있는 것처럼 읽힌다. */
const WALL_SIDE_RIGHT_SHADE = 0.72;
const WALL_SIDE_LEFT_SHADE = 0.48;
/**
 * 플레이어를 가리는 앞쪽 벽의 알파.
 *
 * 완전히 불투명하면 벽 뒤로 들어간 순간 자기 캐릭터를 잃어버린다. 비쳐 보이게 해
 * 위치는 알 수 있게 하되, 가려져 있다는 사실 자체는 남긴다.
 */
const WALL_OCCLUDING_ALPHA = 0.35;
/** 가림 판정에 쓰는 플레이어 몸 사각형의 반폭/높이(px). */
const PLAYER_BODY_HALF_WIDTH = 14;
const PLAYER_BODY_TOP = 44;

/*
 * 벽 레이어 분할.
 *
 * 벽을 "플레이어 앞/뒤" 두 장으로만 나누면 한 장이 통째로 같은 depth라, 벽 뒤에
 * 선 NPC가 전부 벽 위로 떠오른다(실측으로 확인). 그렇다고 벽마다 오브젝트를
 * 만들면 화면 안 수백 개가 되므로, **타일 깊이(tx+ty)를 일정 폭으로 묶은 밴드**
 * 단위로 나눠 그린다. 밴드가 좁을수록 정렬이 정확하고 오브젝트가 늘어난다.
 *
 * 화면(줌 1.8, 1024×768)에 걸리는 타일 깊이 범위가 대략 40이므로, 폭 2 × 32장이면
 * 범위 64를 덮어 화면 전체가 들어온다. 범위를 넘는 벽은 가장자리 레이어에 몰아
 * 넣는다 — 화면 끝이라 정렬 오차가 눈에 띄지 않는다.
 */
const WALL_LAYER_BAND = 2;
const WALL_LAYER_COUNT = 32;

/**
 * 발밑 그림자.
 *
 * 스프라이트가 타일 위에 떠 있는지 딛고 선 것인지를 가르는 것은 그림자다.
 * 광원 방향을 계산하지 않고 발밑에 눌린 타원 하나만 깐다 — 아이소메트릭에서는
 * 이것만으로 접지감이 생긴다.
 */
const SHADOW_RADIUS_X = 13;
const SHADOW_RADIUS_Y = 6;
const SHADOW_ALPHA = 0.32;
/** 그림자는 자기 본체보다 살짝 아래(작은 depth)에 그린다. */
const SHADOW_DEPTH_OFFSET = -DEPTH_SCALE / 4;

/** 0xRRGGBB의 각 성분에 배율을 곱한다 (1보다 작으면 어두워진다). */
function shadeColor(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

/**
 * 바닥 얼룩.
 *
 * 통행 가능한 바닥이 전부 똑같은 색이면 벽을 세워도 바닥만은 종이처럼 평평하다.
 * 타일 좌표를 해시해 명도를 아주 조금 흔들면 질감이 생긴다 — 텍스처 이미지가
 * 없어도 되고, 같은 타일은 언제나 같은 색이라 걸을 때 어른거리지 않는다.
 *
 * 폭이 좁아야 한다. 크게 흔들면 바닥이 체스판처럼 보여 벽과 구분이 흐려진다.
 */
const FLOOR_SHADE_RANGE = 0.14;
/** 바닥 얼룩 단계 수. 값이 적을수록 비슷한 색끼리 뭉쳐 배치 그리기가 덜 깨진다. */
const FLOOR_SHADE_STEPS = 5;

/**
 * 맵 테마별 바닥/벽 색.
 *
 * 종전에는 모든 맵이 같은 색(어두운 남색 바닥 + 테라코타 벽)이라 던전이든 마을이든
 * 숲이든 똑같아 보였다. 지형 종류는 맵 데이터에 없지만 DB `map_ids`의 지명으로
 * 유추할 수 있어(`data/mapThemes.ts`, 자동 생성) 7개 테마로 갈라 칠한다.
 *
 * **바닥과 벽의 대비비를 3:1 이상으로 유지할 것.** 종전 팔레트(0x3a1e1e)가
 * 1.12:1이라 벽이 바닥 무늬로 읽혔고, 실제로 벽에 갇히고도 원인을 못 찾은 사람이
 * 있었다. 현재 값은 town 4.42 / field 3.55 / forest 3.47 / cave 5.07 /
 * castle 5.77 / ship 3.71 / water 3.36이다 (직전 기본값이 3.43이었다).
 * 색을 고칠 때는 상대휘도로 다시 계산해 확인하라.
 */
interface TilePalette {
  floor: number;
  wall: number;
}
const THEME_PALETTES: Readonly<Record<MapTheme, TilePalette>> = {
  town:   { floor: 0x2b2419, wall: 0xb08050 },  // 흙바닥 / 나무·흙벽
  field:  { floor: 0x1c2a1e, wall: 0x8a7a4a },  // 풀빛 / 마른 흙벽
  forest: { floor: 0x16241a, wall: 0x86724a },  // 짙은 풀 / 나무
  cave:   { floor: 0x1a1a1c, wall: 0x8a8a92 },  // 어두운 암반 / 회색 돌
  castle: { floor: 0x232630, wall: 0x9aa0b0 },  // 석재 바닥 / 밝은 석재
  ship:   { floor: 0x2a2018, wall: 0xa07040 },  // 갑판 / 나무
  water:  { floor: 0x122430, wall: 0x3f7a96 },  // 물 / 산호빛 벽
};

/** 타일 좌표 → 0 이상 1 미만의 결정적 난수 (FNV-1a 변형). */
function tileHash01(tx: number, ty: number): number {
  let h = 2166136261;
  h = Math.imul(h ^ (tx & 0xffff), 16777619);
  h = Math.imul(h ^ (ty & 0xffff), 16777619);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/** 바닥 타일 한 칸의 명도 배율. 단계로 끊어 비슷한 색끼리 묶는다. */
function floorShadeFactor(tx: number, ty: number): number {
  const step = Math.floor(tileHash01(tx, ty) * FLOOR_SHADE_STEPS);
  const t = FLOOR_SHADE_STEPS > 1 ? step / (FLOOR_SHADE_STEPS - 1) : 0.5;
  return 1 - FLOOR_SHADE_RANGE / 2 + t * FLOOR_SHADE_RANGE;
}

/** 스프라이트 위에 이름표를 띄우는 픽셀 오프셋 (스프라이트 높이 근사치). */
const PLAYER_LABEL_OFFSET = 46;

/**
 * 공격 모션.
 *
 * 원본 리소스에는 공격 스프라이트가 없다 — 캐릭터 그림은 방향별 정지/보행과
 * 사망뿐이다. 그래서 모션은 있는 그림을 움직여서 만든다. 근접은 대상 쪽으로
 * 짧게 파고들었다 돌아오고(찌르기), 원거리는 반대로 반동으로 물러섰다 돌아오며
 * 화살이 날아간다. 몸짓만으로 근접/원거리가 구별되게 방향을 반대로 두었다.
 */
/** 근접 공격에서 대상 쪽으로 파고드는 거리(px). */
const MELEE_LUNGE_DISTANCE = 14;
/** 원거리 공격에서 반동으로 물러나는 거리(px). 파고드는 것보다 짧아야 자연스럽다. */
const RANGED_RECOIL_DISTANCE = 6;
/** 몸짓 한 번(전진 또는 후퇴)에 쓰는 시간(ms). yoyo로 복귀까지 두 배가 걸린다. */
const ATTACK_LUNGE_MS = 110;
/** 베기 궤적이 남아 있는 시간(ms). */
const SLASH_FADE_MS = 200;
/** 베기 궤적의 반지름(px). 캐릭터 몸통을 감싸는 정도. */
const SLASH_RADIUS = 30;
/** 베기 궤적이 벌어진 각도의 절반(rad). 전체 호는 이것의 두 배가 된다. */
const SLASH_SPREAD = 0.55;
/** 휘두르는 동안 궤적이 도는 각도(rad). */
const SLASH_SWING = 0.9;
/** 몸통 높이(px). 궤적과 화살이 발밑이 아니라 몸을 지나게 하는 기준. */
const PLAYER_BODY_HEIGHT = 20;
/** 화살이 1타일을 지나는 데 걸리는 시간(ms). 거리에 비례해 늘어난다. */
const ARROW_MS_PER_TILE = 45;
/** 화살 비행 시간의 하한/상한(ms). */
const ARROW_MIN_MS = 120;
const ARROW_MAX_MS = 500;
/** 화살 길이(px). */
const ARROW_LENGTH = 16;

/*
 * 타격 이펙트 — 명중 지점에서 섬광 링이 퍼지고 파편이 타격 방향으로 튄다.
 * 공격 스프라이트가 없는 한계를 몸짓(lunge)만으로 때우면 타격감이 없어서,
 * "맞는 순간"을 지점 이펙트로 따로 만든다.
 */
/** 타격 파편 개수. */
const HIT_SPARK_COUNT = 6;
/** 파편이 흩어져 사라지기까지의 시간(ms). */
const HIT_SPARK_MS = 260;
/** 파편이 뻗어 나가는 기준 거리(px). 개체마다 ±로 흔든다. */
const HIT_SPARK_DISTANCE = 34;
/** 타격 순간 퍼지는 섬광 링의 반지름(px)과 수명(ms). */
const HIT_FLASH_RADIUS = 13;
const HIT_FLASH_MS = 150;
/** 맞은 쪽이 타격 방향으로 밀려나는 거리(px). */
const HIT_KNOCKBACK_PX = 7;
/** 남(몹·타인) 공격자가 대상 쪽으로 몸을 내미는 거리(px)와 시간(ms). */
const ENEMY_LUNGE_PX = 10;
const ENEMY_LUNGE_MS = 90;
/**
 * 내가 맞았을 때의 카메라 흔들림. UI는 별도 카메라(uiCamera)가 그리므로
 * 세게 잡아도 채팅창·인벤토리는 흔들리지 않는다.
 */
const PLAYER_HIT_SHAKE_MS = 100;
const PLAYER_HIT_SHAKE_INTENSITY = 0.0045;
/** 무기별 베기 궤적 색. 활은 궤적 대신 화살을 그리므로 여기 없다. */
const SLASH_COLORS: Partial<Record<WeaponType, number>> = {
  none: 0xffffff,
  sword: 0xf2f7ff,
  staff: 0xc9a8ff,
};
/** 파편 기본색 — 무기 색을 모르는 공격(몹·타인·활)에 쓴다. */
const HIT_SPARK_DEFAULT_COLOR = 0xffd9a8;

/**
 * 무기가 닿는 거리(타일).
 *
 * 근접 무기는 붙어야 친다 — 떨어진 적을 클릭하면 그 자리에서 휘두르는 게 아니라
 * 다가가서 친다. 활은 제자리에서 쏜다. 맞았는지는 서버가 정하고, 이 값은
 * "다가갈 것인가 여기서 칠 것인가"만 가른다.
 *
 * 활의 상한은 서버가 대상을 무시하기 시작하는 20타일(C_Attack)보다 넉넉히
 * 안쪽에 둔다. 화면 밖 적까지 쏘려 들면 오히려 조작이 어긋난다.
 */
const MELEE_REACH_TILES = 1;
const RANGED_REACH_TILES = 12;

/**
 * NPC에게 말을 걸 수 있는 거리(타일).
 *
 * C_NpcTalk 자체는 거리를 보지 않지만, 대화창에서 이어지는 C_NpcAction이
 * `Math.abs(pc.getX() - npc.getX()) > 3`이면 통째로 무시한다(서버
 * C_NpcAction.java). 즉 멀리서 창을 열어봐야 상점·창고 버튼이 전부 먹통이 된다.
 * 그래서 몹을 쫓아가 치는 것과 같은 방식으로 다가간 뒤 말을 건다. 3이 아니라
 * 2로 두는 이유는 걷는 동안 NPC가 돌아다녀도 경계에 걸리지 않게 하려는 것.
 */
const NPC_TALK_REACH_TILES = 2;

/**
 * 말을 건 뒤 응답(show_html)을 기다리는 시간(ms).
 *
 * 대화 데이터가 없는 NPC는 서버가 **아무것도 보내지 않는다**(C_NpcTalk에서
 * html이 null이면 그대로 return). 화면에 아무 일도 일어나지 않으면 사용자는
 * 클릭이 씹힌 것으로 읽으므로, 이 시간이 지나면 반응이 없다고 알려 준다.
 */
const NPC_TALK_SILENCE_MS = 1500;

/**
 * 추격(updateAttackChase)에서 쓰는 경로 탐색 노드 예산.
 *
 * 클릭 이동은 MapCollision의 기본값(8000)을 그대로 쓰지만, 추격은 대상이 한 타일
 * 움직일 때마다 다시 탐색한다. 쫓아갈 만한 거리(가시 범위 = 맨해튼 19타일)는
 * 수백 노드면 덮이므로 예산을 낮춰 잡는다 — 대상이 벽 너머로 들어가 도달 불가가 되면
 * A*가 예산을 전부 소진하는데, 그 최악값이 곧 프레임 비용이 되기 때문이다.
 */
const CHASE_MAX_PATH_NODES = 1000;

/**
 * 목적지 하나당 경로를 다시 만들 수 있는 횟수.
 *
 * 경로는 만들어질 때의 엔티티 배치만 반영한다. 40타일을 걷는 동안 몹이 길을 가로막는
 * 일은 흔하므로 1회로는 부족하고(마을을 가로지르면 그때마다 걸음이 끊긴다), 무제한이면
 * 벽 앞에 붙박인 NPC 하나 때문에 매 스텝 A*를 돌게 된다. 그 사이를 잡은 값이다.
 */
const MOVE_PATH_MAX_RETRIES = 4;

/** 투명 상태(S_Invis)일 때의 스프라이트 알파. 0으로 지우지 않는 이유는 applyEntityInvis() 참고. */
const INVIS_ALPHA = 0.4;

/**
 * 외형 이상상태(S_Poison)의 색.
 *
 * 서버는 상태 이름이 아니라 **색만** 보낸다 (원본 클라이언트도 색으로만 구분한다).
 * 1=초록은 독, 2=회색은 마비 계열에 쓰이지만 서버가 보증하지는 않는다.
 */
const POISON_TINTS: Record<number, number> = {
  1: 0x66ff66,
  2: 0x999999,
};

/** S_Weather 코드별 안내 문구. 표에 없는 코드는 알리지 않는다. */
const WEATHER_LABELS: Record<number, string> = {
  0: '날씨가 맑아졌습니다.',
  1: '비가 내리기 시작합니다.',
  2: '눈이 내리기 시작합니다.',
};

/** S_Strup / S_Dexup의 능력치 이름. */
const STAT_UP_LABELS: Record<string, string> = {
  str: '힘',
  dex: '민첩',
};

/** 전투 결과(S_Attack*) 표시가 화면에 떠 있는 시간(ms). */
const COMBAT_FEEDBACK_MS = 700;

/**
 * 사망 후 메뉴가 저절로 열리기까지의 시간(ms).
 *
 * 사망 애니메이션은 5프레임 × 200ms = 1초다. 그보다 짧게 잡으면 쓰러지는 장면이
 * 화면 한가운데 메뉴 패널에 가려져 무엇 때문에 멈췄는지 알 수 없다.
 */
const DEATH_MENU_DELAY_MS = 1200;

/**
 * 전투 결과 문구의 깊이.
 *
 * 월드 오브젝트는 타일 깊이를 ±DEPTH_LIMIT로 clamp해 쓰므로, 그 위 한 칸에 두면
 * 어떤 스프라이트에도 가리지 않으면서 UI(별도 depth) 아래에 남는다.
 */
const DEPTH_COMBAT_FEEDBACK = DEPTH_LIMIT + 1;

/**
 * 월드 진입 직후 마우스 클릭을 무시하는 시간(ms).
 *
 * 두 가지를 함께 막는다:
 *  - 캐릭터 선택 화면에서 카드를 클릭해 진입하면 그 클릭의 뒷자락이 새로 시작된
 *    GameScene의 pointerdown으로 이어져, 의도하지 않은 지점으로 걸어간다.
 *  - 진입 직후에는 스폰 위치·맵 통행 데이터·주변 엔티티가 아직 들어오는 중이라,
 *    이때의 클릭은 화면에 보이는 것과 실제 상태가 다른 채로 이동을 시작시킨다.
 *
 * 그래서 한 프레임 수준이 아니라 로딩이 자리 잡을 만큼 넉넉히 잡는다.
 */
const WORLD_CLICK_GRACE_MS = 3000;
const ENTITY_LABEL_OFFSET = 40;

/**
 * 해석되지 않은 이름 ID(`$958`, `$1958 $1938`)를 가려내는 패턴.
 *
 * `resolveNpcName()`은 표에 없는 키를 원문 그대로 돌려주므로, 결과에 `$숫자`가
 * 남아 있으면 "이름을 못 찾았다"는 뜻이다. 그 경우 gfxId 폴백표로 넘긴다.
 * (한글/영문 이름에는 `$`가 쓰이지 않는다 — DB npcs.name 3480행 확인)
 */
const UNRESOLVED_NAME_ID = /\$\d/;

/**
 * GameState가 이름 없는 개체에 미리 끼워 넣는 자리표시자.
 *
 * 서버가 이름을 비워 보내면 GameState는 `resolveNpcName(data.name || 'NPC')`
 * 형태로 **문자열 'NPC'/'Monster'/'Player'를 대신 채운 뒤** GameScene에 넘긴다
 * (GameState.ts:241/251/262). 그대로 두면 "이름이 왔다"고 오인해 gfxId 폴백표까지
 * 가지 못하고 화면에 'NPC'가 그대로 남는다 — 실제로 맵 2005의 gfx 148/335 경비
 * NPC가 그렇게 보였다.
 *
 * DB 실측으로 이 세 단어를 이름으로 쓰는 NPC는 없다
 * (`npcs.name` / `npcs.name_id` 모두 0행). 따라서 자리표시자로만 취급해도 안전하다.
 */
const NAME_PLACEHOLDERS: ReadonlySet<string> = new Set(['NPC', 'Monster', 'Player']);

/**
 * 텍스처 로드 실패 시 보여줄 폴백 글자의 크기(px).
 *
 * 이름을 찾아냈으면 그 이름을 통째로 그리므로 22px는 너무 크다 — 한 글자짜리
 * 폴백('M'/'N'/'P')일 때만 크게 두고, 이름일 때는 이름표와 비슷한 크기로 줄인다.
 */
const ENTITY_FALLBACK_LETTER_FONT = '22px';
const ENTITY_FALLBACK_NAME_FONT = '12px';

/**
 * 캐릭터 스프라이트의 세로 기준점(originY).
 *
 * SVG는 48px 캔버스 중앙(24,24)을 원점으로 그려지고, 발바닥은 로컬 y=16 —
 * 즉 캔버스 절대 y=40에 있다 (scripts/gen_char_sprite.py의 leg_y=8, height=8).
 * 캔버스 아래 8px는 빈 여백이라, originY를 1(캔버스 바닥)로 두면 발이 타일
 * 중심보다 8px 위에 떠서 캐릭터가 타일에 서 있지 않은 것처럼 보인다.
 *
 * 발바닥을 타일 중심(tileToWorld가 돌려주는 점)에 맞춘다.
 */
const SPRITE_FOOT_ORIGIN_Y = 40 / 48;

/**
 * 실사 몹 그림을 그릴 때 기준으로 삼는 화면 높이(px) — **원본 캔버스 한 변** 기준이다.
 *
 * 원본은 몹마다 같은 캔버스(보통 90×90)에 그려져 있어서 그 안에서 차지하는 비율이
 * 곧 덩치다. 잘라낸 프레임 높이로 맞추면 개구리와 드래곤이 같은 크기가 되므로,
 * 캔버스를 이 크기로 축소한다고 보고 프레임을 그 비율로 줄인다.
 * 계열 SVG 캔버스(48px)보다 조금 크게 잡아, 여백이 잘린 만큼을 벌충한다.
 */
const MOB_IMAGE_CANVAS_PX = 80;

/**
 * 실사 몹 그림의 표시 높이 하한·상한(px).
 *
 * 캔버스 비율만으로 크기를 정하면 원본에서 작게 그려진 몹(개구리는 90px 캔버스에서
 * 세로 10px 남짓)이 화면에서 점이 되어 클릭조차 어렵다. 반대로 보스급은 화면을
 * 가릴 만큼 커진다. 비율은 이 범위 안에서만 반영한다 — 덩치 차이는 남기되
 * "보이지 않음"과 "화면을 덮음"은 막는다.
 */
const MOB_IMAGE_MIN_PX = 26;
const MOB_IMAGE_MAX_PX = 88;

/**
 * 다른 엔티티(몹/NPC/타 플레이어)의 타일 간 이동을 화면에서 보간하는 시간(ms).
 *
 * 서버는 이동을 타일 단위로만 알려주므로, 받은 좌표로 곧장 스냅하면 한 칸씩
 * 순간이동하는 것처럼 보인다. 자기 캐릭터가 updateStep()으로 보간하는 것과 같은
 * 이유로 남의 이동도 보간한다.
 *
 * 실제 보간 시간은 **직전 이동 패킷과의 간격**으로 정한다 — 몹마다 이동 속도가
 * 다르고(가속 버프, 몹 종류), 고정값을 쓰면 빠른 몹은 그림이 계속 뒤처진다.
 * 아래 두 값은 그 간격의 상·하한이다.
 */
const ENTITY_MOVE_DEFAULT_MS = MS_PER_TILE;
const ENTITY_MOVE_MIN_MS = 80;
/**
 * 상한. 이보다 길게 끌면 화면이 실제 위치보다 눈에 띄게 뒤처진다.
 *
 * 다른 **플레이어**의 이동 패킷은 그쪽 클라이언트도 같은 규칙으로 걷기 때문에
 * MS_PER_TILE 주기로 온다. 그래서 상한은 "MS_PER_TILE보다 약간 큰 값"이어야 한다 —
 * 그보다 짧으면 남들이 한 타일마다 도착 후 멈칫거리고, 훨씬 길면 몹이 뛸 때
 * 그림이 계속 뒤처진다.
 *
 * **MS_PER_TILE에서 파생시킨다.** 고정값(옛 700)으로 두면 MS_PER_TILE을 내릴 때마다
 * 상한이 상대적으로 커져 남들만 굼떠 보인다 — 실제로 680 → 340으로 내리면서
 * 700이 두 배 넘는 상한이 되어 타 플레이어가 계속 뒤처지는 문제가 생겼다.
 * 3%는 도착 지터를 삼키는 폭이고(MOVE_GATE_MARGIN과 같은 이유, 다만 별개의 값이다),
 * 옛 상수 그대로 계산하면 680*1.03 ≈ 700으로 예전 값이 그대로 재현된다.
 */
const ENTITY_MOVE_MAX_MS = Math.round(MS_PER_TILE * 1.03);

/** 클릭 지점에서 이 타일 거리 이내의 엔티티를 클릭 대상으로 간주한다. */
const CLICK_PICK_RANGE = 1;

/**
 * 바닥 아이템을 주울 수 있는 최대 거리(타일) — **발밑과 인접 한 칸까지**.
 *
 * 서버는 `getTileLineDistance() > 3`까지 허용하지만(`C_PickUpItem.java`, 초과분은
 * **아무 응답 없이** 무시된다) 클라이언트는 그보다 좁은 1칸만 인정한다. 손이 닿는
 * 자리에서만 줍는 편이 조작 의도와 맞고, 서버 기준보다 좁으므로 "보냈는데 조용히
 * 무시당하는" 경우도 생기지 않는다.
 *
 * 거리는 서버와 같은 직선 거리(max(|dx|,|dy|))로 잰다 — 대각선으로 붙은 칸도 1이다.
 */
const GROUND_PICKUP_RANGE = 1;

/**
 * 아이템을 내려놓을 수 있는 최대 거리(타일).
 *
 * **서버는 드롭 좌표를 전혀 검증하지 않는다** — `C_DropItem`은 받은 x/y의 지면
 * 인벤토리에 그대로 넣는다. 즉 사거리는 클라이언트가 지키는 규칙이고, 줍는 거리와
 * 같은 값으로 맞춘다 (버리자마자 못 줍는 자리에 놓이면 안 된다).
 */
const GROUND_DROP_RANGE = GROUND_PICKUP_RANGE;

/** 바닥에 그리는 아이템 아이콘의 표시 크기(px). 인벤토리 아이콘보다 작게 둔다. */
const GROUND_ITEM_ICON_SIZE = 22;

/** 바닥 아이템 이름표를 아이콘 위 몇 px에 띄울지. */
const GROUND_ITEM_LABEL_OFFSET = 14;

/**
 * 바닥 아이템 이름표 글자 크기.
 *
 * 엔티티 이름표(10px)보다 한 단계 작다 — 아이콘 자체가 22px로 작고, 이름표가 크면
 * 가리키려는 아이템보다 글자가 더 눈에 띄어 월드가 글자로 덮인 것처럼 보인다.
 */
const GROUND_ITEM_LABEL_FONT = '8px';

/** 바닥 아이템 클릭 판정 반경(타일). 아이콘을 빗맞혔을 때의 폴백. */
const GROUND_ITEM_PICK_RANGE = 1;

/**
 * 바닥 아이템의 마우스 판정 영역을 아이콘 크기의 몇 배로 넓힐지 (한 변 기준 여유 비율).
 *
 * 아이콘이 22px밖에 되지 않아 딱 그 크기로만 판정하면 이름표를 띄우려고 마우스를
 * 올리는 것 자체가 조준 과제가 된다. 이름표가 hover에서만 뜨므로 여유가 없으면
 * "이름이 아예 안 나온다"로 느껴진다.
 */
const GROUND_ITEM_HIT_MARGIN = 0.25;

/**
 * 멀리 있는 아이템을 주우러 걸어갈 때의 제한 시간(ms).
 *
 * 길이 막혀 끝내 닿지 못하는 경우가 있어(경로 탐색 실패, 문이 닫힘) 예약을 언제까지
 * 들고 있을지 정해 둔다. 없으면 한참 뒤 엉뚱한 순간에 줍기가 나간다.
 */
const PICKUP_WALK_TIMEOUT_MS = 10000;

/**
 * 걷기 애니메이션의 프레임 전환 주기(ms).
 * 한 타일 보간(MS_PER_TILE) 동안 5프레임을 모두 보여주도록 나눈 값이다.
 * frame 0(idle)이 포함되어 정지 상태에서는 0만 유지한다.
 *
 * 이동 주기와 따로 놀면 한 타일을 가는 동안 다리가 여러 번 헛돌거나(너무 빠름)
 * 한 프레임에 멈춰 미끄러지듯 보인다(너무 느림).
 *
 * **파생식이므로 MS_PER_TILE을 바꾸면 자동으로 따라온다** — 680일 때 136ms,
 * 340인 지금은 68ms다. 68ms/프레임 ≈ 14.7fps로 예전보다 두 배 빠르지만
 * 그것이 맞다: 발이 타일 진행에 물려 있어야 미끄러져 보이지 않으므로,
 * "한 타일 = 5프레임"이라는 관계를 깨고 136ms를 유지하면 한 타일을 가는 동안
 * 2.5프레임만 재생돼 다리가 중간에 굳는다. 60fps 렌더에서 68ms는 애니 한 장당
 * 약 4프레임이라 여전히 부드럽고, 5프레임짜리 짧은 사이클이 깜빡여 보이기
 * 시작하는 50ms 선보다는 위다 (= MS_PER_TILE 250 아래로 내리면 재검토할 것).
 */
const WALK_FRAME_INTERVAL_MS = Math.round(MS_PER_TILE / 5);

/**
 * 텔레포트 연출 시간(ms).
 *
 * 서버는 S_Teleport를 보낸 뒤 클라이언트의 C_Teleport 응답을 기다렸다가 좌표를
 * 옮긴다(`Teleportation.Teleportation(pc)`). 응답을 보내지 않으면 주문서만
 * 소모되고 이동이 완결되지 않으므로, 화면을 어둡게 덮은 직후 곧바로 ack를 보낸다.
 */
const TELEPORT_FADE_MS = 220;

/**
 * ack를 보낸 뒤 새 위치(map_id / own_char_status / spawn)를 기다리는 상한(ms).
 * 이 시간이 지나도 아무것도 오지 않으면 화면과 입력 잠금을 스스로 되돌린다 —
 * 검은 화면에 갇힌 채 조작이 막히는 것이 최악이다.
 */
const TELEPORT_TIMEOUT_MS = 5000;

/** 스킬 이펙트(S_SkillSound) 원의 최대 반지름(px)과 지속 시간(ms). */
const SKILL_EFFECT_RADIUS = 34;
const SKILL_EFFECT_MS = 420;

/**
 * 서버가 알려주는 장착 무기 코드(S_CharVisualUpdate) → 스프라이트 무기 종류.
 *
 * 값은 서버 `L1PcInstance.getCurrentWeapon()` = `L1Item.getType1()`이고,
 * 그 실체는 `ItemTable._weaponId` 표다(sword=4, twohandsword=50, dagger=46,
 * bow=20, arrow=66, spear=24, blunt=11, staff=40, claw=58, dualsword=54,
 * gauntlet=62, sting=2922, chainsword=24, kiringku=58). 0은 맨손.
 *
 * 캐릭터 그림은 none/sword/bow/staff 네 종류뿐이므로, 활 계열과 지팡이만
 * 따로 잡고 나머지 무기는 전부 근접(sword)으로 접는다.
 */
const WEAPON_CODE_TO_TYPE: Record<number, WeaponType> = {
  0: 'none',
  20: 'bow',   // bow
  66: 'bow',   // arrow (활을 든 상태로 브로드캐스트되는 경우가 있다)
  40: 'staff', // staff
};

/**
 * 바닥에 놓인 아이템 하나 (S_DropItem → `drop_item`).
 *
 * 엔티티(EntitySprite)와 분리해서 관리한다 — 바닥 아이템은 걷지도 죽지도 않고,
 * 클릭했을 때 하는 일(줍기)도 다르다. 다만 objId 공간은 엔티티와 공유하므로
 * `object_remove`는 양쪽 모두에서 지워야 한다.
 */
interface GroundItemSprite {
  objId: number;
  /** 아이템 카테고리 아이콘(SVG). 인벤토리와 같은 23종 표를 쓴다. */
  sprite: Phaser.GameObjects.Image;
  /** 이름표. 겹쳐 쌓인 바닥을 읽을 수 있게 상시 표시한다. */
  label: Phaser.GameObjects.Text;
  /** 서버 타일 좌표. 줍기 패킷에 그대로 실어야 한다 (플레이어 좌표가 아니다). */
  tileX: number;
  tileY: number;
  /** 표시명. 서버가 겹친 개수를 이름 꼬리에 붙여 보낼 수 있다. */
  name: string;
  /** 겹친 개수. 줍기 패킷의 count로 쓴다. */
  count: number;
  gfxId: number;
}

interface EntitySprite {
  objId: number;
  /** 본체. 텍스트 더미 대신 캐릭터 SVG 텍스처가 올라가는 Phaser Image. */
  sprite: Phaser.GameObjects.Image;
  /** 발밑 그림자. 접지감을 만드는 눌린 타원 (본체보다 살짝 아래 depth). */
  shadow: Phaser.GameObjects.Ellipse;
  nameText: Phaser.GameObjects.Text;
  healthBar?: Phaser.GameObjects.Graphics;
  type: 'player' | 'npc' | 'monster';
  /** 서버 타일 좌표 (렌더 위치는 여기서 파생된다). */
  tileX: number;
  tileY: number;
  /**
   * 어떤 그림 체계를 쓰는가.
   *  - 'char': 캐릭터 아틀라스 (플레이어 및 플레이어 외형을 쓰는 NPC)
   *  - 'mob' : 몬스터 계열 SVG (mobSprites.ts) + 이름 해시 tint
   * 몹 SVG 로드가 끝내 실패하면 'char'로 강등해 기존 동작으로 안전하게 떨어진다.
   */
  kind: 'char' | 'mob';
  /** kind === 'mob'일 때의 계열. inferMobFamily(gfxId, name)의 결과. */
  mobFamily?: MobFamily;
  /**
   * 실제 몹 그림이 있으면 그 규격 (`data/mobImages.ts`).
   *
   * 있으면 계열 SVG 대신 이 그림을 쓴다. 로드에 실패하면 undefined로 되돌려
   * 계열 SVG 경로로 떨어진다 — 그림이 없는 몹과 같은 취급이 된다.
   */
  mobImage?: MobImageMeta;
  /** kind === 'mob'일 때 setTint로 입힐 개체 색. 같은 계열을 이름으로 구분한다. */
  mobTintColor?: number;
  /**
   * 변신(S_ChangeShape) 전의 원래 그림 정보. 변신 중일 때만 존재한다.
   * 해제(polyId 0)되면 이 값을 그대로 되돌려 놓는다 — 스폰 시점의 gfxId를
   * 따로 들고 있지 않아 다시 추론할 수단이 없기 때문이다.
   */
  basePoly?: {
    kind: 'char' | 'mob';
    mobFamily?: MobFamily;
    mobTintColor?: number;
    mobImage?: MobImageMeta;
  };
  /** SVG 식별자. 텍스처 키 생성에 사용. */
  characterId: CharacterId;
  weapon: WeaponType;
  /** 현재 바라보는 방향. */
  direction: Direction;
  /** 현재 표시 중인 프레임 (0=idle, 1~4=걷기). */
  frame: number;
  /** 이동 중 여부. true일 때만 frame을 advance한다. */
  isWalking: boolean;
  /** frame 전환용 누적 타이머 (ms). */
  walkFrameTimer: number;
  /** 마지막으로 텍스처를 등록한 키 (중복 등록 방지). */
  currentTextureKey: string | null;
  /** 로드 실패 시 폴백으로 보여줄 텍스트(첫 글자). 없으면 SVG만 사용. */
  fallbackLabel: string;
  /** 위 폴백 텍스트의 실체. 텍스처가 붙으면 숨기고, 제거 시 함께 파괴한다. */
  fallbackText: Phaser.GameObjects.Text;
  /**
   * 화면 표시용 이동 보간. 논리 좌표(tileX/tileY)는 패킷을 받는 즉시 갱신하고
   * 그림만 이 상태를 따라간다 — 거리·공격 판정이 그림보다 늦어지면 안 된다.
   */
  move?: {
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
    elapsed: number;
    duration: number;
  };
  /** 직전 이동 패킷을 받은 시각(scene time). 다음 보간 시간을 정하는 기준. */
  lastMoveAt: number;
  /**
   * 사망 애니메이션 진행 상태. 존재하는 동안에는 walking animation 대신
   * death 프레임을 표시하고, 최종 frame(묘비)에 도달하면 그대로 고정된다.
   */
  deathAnim?: {
    /** ms 단위 시작 시각. this.time.now를 그대로 쓴다. */
    startedAt: number;
    /** 사망한 순간의 캐릭터 ID (스프라이트 외형이 달라져도 안전하게). */
    charId: CharacterId;
    /** 사망한 순간의 방향 (마지막으로 보던 방향으로 누움). */
    direction: Direction;
    /** 마지막으로 텍스처를 등록한 키. currentTextureKey와는 별도 추적. */
    lastTextureKey: string | null;
    /**
     * 몹 방식으로 쓰러지는가.
     *
     * 사망 아틀라스는 캐릭터용 14종뿐이라 몹에는 쓸 그림이 없다. 억지로 쓰면
     * 몬스터가 죽는 순간 왕자 시체로 바뀐다. 그래서 몹은 프레임 교체 대신
     * 기울이고 흐리게 하는 tween 하나로 끝낸다.
     */
    mob: boolean;
  };
}

/**
 * 아이템을 쓰려면 무엇을 골라야 하는가.
 *
 * 서버 `C_UseItem`(opcode 44) 생성자는 itemObjId(4바이트)를 읽은 뒤
 * **아이템 자신의 use_type**으로 이어 읽을 인자를 정한다. 그 인자를 무엇으로
 * 채워야 하는지가 곧 "무엇을 골라야 하는가"이므로, 분기를 서버 생성자와
 * 1:1로 맞춰 한곳(targetKindFor)에만 적는다.
 *
 *  - inventory   : 인벤토리의 다른 아이템 objId — readD()
 *                  (7 identify / 14 choice / 26 dai / 27 zel)
 *  - entity      : 월드 엔티티 objId — readD()
 *                  (8 res / 30 spell_buff)
 *  - ground      : 타일 좌표 (+ 5/17/39는 objId도) — readD()+readH()*2 / readH()*2
 *                  (5 spell_long / 17 spell_short / 39 spell_point / 42 fishing_rod)
 *  - text        : 문자열 — readS()
 *                  (16 sosc 변신 스크롤)
 *  - bookmark    : 북마크 목록에서 목적지 — readH()+readD()
 *                  (6 ntele / 29 btele 순간이동 스크롤)
 *  - unsupported : 서버는 인자를 기다리지만 고를 UI가 아직 없다
 *                  (28 blank = 스킬 목록)
 *  - none        : 대상이 필요 없다 — 즉시 전송 (그 외 전부. 포션·음식·장비
 *                  장착·귀환 주문서(73)가 여기 해당하며 기존 동작 그대로다)
 */
type UseTargetKind =
  | 'inventory'
  | 'entity'
  | 'ground'
  | 'text'
  | 'bookmark'
  | 'unsupported'
  | 'none';

/** 대상 선택을 기다리는 아이템 사용 한 건. */
interface PendingItemUse {
  /** 무엇을 고르는 중인지. 취소/완료 처리는 종류와 무관하게 한 경로로 흐른다. */
  kind: Exclude<UseTargetKind, 'none' | 'unsupported'>;
  /** 사용하려는 아이템(스크롤 자신)의 objId. 대상의 objId가 아니다. */
  itemId: number;
  /** 그 아이템의 use_type. 게이트웨이가 패킷 모양을 정하는 데 그대로 쓴다. */
  useType: number;
}

/** use_item 메시지에 use_type별로 덧붙는 인자. */
interface UseItemExtra {
  targetId?: number;
  /** use_type 6/29 — 서버가 readH()로 읽는 자리. 아래 handleBookmarkPick 주석 참고. */
  mapId?: number;
  x?: number;
  y?: number;
  text?: string;
}

/**
 * 확인(감정) 주문서를 쓴 뒤 서버 응답을 기다리는 유효 시간(ms).
 *
 * 서버 `S_IdentifyDesc`에는 **어느 아이템을 감정했는지가 들어 있지 않다**
 * (descId는 아이템 종류의 설명 리소스 번호이지 objId가 아니다). 그래서 대상
 * objId는 클라이언트가 보낸 쪽에서 기억해 둘 수밖에 없는데, 그 기억이 무한정
 * 남아 있으면 한참 뒤 다른 경로로 온 감정 결과에 엉뚱한 아이템 이름이 붙는다.
 */
const IDENTIFY_TARGET_TTL_MS = 10000;

/**
 * S_IdentifyDesc의 messageId → 설명 줄 만들기.
 *
 * 서버는 문장을 보내지 않는다 (원본 클라이언트 리소스에 있다). 번호별 문장은
 * `src/jp/l1j/server/packets/server/S_IdentifyDesc.java`의 주석이 근거다.
 * `args[0]`은 언제나 아이템 이름이라 제목으로 따로 쓰고, 여기서는 **속성 줄만**
 * 만든다 (제목과 본문에 같은 이름이 두 번 나오지 않게).
 *
 * 인자가 모자랄 수 있다 — 기마용 헬름(itemId 20383)은 서버가 argCount를 3으로
 * 쓰면서 문자열을 2개만 보내는 버그가 있다. 그래서 값이 있는 줄만 만든다.
 */
const IDENTIFY_DESC_LINES: Record<number, (args: string[]) => Array<string | null>> = {
  // \f1%0：小さなモンスター打撃%1 大きなモンスター打撃%2
  134: (a) => [
    a[1] ? `소형 몬스터 타격: ${a[1]}` : null,
    a[2] ? `대형 몬스터 타격: ${a[2]}` : null,
  ],
  // \f1%0：防御力%1 防御具
  135: (a) => [a[1] ? `방어력: ${a[1]}` : null],
  // \f1%0：満腹度%1［重さ%2］
  136: (a) => [a[1] ? `포만도: ${a[1]}` : null, a[2] ? `무게: ${a[2]}` : null],
  // \f1%0：使用可能回数%1［重さ%2］
  137: (a) => [a[1] ? `사용 가능 횟수: ${a[1]}` : null, a[2] ? `무게: ${a[2]}` : null],
  // \f1%0：［重さ%1］
  138: (a) => [a[1] ? `무게: ${a[1]}` : null],
};

export class GameScene extends Phaser.Scene {
  // Connection
  private connectionManager: ConnectionManager | null = null;

  // UI Components
  private statusBar!: StatusBar;
  private miniMap!: MiniMap;
  private chatBox!: ChatBox;
  private inventory!: Inventory;
  /** 캐릭터 상세 정보 창 (좌측 상단, 기본 숨김). */
  private characterDetailPanel!: CharacterDetailPanel;
  /** 장착 아이템 확인 창 (좌측 중단, 기본 숨김). */
  private equipmentPanel!: EquipmentPanel;
  private characterStatsPanel!: CharacterStatsPanel;
  /** NPC 대화/퀘스트 창 (화면 중앙, 기본 숨김). show_html 수신 시 열린다. */
  private htmlDialog!: HtmlDialog;
  /** 순간이동 북마크 목록 창 (화면 중앙, 기본 숨김). use_type 6/29에서 열린다. */
  private bookmarkDialog!: BookmarkDialog;
  /** 감정 결과 창 (화면 중앙, 기본 숨김). identify_desc 수신 시 열린다. */
  private identifyDialog!: IdentifyDialog;
  /** 개수 입력 창 (화면 중앙, 기본 숨김). 겹친 아이템을 나눠 버리거나 주울 때 열린다. */
  private quantityDialog!: QuantityDialog;
  private quickSlotBar!: QuickSlotBar;
  private menuButton!: MenuButton;
  private menuPanel!: Phaser.GameObjects.Container;
  private controlHints!: Phaser.GameObjects.Text;

  // Game Entities
  /** 플레이어 본체 SVG. 텍스트 더미 대신 Phaser Image로 표시한다. */
  private playerSprite!: Phaser.GameObjects.Image;
  private playerNameText!: Phaser.GameObjects.Text;

  /**
   * 칭호를 뺀 순수 캐릭터 이름.
   *
   * 이름표 텍스트에는 칭호(S_CharTitle)가 붙을 수 있어서, 칭호가 바뀔 때마다
   * 표시 문자열을 파싱해 되돌리는 대신 원래 이름을 여기 따로 들고 있는다.
   */
  private playerBaseName: string = '';
  private entitySprites: Map<number, EntitySprite> = new Map();

  /**
   * 바닥에 놓인 아이템들 (objId → 스프라이트).
   *
   * 서버는 시야에 들어온 바닥 아이템을 S_DropItem(opcode 3)으로 알리고, 없어지면
   * S_RemoveObject를 보낸다 — 엔티티와 같은 objId 공간을 쓰므로 제거 경로도 공유한다.
   */
  private groundItems: Map<number, GroundItemSprite> = new Map();

  /**
   * 걸어가서 주우려고 예약해 둔 바닥 아이템.
   *
   * 몹 추격(attackTargetId + chaseIntent)과 달리 대상이 움직이지 않으므로 경로를
   * 다시 짜지 않고, 사거리에 들어왔는지만 매 프레임 확인한다(updatePendingPickup).
   */
  private pendingPickup: { objId: number; expiresAt: number } | null = null;

  // ----- 플레이어 스프라이트 상태 (SVG 텍스처 관리) -----
  /** 플레이어 캐릭터 ID (gfxId에서 추론). */
  private playerCharacterId: CharacterId = 'prince_m';
  /** 플레이어 무기. 인벤토리의 장착 표시에서 역산해 갱신한다. */
  private playerWeapon: WeaponType = 'none';
  /**
   * 공격 모션이 몸통에 주는 화면 오프셋(px).
   *
   * 스프라이트 좌표를 직접 tween할 수 없다 — renderPlayerAt()이 매 프레임
   * 타일 좌표로 위치를 덮어쓰기 때문이다. 그래서 오프셋만 tween하고
   * 그리는 쪽에서 더한다.
   */
  private readonly attackLunge = { x: 0, y: 0 };
  /** 진행 중인 공격 몸짓. 연타로 오프셋이 어긋나지 않게 새 공격 전에 정리한다. */
  private attackLungeTween: Phaser.Tweens.Tween | null = null;
  /** 현재 바라보는 방향. */
  private playerDirection: Direction = 'S';
  /** 현재 표시 중인 프레임. */
  private playerFrame: number = 0;
  /** 이동 중이면 frame을 advance한다. */
  private playerIsWalking: boolean = false;
  /** frame 전환용 누적 타이머. */
  private playerWalkFrameTimer: number = 0;
  /** 마지막으로 등록한 텍스처 키 (중복 등록 방지). */
  private playerCurrentTextureKey: string | null = null;
  /**
   * 변신(S_ChangeShape) 중일 때 자기 캐릭터가 쓸 몹 계열. null이면 원래 캐릭터 그림.
   * 그림을 고르는 곳은 applyPlayerTexture() 한 곳이므로 여기만 바꾸면 전부 따라온다.
   */
  private playerPolyFamily: MobFamily | null = null;
  /** 변신 그림에 입힐 개체 색. 몹 SVG는 tint를 곱하는 전제로 밝게 그려져 있다. */
  private playerPolyTint: number = 0xffffff;
  /**
   * 사망 애니메이션 진행 상태. null이면 살아있는 상태다.
   * updatePlayerWalkAnimations()에서 walking보다 death를 우선 처리한다.
   */
  private playerDeathAnim: {
    startedAt: number;
    charId: CharacterId;
    direction: Direction;
    lastTextureKey: string | null;
  } | null = null;

  // Input state
  private keys!: {
    up: Phaser.Input.Keyboard.Key;
    down: Phaser.Input.Keyboard.Key;
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
  };

  // ==================== 위치 / 이동 (전부 타일 단위) ====================
  /** 플레이어의 논리 타일 좌표 = 서버가 알고 있는 위치. */
  private playerTileX: number = 0;
  private playerTileY: number = 0;
  private currentHeading: number = 4;

  /** 진행 중인 "한 타일 스텝". 시작하면 반드시 끝까지 완주한다 (서버가 이미 도착 타일을 기록했으므로). */
  private isStepping: boolean = false;
  private stepFromX: number = 0;
  private stepFromY: number = 0;
  private stepToX: number = 0;
  private stepToY: number = 0;
  private stepToTileX: number = 0;
  private stepToTileY: number = 0;
  private stepElapsed: number = 0;
  /**
   * 스텝을 시작한(= C_MoveChar를 보낸) 벽시계 시각.
   *
   * 스텝 완료 판정에 Phaser의 delta 누적(`stepElapsed`)을 쓰면 안 된다. Phaser의
   * TimeStep은 delta smoothing(`deltaHistory` 평활)을 걸어서 보고값이 실제 경과
   * 시간보다 커질 수 있고, 그러면 누적값이 MS_PER_TILE에 **실제 시간보다 먼저**
   * 닿아 다음 C_MoveChar가 규정보다 빨리 나간다.
   *
   * 서버 AcceleratorChecker의 통과 하한은 328.2ms인데 MS_PER_TILE이 340ms라
   * 여유가 11.8ms(3.6%)뿐이라, 평활 오차가 그 폭을 넘기면 그대로 위반이 된다.
   * 실제로 26fps 환경에서 키보드로 22초 연속 이동하니
   * "Overspeed detected. Disconnect the connection after 10 seconds."가 떴다.
   * (헤드리스가 2~3fps일 때는 프레임 시간이 훨씬 길어 드러나지 않았다.)
   *
   * 서버 `CheckStrictness`를 102 → 200으로 완화하면서 하한이 660 → 328.2ms로
   * 내려갔지만, **절대 여유는 20ms → 11.8ms로 오히려 좁아졌다.** 평활 오차는
   * 비율이 아니라 밀리초로 발생하므로 벽시계 기준이 예전보다 더 중요해졌다.
   *
   * 그래서 **송신 간격만은 벽시계로 잰다.** 화면 보간도 같은 기준을 쓰면
   * 두 값이 어긋날 일이 없다.
   */
  private stepStartedAt: number = 0;

  /** 클릭 이동 목적지 (타일 좌표). 키보드 입력이 들어오면 취소된다. */
  private moveTargetTile: { tx: number; ty: number } | null = null;
  /**
   * 목적지까지의 경로 (mapCollision.findPath 결과 = 출발 타일을 제외한 경유 타일).
   * 앞에서부터 movePathIndex로 소비한다 — 매 프레임 shift()로 배열을 재할당하지 않는다.
   */
  private movePath: PathNode[] = [];
  private movePathIndex: number = 0;
  /**
   * 지금 목적지가 경로 추종 중인지. false면 맵 통행 데이터가 없어 경로를 만들지
   * 못한 상태이므로, 예전처럼 목적지 방향으로 한 타일씩 밀고 가는 그리디로 폴백한다.
   * (경로 추종 중일 때 경로가 바닥나면 = 다 걸었으므로 목적지를 비운다. 부분 경로라
   *  목표에 못 닿았어도 마찬가지다 — 그리디로 이어 붙이면 벽에 들이받는다.)
   */
  private movePathActive: boolean = false;
  /**
   * 이 목적지에 대해 경로를 다시 만든 횟수.
   * 다음 스텝이 막혔을 때(문이 닫혔거나 몹이 길을 가로막았다) 재계산하되
   * MOVE_PATH_MAX_RETRIES회로 제한한다. 조건 없이 다시 계산하면 막힌 상태가
   * 이어지는 동안 매 프레임 A*를 돈다.
   */
  private movePathRetries: number = 0;
  /**
   * 이 목적지의 탐색 노드 예산. 재계산할 때도 처음과 같은 예산을 써야 한다
   * (추격은 CHASE_MAX_PATH_NODES로 낮춰 잡는다).
   */
  private movePathMaxNodes: number | undefined = undefined;
  /**
   * 쫓아가서 칠 대상의 objId. 근접 무기로 떨어진 적을 클릭했을 때 설정된다.
   *
   * 사거리에 들어가면 update 루프가 걸음을 멈추고 공격한 뒤 비운다. 원작에서
   * 근접 무기는 클릭 한 번으로 다가가 때리지, 제자리에서 허공을 베지 않는다.
   */
  private attackTargetId: number | null = null;
  /**
   * 쫓아간 뒤에 할 일. 몹이면 치고(attack), NPC면 말을 건다(talk).
   * 추격 자체(목적지 갱신·도착 판정)는 attackTargetId 하나로 공용이다.
   */
  private chaseIntent: 'attack' | 'talk' = 'attack';

  // ==================== 공격 송신 게이트 (서버 가속기 검사 대응) ====================
  /**
   * 마지막으로 `attack`을 실제로 보낸 벽시계 시각(performance.now()).
   *
   * 이동의 `stepStartedAt`과 같은 이유로 Phaser의 delta 누적이 아니라 벽시계를 쓴다 —
   * delta smoothing 오차가 섞이면 규정보다 빨리 보내게 된다.
   * 0으로 시작하므로 **첫 공격은 언제나 즉시** 나간다.
   */
  private lastAttackSentAt: number = 0;
  /**
   * 게이트에 막혀 미뤄둔 공격의 대상 objId. 없으면 null.
   *
   * 큐가 아니라 **한 칸짜리 자리**다 — 연타로 쌓아두면 게이트가 열리는 순간
   * 밀린 것들이 한꺼번에 터져 나가 오히려 위반이 된다. 늦게 누른 쪽이 이긴다
   * (대상을 바꾸면 예약도 갈아 끼워진다).
   */
  private pendingStrikeTargetId: number | null = null;
  /** 게이트에 막혀 미뤄진 공격 횟수 (진단용). `window.__l1jGateStats()`로 본다. */
  private attackGateDeferrals: number = 0;
  /**
   * 서버 하한에 막혀 시작을 미룬 걸음 수 (진단용). `window.__l1jGateStats()`로 본다.
   * 확인된 gfx(보행 640ms)에서는 MS_PER_TILE이 하한 338.1ms보다 크므로 0으로 남는다 —
   * 0이 아니면 보행이 640보다 느린 gfx가 나타났다는 뜻이다 (moveGateMs 주석 참조).
   */
  private moveGateHolds: number = 0;

  /**
   * 추격 중 마지막으로 경로를 만든 대상 타일.
   *
   * 대상이 **이 타일을 벗어났을 때만** 다시 탐색한다 — 매 프레임(60fps) A*를 돌면
   * 메인 스레드가 죽는다. 경로 탐색이 실패해서 목적지가 비워진 경우에도 이 값은
   * 남으므로, 실패가 매 프레임 되풀이되지 않는다 (대상이 실제로 움직여야 재시도).
   */
  private chasePathGoal: { tx: number; ty: number } | null = null;
  /** 말을 걸고 응답(show_html)을 기다리는 중인 대상. 침묵 안내에만 쓴다. */
  private npcTalkPending: { objId: number; name: string; timer: Phaser.Time.TimerEvent } | null = null;
  /** 벽 충돌 판정에 쓰는 현재 맵 ID (map_id 메시지로 갱신). */
  private currentMapId: number | null = null;

  /** depth 계산 기준점 = 플레이어 타일의 tileDepth. 플레이어가 움직일 때마다 갱신된다. */
  private depthOrigin: number = 0;

  // 타일 배경 렌더링
  private tileGridGraphics!: Phaser.GameObjects.Graphics;
  /**
   * 벽 블록 레이어. 타일 깊이 밴드마다 한 장씩(WALL_LAYER_BAND / _COUNT 참고).
   * 씬이 사는 동안 재사용하며, 다시 그릴 때 clear()만 한다.
   */
  private wallLayers: Phaser.GameObjects.Graphics[] = [];
  private lastTileGridCenterX: number | null = null;
  private lastTileGridCenterY: number | null = null;
  // 서버 가시 범위(19타일) 링은 플레이어 타일 기준이므로, 카메라 중심 타일이
  // 그대로여도 플레이어가 움직였으면 다시 그려야 한다.
  private lastTileGridPlayerX: number | null = null;
  private lastTileGridPlayerY: number | null = null;
  /**
   * 자기 캐릭터의 광원 반경(타일, S_Light). 0이면 아직 통지받지 못한 상태로
   * 보고 바닥 밝기를 건드리지 않는다 — 램프도 없이 화면이 통째로 어두워지면
   * 통행 데이터를 읽을 수 없게 된다.
   */
  private playerLightTiles: number = 0;

  // Camera
  private cameraTargetX: number = 0;
  private cameraTargetY: number = 0;
  /**
   * UI 전용 카메라 (줌 1 고정, 스크롤 없음).
   * 메인 카메라는 월드만, 이 카메라는 UI만 렌더링한다 — setupCameras() 참고.
   */
  private uiCamera: Phaser.Cameras.Scene2D.Camera | null = null;
  /** UI 카메라에만 보여야 하는 오브젝트 (= 메인 카메라가 무시할 대상). */
  private uiObjects: Phaser.GameObjects.GameObject[] = [];

  // Menu state
  private isMenuVisible: boolean = false;
  /**
   * 사망 때문에 자동으로 열린 메뉴인가.
   *
   * 부활할 때 자동으로 닫을 대상을 가리기 위한 표시다. 사용자가 자기 손으로 연
   * 메뉴까지 부활 순간에 닫아버리면 조작을 빼앗는 것이 된다.
   */
  private menuOpenedByDeath: boolean = false;
  /** 사망 연출이 끝나기를 기다리는 메뉴 열기 타이머 (부활하면 취소한다). */
  private deathMenuTimer: Phaser.Time.TimerEvent | null = null;

  // ==================== 아이템 대상 선택 ====================
  /** 대상 선택 안내 배너 (+ use_type 16의 문자열 입력). */
  private targetPrompt!: TargetPrompt;
  /**
   * 대상 선택 대기 상태. null이면 대상 선택 모드가 아니다.
   * 한 번에 하나만 존재한다 — 새 아이템 사용을 시작하면 이전 것은 접힌다.
   */
  private pendingUse: PendingItemUse | null = null;

  /**
   * 방금 감정(use_type 7)을 요청한 대상 아이템의 objId.
   *
   * S_IdentifyDesc에는 대상 objId가 없어(위 IDENTIFY_TARGET_TTL_MS 주석 참고)
   * "무엇을 감정했는가"는 보낸 쪽만 안다. 응답이 오면 소비하고 비운다.
   */
  private pendingIdentifyTargetId: number | null = null;
  /** 위 값을 기억한 시각(ms). IDENTIFY_TARGET_TTL_MS가 지나면 만료로 본다. */
  private pendingIdentifyAt: number = 0;

  /**
   * 이번 클릭은 창의 닫기 버튼이 이미 소비했다는 표시.
   *
   * Phaser는 한 번의 pointerdown에서 **오브젝트 핸들러를 먼저 돌리고 그 다음에
   * 씬의 `pointerdown`을 쏜다**. 그래서 인벤토리의 X 버튼이 창을 먼저 숨겨 버리면,
   * 이어서 도는 씬 핸들러의 `isPointerOverOpenPanels()`는 "창이 닫혀 있다"고 보고
   * 같은 클릭을 월드 클릭으로 처리한다 — X를 누르면 창이 닫히면서 캐릭터가 그
   * 자리로 걸어가 버렸다. 닫기 버튼이 이 플래그를 세우고 씬 핸들러가 곧바로
   * 읽고 지운다.
   */
  private worldClickConsumed: boolean = false;

  // ==================== 텔레포트 ====================
  /**
   * S_Teleport를 받고 ack를 보낸 뒤, 새 위치가 도착하기를 기다리는 중인지.
   * 이동 잠금 자체는 GameState(isTeleporting)가 들고 있고, 여기서는 화면
   * 페이드와 안전장치 타이머만 관리한다.
   */
  private teleportPending: boolean = false;
  /** 위치가 끝내 오지 않을 때 잠금을 스스로 푸는 타이머. */
  private teleportTimeout: Phaser.Time.TimerEvent | null = null;

  /**
   * 스프라이트를 이미 미리 받아둔 몹 계열.
   *
   * 한 계열이 화면에 처음 나타날 때 8방향 × 5프레임을 통째로 받아두면 방향을
   * 틀거나 걷기 시작할 때 그림이 한 박자 늦게 바뀌는 것을 없앨 수 있다.
   * 계열당 한 번만 하므로 같은 종류가 떼로 나와도 요청이 늘지 않는다.
   */
  private preloadedMobFamilies: Set<MobFamily> = new Set();

  /**
   * 월드 클릭을 받기 시작했는지. 진입 유예가 끝나면 true가 된다.
   *
   * 시각 비교(now >= 기준시각)로 두면 안 된다 — create() 시점에는 Scene.time.now가
   * 아직 0이라(Clock은 첫 preUpdate에서 갱신된다) 기준시각이 0 + 유예로 잡히고,
   * 실제 게임 루프 시각은 이미 그보다 훨씬 커서 유예가 처음부터 만료돼 버린다.
   */
  private worldClickEnabled: boolean = false;

  /** 유예 중 안내를 이미 띄웠는지. 클릭할 때마다 채팅창이 도배되지 않게 한다. */
  private worldNotReadyNoticed: boolean = false;

  /**
   * 세션 유실을 이미 처리했는지. 재연결이 여러 번 튀어도 안내와 화면 전환을
   * 한 번만 하기 위한 플래그.
   */
  private sessionLostHandled: boolean = false;

  /**
   * 이 y 이상(화면 아래쪽)은 하단 UI 영역이라 월드 클릭으로 취급하지 않는다.
   * 채팅창 상단에 맞춘다 — 그 아래는 채팅창·채널 탭·HP/MP 바가 차지하고,
   * 컴포넌트 사이 빈틈으로 클릭이 새면 UI를 누르려다 캐릭터가 걸어간다.
   */
  private worldClickBottomLimit: number = 0;

  // Chat history
  private chatHistory: string[] = [];

  // networkEvents는 GameState도 구독하는 전역 버스라 offAll(event)로 통째로
  // 지울 수 없다 — 씬 종료 시 이 씬이 등록한 핸들러만 정확히 제거하기 위해
  // (event, handler) 쌍을 직접 추적한다.
  private networkEventHandlers: Array<{ event: string; handler: (data: any) => void }> = [];

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Phaser는 씬 인스턴스를 재사용하므로(캐릭터 선택 복귀 후 재진입 등)
    // 이전 세션의 잔존 상태를 먼저 초기화한다. entitySprites가 참조하던
    // 스프라이트들은 씬 SHUTDOWN 시 Phaser가 이미 파괴했으므로 destroy 호출
    // 없이 Map만 비운다.
    this.entitySprites.clear();
    // 바닥 아이템도 같은 이유로 Map만 비운다 (스프라이트는 이미 파괴됐다).
    this.groundItems.clear();
    // 주우러 가던 예약도 씬을 넘기지 않는다 (재진입 직후 엉뚱한 줍기가 나간다).
    this.pendingPickup = null;
    this.isStepping = false;
    this.stepElapsed = 0;
    this.clearMoveTarget();
    this.isMenuVisible = false;
    // 사망으로 열린 메뉴의 흔적도 씬을 넘기지 않는다 — 타이머 자체는 씬 종료 시
    // Phaser가 파괴하지만, 참조가 남으면 재진입 후 remove()가 죽은 타이머를 만진다.
    this.menuOpenedByDeath = false;
    this.deathMenuTimer = null;
    this.chatHistory = [];
    this.currentHeading = 4;
    this.currentMapId = null;
    this.playerTileX = 0;
    this.playerTileY = 0;
    this.depthOrigin = 0;
    this.lastTileGridCenterX = null;
    this.lastTileGridCenterY = null;
    this.lastTileGridPlayerX = null;
    this.lastTileGridPlayerY = null;
    this.networkEventHandlers = [];
    this.uiObjects = [];
    // 대상 선택 대기도 씬을 넘어 남으면 안 된다 (재진입 직후 클릭이 대상 지정으로 먹힌다).
    this.pendingUse = null;
    // 감정 대상 기억도 씬을 넘기지 않는다 (다른 캐릭터의 인벤토리에 붙으면 안 된다).
    this.pendingIdentifyTargetId = null;
    this.pendingIdentifyAt = 0;
    this.worldClickConsumed = false;
    // 추격·대화 대기도 씬을 넘기지 않는다 (타이머는 씬 종료 시 Phaser가 파괴한다).
    this.clearChase();
    this.chaseIntent = 'attack';
    this.npcTalkPending = null;
    // 변신/광원도 캐릭터마다 다시 통지받는다. 남겨두면 다른 캐릭터가 몹 모습으로
    // 시작하거나 화면이 까맣게 시작한다.
    this.playerPolyFamily = null;
    this.playerPolyTint = 0xffffff;
    this.playerLightTiles = 0;
    // 텔레포트 잠금은 씬을 넘나들며 남으면 안 된다 (재진입 시 조작 불가가 된다).
    this.teleportPending = false;
    this.teleportTimeout = null;
    // 텍스처 캐시는 spriteLoader가 전역으로 들고 있지만 preload 여부는 씬 단위로
    // 다시 판단해도 무해하다 (이미 받은 파일은 캐시에서 즉시 resolve된다).
    this.preloadedMobFamilies.clear();

    // 진입 직후 유예. 씬 인스턴스가 재사용되므로 create마다 다시 건다.
    // (Clock의 delayedCall은 씬 시작 기준이라 시각 계산이 필요 없다)
    this.worldClickEnabled = false;
    this.worldNotReadyNoticed = false;
    this.sessionLostHandled = false;
    this.time.delayedCall(WORLD_CLICK_GRACE_MS, () => {
      this.worldClickEnabled = true;
    });
    // createUI가 채팅창을 만든 뒤 실제 값으로 덮어쓴다. 그 전에는 모든 클릭을 막는다.
    this.worldClickBottomLimit = 0;

    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // 다크 배경
    this.cameras.main.setBackgroundColor(0x0a0a15);

    // 카메라 구성 (월드 줌 + UI 전용 카메라). 월드 오브젝트를 만들기 전에
    // 먼저 실행해야 registerWorldObjects()가 UI 카메라에 등록될 수 있다.
    this.setupCameras(width, height);

    // 타일 그리드 생성
    this.createTileGrid();

    // 게이트웨이 연결
    this.setupConnection();

    // UI 생성
    this.createUI(width, height);

    // 생성된 UI를 UI 카메라 전용으로 배정 (메인 카메라는 무시)
    this.registerUiObjects();

    // 입력 설정
    this.setupInput();

    // 네트워크 이벤트 리스닝
    this.setupNetworkListeners();

    // 게임 상태 이벤트 리스닝
    this.setupGameStateListeners();

    // 플레이어 스프라이트 생성 (초기 타일 = (0,0), 실제 위치는 hydrate/스폰에서 스냅)
    this.createPlayerSprite();
    // cameraTargetX/Y를 초기화하여 update의 카메라 lerp가 플레이어 월드 좌표를 따라가도록 한다.
    // startFollow는 호출하지 않는다 (update에서 단일하게 lerp 처리하므로 이중 적용 방지).
    const origin = tileToWorld(this.playerTileX, this.playerTileY);
    this.cameraTargetX = origin.x;
    this.cameraTargetY = origin.y;
    this.cameras.main.setScroll(origin.x - width / 2, origin.y - height / 2);
    this.cameras.main.setRoundPixels(true);
    // 줌은 setupCameras()에서 이미 적용했다 (여기서 다시 1로 되돌리지 말 것).

    // CharSelectScene 단계에서 이미 도착한 own_char_status/spawn을 GameState가
    // 선점해뒀을 수 있다 (리스너 등록 전 레이스). 현재 스냅샷을 그대로 반영한다.
    this.hydrateFromGameState();

    // ESC 메뉴 숨김 상태로 시작
    this.menuPanel.setVisible(false);

    // 씬 종료 시(캐릭터 선택으로 복귀 등) 이 씬이 등록한 리스너를 정리한다.
    // 정리하지 않으면 GameScene에 다시 진입할 때마다 핸들러가 중복 등록되어
    // 채팅/스폰/이동 이벤트가 여러 번 처리된다.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanupListeners, this);

    // 개발 모드 한정 테스트용 dev hook.
    // - window.__testDeathPlayer(): 자기 캐릭터 사망 애니메이션 즉시 시작
    // - window.__testRevivePlayer(): 사망 애니메이션 해제 (부활 시뮬레이션)
    // Phaser 테스트 또는 브라우저 devtools 콘솔에서 직접 호출해 동작을 확인할 수 있다.
    if (typeof window !== 'undefined') {
      (window as any).__testDeathPlayer = () => this.startPlayerDeathAnimation();
      (window as any).__testRevivePlayer = () => this.clearPlayerDeathAnimation();
      // - window.__l1jGateStats(): 가속기 검사 게이트 현황 (화면에는 띄우지 않는다)
      (window as any).__l1jGateStats = () => ({
        attackGateMs: Math.round(this.attackGateMs()),
        moveGateMs: Math.round(this.moveGateMs()),
        // 게이트가 어떤 무기로 계산됐는지 — 이 값이 틀리면 곧바로 강제 접속 종료다.
        inventoryWeapon: this.playerWeapon,
        visualUpdateWeapon: gameState.getPlayerWeapon(),
        attackActCandidates: this.attackActCandidates(),
        attackGateDeferrals: this.attackGateDeferrals,
        moveGateHolds: this.moveGateHolds,
        pendingStrikeTargetId: this.pendingStrikeTargetId,
        sinceLastAttackMs: Math.round(performance.now() - this.lastAttackSentAt),
      });
      /**
       * - window.__l1jEntities(): 화면에 있는 엔티티와 자기 위치를 함께 돌려준다.
       *
       * 월드는 Phaser 캔버스라 DOM에서 몹을 집을 수 없다. 자동화 테스트(공격
       * 대상 클릭 등)가 좌표를 얻을 유일한 통로라서 화면 좌표까지 같이 낸다 —
       * 캔버스 좌측 상단 기준이므로, 페이지 좌표로 쓰려면 캔버스의 offset과
       * 표시 배율(캔버스 크기 / 게임 해상도)을 곱해야 한다.
       */
      (window as any).__l1jEntities = () => {
        const cam = this.cameras.main;
        const entities: any[] = [];
        this.entitySprites.forEach((e) => {
          entities.push({
            objId: e.objId,
            type: e.type,
            name: e.nameText.text,
            tileX: e.tileX,
            tileY: e.tileY,
            /** deathAnim이 걸려 있으면 쓰러지는 중이거나 이미 묘비다. */
            dead: !!e.deathAnim,
            visible: e.sprite.visible,
            // 월드 → 화면 변환은 scrollX가 아니라 worldView를 기준으로 해야 한다.
            // 이 카메라는 setZoom()을 쓰는데(viewZoomFor), scrollX는 zoom을 반영하지
            // 않는 값이라 그대로 곱하면 대상이 실제와 다른 지점으로 계산된다.
            screenX: (e.sprite.x - cam.worldView.x) * cam.zoom,
            screenY: (e.sprite.y - cam.worldView.y) * cam.zoom,
          });
        });
        return {
          player: {
            tileX: this.playerTileX,
            tileY: this.playerTileY,
            weapon: this.playerWeapon,
            mapId: this.currentMapId,
            screenX: (this.playerSprite.x - cam.worldView.x) * cam.zoom,
            screenY: (this.playerSprite.y - cam.worldView.y) * cam.zoom,
          },
          camera: {
            zoom: cam.zoom,
            viewX: cam.worldView.x,
            viewY: cam.worldView.y,
            width: cam.width,
            height: cam.height,
          },
          ui: {
            worldClickEnabled: this.worldClickEnabled,
            worldClickBottomLimit: this.worldClickBottomLimit,
            menuVisible: this.isMenuVisible,
            chatActive: this.chatBox?.isInputActive?.() ?? false,
            inventoryVisible: this.inventory?.visible ?? false,
            htmlDialogOpen: this.htmlDialog?.isOpen?.() ?? false,
          },
          entities,
        };
      };
      /**
       * - window.__l1jAttack(objId): 클릭 경로를 거치지 않고 곧바로 공격을 건다.
       *
       * 자동화에서 캔버스 클릭 좌표를 맞추는 것과 전투 자체를 확인하는 것은 서로
       * 다른 문제다. 전투·사망 연출을 보려는 테스트가 좌표 계산에 발목 잡히지
       * 않도록 대상 지정 경로만 따로 열어 둔다.
       */
      (window as any).__l1jAttack = (objId: number) => {
        const target = this.entitySprites.get(objId);
        if (!target) return { ok: false, reason: '대상 없음' };
        if (target.type !== 'monster') return { ok: false, reason: `type=${target.type}` };
        this.beginAttack(target);
        return { ok: true, objId, tileX: target.tileX, tileY: target.tileY };
      };
    }

    // 인트로(`/` 경로)를 걷는다. 월드가 한 번은 그려진 뒤에 걷어야 검은 화면이
    // 비치지 않으므로 몇 프레임 분량을 기다린다.
    this.time.delayedCall(350, () => introOverlay.dismiss());
  }

  /**
   * 이 씬이 등록한 networkEvents/gameEvents 리스너를 해제한다.
   * networkEvents는 GameState도 구독하는 전역 버스이므로 이벤트 이름 단위로
   * 통째로 지우면(offAll(event)) GameState의 구독까지 함께 사라진다 — 반드시
   * 이 씬에서 등록한 핸들러 참조만 개별적으로 off()해야 한다.
   * gameEvents는 GameScene 외에 구독자가 없어 offAll()로 전체 정리해도 안전하다.
   */
  private cleanupListeners(): void {
    this.networkEventHandlers.forEach(({ event, handler }) => networkEvents.off(event, handler));
    this.networkEventHandlers = [];
    // 씬 인스턴스는 재진입 때 그대로 재사용되므로 미뤄둔 공격을 남기면 안 된다.
    // (lastAttackSentAt은 벽시계라 재진입 첫 공격은 어차피 즉시 나간다.)
    this.clearPendingStrike();
    gameEvents.offAll();
    // 씬을 나갈 때 UI 카메라도 정리한다. 정리하지 않으면 재진입 시 카메라가
    // 계속 쌓여 UI가 여러 번 렌더링된다.
    this.destroyUiCamera();
    this.uiObjects = [];
  }

  // ==================== 카메라 (월드 / UI 분리) ====================

  /**
   * 캔버스 크기에 대응하는 월드 카메라 줌.
   * 기준 시야(REFERENCE_VIEW_*)와 같은 면적만큼의 타일이 보이도록 면적비의
   * 제곱근을 쓴다. 예) 1024x768 -> sqrt((1024*768)/(640*377)) ≈ 1.81
   *
   * 면적으로 맞추므로 캔버스 종횡비가 원본과 달라도 보이는 타일 **수**는 유지된다
   * (가로로 긴 창이면 좌우로, 세로로 긴 창이면 위아래로 조금씩 더 보인다).
   */
  private static viewZoomFor(camWidth: number, camHeight: number): number {
    return Math.sqrt(
      (camWidth * camHeight) / (REFERENCE_VIEW_WIDTH * REFERENCE_VIEW_HEIGHT)
    );
  }

  /**
   * 메인(월드) 카메라 줌 적용 + UI 전용 카메라 생성.
   *
   * Phaser에서는 setScrollFactor(0)인 오브젝트도 **카메라 zoom의 영향을 받는다**.
   * 따라서 메인 카메라에 줌을 걸면 채팅창/상태바/미니맵/퀵슬롯까지 같은 배율로
   * 확대되어 레이아웃이 깨진다. 이를 피하려고 카메라를 둘로 나눈다.
   *
   *  - cameras.main : 줌 적용, 월드(타일 그리드/플레이어/엔티티/이름표/HP바)만 렌더
   *  - uiCamera     : 줌 1 고정 + 스크롤 0, UI만 렌더
   *
   * 배정은 상호 ignore()로 한다 — 월드 오브젝트는 registerWorldObjects()에서
   * UI 카메라가 무시하고, UI 오브젝트는 registerUiObjects()에서 메인 카메라가
   * 무시한다. 포인터 히트테스트도 cameraFilter를 따르므로(Phaser InputManager의
   * inputCandidate()), 카메라 분리만으로 클릭 판정도 각자 올바른 카메라 기준이 된다.
   */
  private setupCameras(width: number, height: number): void {
    this.cameras.main.setZoom(GameScene.viewZoomFor(width, height));

    // 씬 재진입(create 재실행) 시 이전 UI 카메라가 남아 있으면 먼저 제거한다.
    this.destroyUiCamera();

    const uiCamera = this.cameras.add(0, 0, width, height);
    uiCamera.setName('ui');
    uiCamera.setZoom(1);
    uiCamera.setScroll(0, 0);
    uiCamera.setRoundPixels(true);
    this.uiCamera = uiCamera;
  }

  /** UI 카메라 제거 (이미 씬 종료로 파괴된 경우에도 안전하다). */
  private destroyUiCamera(): void {
    if (!this.uiCamera) return;
    if (this.cameras && this.cameras.cameras.indexOf(this.uiCamera) !== -1) {
      this.cameras.remove(this.uiCamera, true);
    }
    this.uiCamera = null;
  }

  /**
   * 월드 오브젝트로 등록한다 = UI 카메라가 무시한다.
   * 엔티티 스프라이트처럼 게임 도중 동적으로 생성되는 오브젝트는 생성 직후 반드시 호출할 것.
   */
  private registerWorldObjects(...objects: Phaser.GameObjects.GameObject[]): void {
    if (!this.uiCamera || objects.length === 0) return;
    this.uiCamera.ignore(objects);
  }

  /**
   * UI 오브젝트를 한곳에서 모아 메인 카메라가 무시하도록 배정한다.
   *
   * UI 컴포넌트는 전부 Container이므로 컨테이너만 등록하면 자식까지 함께 처리된다
   * (Phaser의 렌더/입력 모두 부모 Container의 willRender를 먼저 확인한다).
   * ChatBox의 입력창처럼 씬 최상위에 붙는 DOM 요소는 어떤 컨테이너에도 속하지
   * 않으므로 디스플레이 리스트에서 직접 찾아 추가한다. DOM 요소도 카메라 행렬
   * (= zoom)을 CSS transform으로 그대로 반영하므로 UI 카메라에 붙여야 한다.
   */
  private registerUiObjects(): void {
    const candidates: Array<Phaser.GameObjects.GameObject | undefined> = [
      this.statusBar,
      this.chatBox,
      this.miniMap,
      this.inventory,
      this.characterStatsPanel,
      this.characterDetailPanel,
      this.equipmentPanel,
      this.htmlDialog,
      this.bookmarkDialog,
      this.identifyDialog,
      this.quantityDialog,
      this.targetPrompt,
      this.quickSlotBar,
      this.menuButton,
      this.menuPanel,
      this.controlHints,
      ...this.children.list.filter((obj) => obj instanceof Phaser.GameObjects.DOMElement),
    ];

    this.uiObjects = candidates.filter(
      (obj): obj is Phaser.GameObjects.GameObject => obj !== undefined && obj !== null
    );

    this.cameras.main.ignore(this.uiObjects);
  }

  /**
   * networkEvents 구독 + 씬 종료 시 해제를 위한 참조 추적을 함께 처리하는 헬퍼.
   */
  private onNetwork<T = any>(event: string, handler: (data: T) => void): void {
    networkEvents.on(event, handler as (data: unknown) => void);
    this.networkEventHandlers.push({ event, handler: handler as (data: any) => void });
  }

  /**
   * GameScene의 네트워크/상태 리스너가 등록되기 전에 GameState가 이미 받아둔
   * 플레이어 위치·스탯·주변 엔티티를 초기 렌더링에 반영한다.
   */
  private hydrateFromGameState(): void {
    if (gameState.getPlayerObjId() !== null) {
      const pos = gameState.getPlayerPosition();
      this.snapPlayerToTile(pos.x, pos.y);
      this.miniMap.setPlayerPosition(pos.x, pos.y, pos.heading);
    }

    // 자기 캐릭터의 외형(클래스/성별).
    //
    // player:set 이벤트만 믿으면 안 된다 — scene.start()는 다음 프레임에 create를
    // 실행하는데, 그 사이 도착한 spawn은 리스너가 없는 상태에서 처리돼 이벤트가
    // 그대로 사라진다. 실제로 진입 시 spawn이 create보다 먼저 오는 경우가 있어
    // 캐릭터가 기본 외형(prince_m)으로 남았다.
    const gfxId = gameState.getPlayerGfxId();
    if (gfxId !== null) {
      this.playerCharacterId = inferCharacter(gfxId);
      this.playerWeapon = this.resolvePlayerWeapon();
    }
    // 변신·광원도 create 전에 도착했을 수 있다 (위 gfxId와 같은 이유).
    // 변신은 applyPlayerTexture()가 참조하므로 그 전에 정해 둔다.
    const poly = gameState.getPlayerPoly();
    if (poly) {
      this.playerPolyFamily = inferMobFamily(poly, this.playerNameText.text);
      this.playerPolyTint = mobTint(this.playerNameText.text);
      this.preloadMobFamilyOnce(this.playerPolyFamily);
    }
    this.playerLightTiles = gameState.getPlayerLight();
    this.applyPlayerTexture();

    // 상세 스탯과 인벤토리는 GameScene이 시작되기 전에 이미 도착해 있을 수 있다.
    // 그 경우 player:detail / inventory 이벤트는 리스너 없이 지나가므로,
    // 여기서 현재 값을 직접 읽어 창을 채운다.
    const detail = gameState.getDetailStats();
    if (detail) this.characterDetailPanel.setStats(detail);

    const storedItems = gameState.getInventory();
    if (storedItems.length > 0) {
      this.applyInventory(this.toInventoryItems(storedItems));
    }

    const stats = gameState.getPlayerStats();
    this.statusBar.setStatus({
      hp: stats.hp,
      maxHp: stats.maxHp,
      mp: stats.mp,
      maxMp: stats.maxMp,
      level: stats.level,
    });

    gameState.getNpcs().forEach((npc) => this.spawnEntitySprite(npc, 'npc'));
    gameState.getMonsters().forEach((monster) => this.spawnEntitySprite(monster, 'monster'));
    gameState.getPlayers().forEach((player) => this.spawnEntitySprite(player, 'player'));

    // 이미 알고 있는 변신/투명 상태를 방금 만든 스프라이트에 얹는다.
    // (entity:poly / entity:invis 이벤트도 create 전에 지나갔을 수 있다)
    this.entitySprites.forEach((_entity, objId) => {
      const entityPoly = gameState.getEntityPoly(objId);
      if (entityPoly) this.applyEntityPoly(objId, entityPoly, false);
      if (gameState.isEntityInvisible(objId)) this.applyEntityInvis(objId, true, false);
    });

    // map_id는 씬이 리스너를 걸기 전에 지나갈 수 있으므로(로그인 직후가 그렇다)
    // GameState가 이미 알고 있으면 여기서 따라잡는다. **맵 0이 유효한 맵**이라
    // 값으로 판정하면(> 0) 말하는 섬·본토에서 영영 따라잡지 못하고 currentMapId가
    // null로 남아 벽 충돌 판정과 경로 탐색이 통째로 꺼진다.
    if (gameState.hasMapId()) {
      this.currentMapId = gameState.getMapId();
      mapCollision.setCurrentMap(this.currentMapId, () => this.updateTileGrid(true));
    }
  }

  /**
   * 타일 그리드 배경 생성.
   * 실제 서버 맵 데이터(maps/{id}.txt, 게이트웨이 /map/{id}가 서빙)를 읽어
   * 통행 가능/벽 타일을 카메라 주변에만 그린다. 플레이어가 같은 타일에 머무는
   * 동안은 다시 그리지 않고, 맵 데이터가 늦게 도착하면(첫 진입 시) 도착 시점에
   * 한 번 더 그린다 — updateTileGrid()/맵 로드 콜백 참고.
   */
  private createTileGrid(): void {
    this.tileGridGraphics = this.add.graphics();
    this.tileGridGraphics.setDepth(TILE_GRID_DEPTH);
    this.registerWorldObjects(this.tileGridGraphics);

    // 벽 레이어. depth는 매번 updateTileGrid()가 밴드에 맞춰 다시 잡는다.
    this.wallLayers = [];
    for (let i = 0; i < WALL_LAYER_COUNT; i++) {
      const layer = this.add.graphics();
      this.registerWorldObjects(layer);
      this.wallLayers.push(layer);
    }

    this.updateTileGrid(true);
  }

  /**
   * 카메라가 새 타일로 넘어갔을 때(또는 force=true) 주변 타일 마름모를 다시 그린다.
   * 맵 데이터가 아직 없으면 통행 가능 여부를 모르는 회색조 타일로 채운다.
   *
   * 아이소메트릭에서는 "화면 사각형"이 타일 공간에서는 마름모라, 화면 네 모서리를
   * worldToTileFloat()로 변환해 tx/ty의 min/max를 구한 뒤 그 사각 범위를 훑고
   * 실제 화면 밖 타일은 개별적으로 걸러낸다.
   */
  private updateTileGrid(force: boolean = false): void {
    const cam = this.cameras.main;

    // 줌이 1이 아니면 실제로 보이는 월드 영역은 cam.width/height가 아니라
    // (cam.width / zoom) x (cam.height / zoom)이다. cam.worldView와 같은 계산이지만
    // worldView는 preRender 이후에만 갱신되므로(create() 시점에는 비어 있다)
    // 여기서 직접 구한다. 이 값을 쓰지 않으면 화면 밖 타일까지 그려 낭비된다.
    const zoom = cam.zoom || 1;
    const viewWidth = cam.width / zoom;
    const viewHeight = cam.height / zoom;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;
    const viewLeft = centerX - viewWidth / 2;
    const viewTop = centerY - viewHeight / 2;
    const viewRight = viewLeft + viewWidth;
    const viewBottom = viewTop + viewHeight;

    // 카메라 중심 타일 + 플레이어 타일이 모두 그대로면 다시 그릴 필요가 없다.
    // (서버 가시 범위 링은 플레이어 타일 기준이라 플레이어 이동도 감지해야 한다.)
    const center = worldToTile(centerX, centerY);
    if (
      !force
      && this.lastTileGridCenterX === center.tx
      && this.lastTileGridCenterY === center.ty
      && this.lastTileGridPlayerX === this.playerTileX
      && this.lastTileGridPlayerY === this.playerTileY
    ) {
      return;
    }
    this.lastTileGridCenterX = center.tx;
    this.lastTileGridCenterY = center.ty;
    this.lastTileGridPlayerX = this.playerTileX;
    this.lastTileGridPlayerY = this.playerTileY;

    const g = this.tileGridGraphics;
    g.clear();
    for (const layer of this.wallLayers) layer.clear();

    // 바닥/벽 색은 맵 테마(마을·던전·숲…)에서 온다. 어느 테마를 쓰든 바닥과 벽의
    // 대비비는 3:1 이상으로 잡아 두었다 — THEME_PALETTES 주석 참고.
    const palette = THEME_PALETTES[mapTheme(this.currentMapId)];
    const passableColor = palette.floor;
    const wallColor = palette.wall;
    const unknownColor = 0x101018;   // 맵 데이터 로딩 전
    // 격자선과 벽 윤곽은 각자의 면 색을 밝힌 것이다. 테마마다 따로 고르지 않아도
    // 색조가 저절로 맞는다.
    const lineColor = shadeColor(passableColor, 1.6);
    // 벽 타일의 테두리. 가시 범위 밖(알파 0.35)이나 광원 밖(×0.45)으로 흐려져
    // 면 색이 배경에 묻히는 구간에서도 벽 덩어리의 윤곽은 남게 한다.
    const wallLineColor = shadeColor(wallColor, 1.4);

    const mapId = this.currentMapId;
    const loaded = mapId !== null && mapCollision.isLoaded(mapId);

    // 화면 네 모서리를 타일 공간으로 옮겨 경계 상자를 만든다 (+여유 2타일).
    const corners = [
      worldToTileFloat(viewLeft, viewTop),
      worldToTileFloat(viewRight, viewTop),
      worldToTileFloat(viewLeft, viewBottom),
      worldToTileFloat(viewRight, viewBottom),
    ];
    const margin = 2;
    const minTx = Math.floor(Math.min(...corners.map((c) => c.tx))) - margin;
    const maxTx = Math.ceil(Math.max(...corners.map((c) => c.tx))) + margin;
    const minTy = Math.floor(Math.min(...corners.map((c) => c.ty))) - margin;
    const maxTy = Math.ceil(Math.max(...corners.map((c) => c.ty))) + margin;

    // 타일 한 칸이 화면 밖으로 얼마나 걸쳐 있어도 그릴지 (마름모 반경).
    const padX = 32;
    const padY = 20;

    // 서버 가시 범위(맨해튼 19타일) 안/밖의 알파 값.
    // 밖은 서버가 엔티티도 채팅도 보내주지 않는 영역이라 눈에 띄게 흐리게 그린다.
    const inRangeFillAlpha = 1;
    const inRangeLineAlpha = 0.25;
    const outOfRangeFillAlpha = 0.35;
    const outOfRangeLineAlpha = 0.07;

    // 광원 반경(S_Light) 밖은 한 단계 더 어둡게 깐다. 별도 조명 시스템을 두지
    // 않고 이미 있는 바닥 알파에 곱하기만 한다 — 램프/라이트 마법으로 시야가
    // 넓어지는 것이 눈에 보이면 그것으로 충분하다.
    // 0이면 아직 S_Light를 못 받은 것이므로 아무것도 어둡게 하지 않는다.
    const lightTiles = this.playerLightTiles;
    const outOfLightScale = 0.45;

    let currentColor = -1;
    let currentFillAlpha = -1;
    let currentLineColor = -1;
    let currentLineAlpha = -1;

    // 벽은 곧바로 그리지 않고 모아 둔다. 블록으로 세우면 서로 겹치므로 화면
    // 안쪽(작은 tx+ty)부터 순서대로 그려야 하는데, 이 루프는 ty→tx 순이라
    // 그 순서가 아니다. 평면 마름모일 때는 겹치지 않아 문제가 없었다.
    const walls: Array<{ tx: number; ty: number; depth: number; fillAlpha: number; lineAlpha: number }> = [];
    const playerDepth = tileDepth(this.playerTileX, this.playerTileY);

    for (let ty = minTy; ty <= maxTy; ty++) {
      for (let tx = minTx; tx <= maxTx; tx++) {
        const { x, y } = tileToWorld(tx, ty);
        // 경계 상자 안이지만 실제로는 화면 밖인 타일(마름모 바깥 모서리)을 걸러낸다.
        if (x < viewLeft - padX || x > viewRight + padX) continue;
        // 벽은 위로 솟으므로 화면 위쪽으로 블록 높이만큼 더 여유를 둔다 —
        // 그러지 않으면 화면 상단 경계의 벽이 몸통만 잘려 나타난다.
        if (y < viewTop - padY - WALL_HEIGHT_PX || y > viewBottom + padY) continue;

        let color = unknownColor;
        let isWall = false;
        if (loaded && mapId !== null) {
          isWall = !mapCollision.isPassable(mapId, tx, ty);
          // 바닥은 타일마다 명도를 아주 조금 흔들어 질감을 만든다 (얼룩).
          // 벽은 블록 면이 이미 세 가지 밝기로 갈리므로 손대지 않는다.
          color = isWall ? wallColor : shadeColor(passableColor, floorShadeFactor(tx, ty));
        }

        const playerDistance = tileDistance(tx, ty, this.playerTileX, this.playerTileY);
        const outOfServerRange = playerDistance > SERVER_VISIBLE_TILE_RANGE;
        const outOfLight = lightTiles > 0 && playerDistance > lightTiles;
        const dim = outOfLight ? outOfLightScale : 1;
        const fillAlpha = (outOfServerRange ? outOfRangeFillAlpha : inRangeFillAlpha) * dim;
        const lineAlpha = (outOfServerRange ? outOfRangeLineAlpha : inRangeLineAlpha) * dim;

        if (isWall) {
          walls.push({ tx, ty, depth: tileDepth(tx, ty), fillAlpha, lineAlpha });
          continue;
        }

        if (color !== currentColor || fillAlpha !== currentFillAlpha) {
          g.fillStyle(color, fillAlpha);
          currentColor = color;
          currentFillAlpha = fillAlpha;
        }
        // 색이 바뀔 때만 스타일을 다시 건다 (기존 배치 그리기 구조 유지).
        if (lineColor !== currentLineColor || lineAlpha !== currentLineAlpha) {
          g.lineStyle(1, lineColor, lineAlpha);
          currentLineColor = lineColor;
          currentLineAlpha = lineAlpha;
        }

        this.traceTileDiamond(g, tx, ty);
        g.fillPath();
        g.strokePath();
      }
    }

    this.drawWallBlocks(walls, playerDepth, wallColor, wallLineColor);
  }

  /**
   * 모아 둔 벽 타일을 육면체 블록으로 세운다.
   *
   * 두 가지 순서를 지켜야 한다.
   *
   * 1. **레이어 사이** — 벽 뒤에 선 엔티티가 벽 위로 떠오르지 않도록, 타일 깊이
   *    밴드마다 레이어를 나누고 각 레이어의 depth를 그 밴드의 타일 깊이에 맞춘다.
   * 2. **레이어 안** — 블록끼리도 겹치므로 화면 안쪽(작은 tx+ty)부터 그린다.
   *    평면 마름모일 때는 겹치지 않아 순서가 문제되지 않았다.
   *
   * 플레이어를 실제로 가리는 벽만 비쳐 보이게 한다. 앞쪽 벽을 전부 반투명하게
   * 만들면 벽이 벽으로 보이지 않는다.
   */
  private drawWallBlocks(
    walls: Array<{ tx: number; ty: number; depth: number; fillAlpha: number; lineAlpha: number }>,
    playerDepth: number,
    wallColor: number,
    wallLineColor: number,
  ): void {
    if (walls.length === 0) return;

    walls.sort((a, b) => a.depth - b.depth);

    const rightColor = shadeColor(wallColor, WALL_SIDE_RIGHT_SHADE);
    const leftColor = shadeColor(wallColor, WALL_SIDE_LEFT_SHADE);

    // 플레이어 몸이 차지하는 화면 사각형 — 앞쪽 벽이 이것과 겹치면 가림 처리한다.
    const player = tileToWorld(this.playerTileX, this.playerTileY);
    const bodyLeft = player.x - PLAYER_BODY_HALF_WIDTH;
    const bodyRight = player.x + PLAYER_BODY_HALF_WIDTH;
    const bodyTop = player.y - PLAYER_BODY_TOP;

    // 밴드 0의 기준. 화면 안 벽 중 가장 뒤쪽 깊이에서 시작한다.
    const baseDepth = walls[0].depth;
    const lastIndex = WALL_LAYER_COUNT - 1;

    for (const wall of walls) {
      // 범위를 넘는 벽(화면 대각선 끝)은 가장자리 레이어에 몰아넣는다.
      const band = Math.min(
        lastIndex,
        Math.floor((wall.depth - baseDepth) / WALL_LAYER_BAND),
      );
      const layer = this.wallLayers[band];

      let alphaScale = 1;
      if (wall.depth > playerDepth) {
        const { x, y } = tileToWorld(wall.tx, wall.ty);
        // 블록이 그려지는 화면 범위 (윗면은 WALL_HEIGHT_PX만큼 위로 올라간다).
        const overlaps =
          x + HALF_TILE_WIDTH > bodyLeft &&
          x - HALF_TILE_WIDTH < bodyRight &&
          y + HALF_TILE_HEIGHT > bodyTop &&
          y - WALL_HEIGHT_PX - HALF_TILE_HEIGHT < player.y;
        if (overlaps) alphaScale = WALL_OCCLUDING_ALPHA;
      }

      this.drawWallBlock(
        layer,
        wall.tx,
        wall.ty,
        wallColor,
        rightColor,
        leftColor,
        wallLineColor,
        wall.fillAlpha * alphaScale,
        wall.lineAlpha * alphaScale,
      );
    }

    // 레이어 depth를 밴드의 타일 깊이에 맞춘다. 밴드가 덮는 마지막 타일 깊이를
    // 기준으로 삼아, 그보다 앞(깊이가 큰)에 선 엔티티가 벽 위로 올라오게 한다.
    for (let band = 0; band < WALL_LAYER_COUNT; band++) {
      const bandLastDepth = baseDepth + band * WALL_LAYER_BAND + (WALL_LAYER_BAND - 1);
      const d = (bandLastDepth - this.depthOrigin) * DEPTH_SCALE;
      this.wallLayers[band].setDepth(
        Math.max(-DEPTH_LIMIT, Math.min(DEPTH_LIMIT, d)) + DEPTH_SCALE / 2,
      );
    }
  }

  /**
   * 벽 한 칸을 육면체로 그린다.
   *
   * 밑면은 타일 마름모 그대로이고, 윗면은 그것을 WALL_HEIGHT_PX만큼 올린 것이다.
   * 사이의 앞쪽 두 변(왼-아래, 아래-오른)이 좌우 측면이 된다. 뒤쪽 두 변은 어차피
   * 윗면과 이웃 블록에 가려지므로 그리지 않는다.
   *
   * 측면 → 윗면 순으로 그려야 윗면이 측면 위에 덮인다.
   */
  private drawWallBlock(
    g: Phaser.GameObjects.Graphics,
    tx: number,
    ty: number,
    topColor: number,
    rightColor: number,
    leftColor: number,
    lineColor: number,
    fillAlpha: number,
    lineAlpha: number,
  ): void {
    const p = tileDiamondPoints(tx, ty);
    // [top, right, bottom, left] 순서 (isometric.ts).
    const topX = p[0], topY = p[1];
    const rightX = p[2], rightY = p[3];
    const botX = p[4], botY = p[5];
    const leftX = p[6], leftY = p[7];
    const h = WALL_HEIGHT_PX;

    // 우측면: bottom → right → right(위) → bottom(위)
    g.fillStyle(rightColor, fillAlpha);
    g.beginPath();
    g.moveTo(botX, botY);
    g.lineTo(rightX, rightY);
    g.lineTo(rightX, rightY - h);
    g.lineTo(botX, botY - h);
    g.closePath();
    g.fillPath();

    // 좌측면: left → bottom → bottom(위) → left(위)
    g.fillStyle(leftColor, fillAlpha);
    g.beginPath();
    g.moveTo(leftX, leftY);
    g.lineTo(botX, botY);
    g.lineTo(botX, botY - h);
    g.lineTo(leftX, leftY - h);
    g.closePath();
    g.fillPath();

    // 윗면: 마름모를 h만큼 올린 것.
    g.fillStyle(topColor, fillAlpha);
    g.lineStyle(1, lineColor, lineAlpha);
    g.beginPath();
    g.moveTo(topX, topY - h);
    g.lineTo(rightX, rightY - h);
    g.lineTo(botX, botY - h);
    g.lineTo(leftX, leftY - h);
    g.closePath();
    g.fillPath();
    g.strokePath();
  }

  /**
   * tileDiamondPoints()가 돌려준 4꼭짓점으로 Graphics 경로를 만든다.
   * (Phaser의 fillPoints/strokePoints는 {x,y} 객체 배열을 요구하므로,
   *  평평한 number[]를 그대로 쓰려면 경로 API를 직접 호출해야 한다.)
   */
  private traceTileDiamond(g: Phaser.GameObjects.Graphics, tx: number, ty: number): void {
    const p = tileDiamondPoints(tx, ty);
    g.beginPath();
    g.moveTo(p[0], p[1]);
    g.lineTo(p[2], p[3]);
    g.lineTo(p[4], p[5]);
    g.lineTo(p[6], p[7]);
    g.closePath();
  }

  /**
   * 게이트웨이 연결 설정
   */
  private setupConnection(): void {
    this.connectionManager = ConnectionManager.getInstance(ServerConfig.GATEWAY_URL);
    this.connectionManager.connect().catch((err) => {
      console.error('Gateway connection failed:', err);
    });
  }

  /**
   * UI 컴포넌트 생성 (라니지 클래식 스타일)
   *  - 채팅창: 화면 하단 중앙, HP/MP 바 바로 위에 밀착
   *  - HP/MP 바: 화면 최하단 중앙 가로 바
   *  - 미니맵: 우상단
   *  - 인벤토리: 우측 중앙 (기본 숨김)
   *  - 캐릭터 상태 패널, 단축키 바, 메뉴 버튼: 라니지 클래식 신규 컴포넌트
   */
  private createUI(width: number, height: number): void {
    // 하단 UI는 [채팅창] → [HP/MP 바] 순으로 화면 아래에 붙인다.
    // 두 컴포넌트 모두 원점 기준 실제 높이가 눈에 보이는 것과 달라서
    // (채팅창은 아래에 채널 탭 줄, HP/MP 바는 아래에 EXP 바가 더 붙는다)
    // 각자에게 실측 높이를 물어 계산한다.
    const bottomMargin = 8; // 화면 하단 여백
    const stackGap = 4;     // 채팅창과 HP/MP 바 사이 간격

    // HP/MP 가로 바 — 화면 최하단에 밀착
    this.statusBar = new StatusBar(this, width / 2, 0);
    this.statusBar.setY(height - bottomMargin - this.statusBar.getExtentBelow());
    this.statusBar.setDepth(100);

    // 채팅창 — HP/MP 바 바로 위에 밀착 (하단 중앙, 화면 가운데 정렬)
    const chatX = Math.max(10, width / 2 - 250);
    this.chatBox = new ChatBox(this, {
      maxMessages: 50,
      width: 500,
      height: 130,
      x: chatX,
      y: 0,
    });
    this.chatBox.setPosition(
      chatX,
      this.statusBar.y - this.statusBar.getExtentAbove() - stackGap - this.chatBox.getTotalHeight()
    );
    this.chatBox.setDepth(90);

    // 채팅창 상단부터 화면 끝까지는 하단 UI 영역 — 월드 클릭을 받지 않는다.
    this.worldClickBottomLimit = this.chatBox.y;

    // 채팅 메시지 전송 리스너
    this.chatBox.on('send', (data: { type: string; message: string }) => {
      this.sendChat(data.message, data.type as 'all' | 'shout' | 'world' | 'clan' | 'party' | 'trade' | 'system' | 'tell');
    });

    // 미니맵 — 우상단 (충분한 여백으로 잘림 방지)
    // MiniMap 컨테이너는 140x140 + 프레임 12px + 타이틀 16px = 약 158x168.
    // x = width - 90 (오른쪽 90px 여백), y = 100 (상단 100px 여백 - 타이틀 포함)
    this.miniMap = new MiniMap(this, width - 156, 105);
    this.miniMap.setDepth(100);

    // 인벤토리 — 화면 우측, 기본 숨김
    this.inventory = new Inventory(this, 0, 0, {
      onUseItem: (itemId: number) => {
        this.useItem(itemId);
      },
      onEquipItem: (itemId: number) => {
        this.equipItem(itemId);
      },
      // 대상 선택 모드에서 슬롯을 클릭했다 — 사용이 아니라 대상 지정이다.
      onSelectTarget: (itemId: number) => {
        this.handleInventoryTargetPick(itemId);
      },
      // 창의 X 버튼으로 닫혔다 — 인벤토리 대상 선택은 이어갈 수 없다.
      // 창을 닫은 그 클릭이 월드로 새지 않도록 함께 표시해 둔다.
      onClose: () => {
        this.worldClickConsumed = true;
        this.cancelInventoryTargetSelection();
      },
      // 인벤토리에서 끌어낸 아이템의 착지 지점을 아는 쪽은 씬이다.
      // 받는 곳은 퀵슬롯 바와 월드(= 바닥에 내려놓기) 둘이다.
      onItemDragMove: (_item, pointer) => {
        this.quickSlotBar.setDropTarget(this.quickSlotBar.getSlotIndexAt(pointer.x, pointer.y));
      },
      onItemDragEnd: (item, pointer) => {
        this.quickSlotBar.setDropTarget(null);
        const slotIndex = this.quickSlotBar.getSlotIndexAt(pointer.x, pointer.y);
        if (slotIndex !== null) {
          this.assignQuickSlot(slotIndex, item);
          return;
        }
        // 퀵슬롯이 아니면 월드에 놓은 것인지 본다. 창 위나 하단 UI에서 손을 떼면
        // "취소"다 — 실수로 아이템을 버리게 되면 되돌릴 방법이 없다.
        if (this.isWorldDropPoint(pointer)) {
          this.dropItemAt(item, pointer);
        }
      },
    });
    // 우측 정렬. 패널 폭은 슬롯 열 수에서 계산되므로 여기서 값을 가정하지 않고 물어본다.
    const inventoryMargin = 16;
    this.inventory.setPosition(
      width - this.inventory.getPanelWidth() / 2 - inventoryMargin,
      height / 2
    );
    this.inventory.setVisible(false);
    this.inventory.setDepth(150);

    // 라니지 클래식 신규 컴포넌트들 (Phase C에서 동적 생성)
    this.createCharacterStatsPanel(width, height);
    this.createQuickSlotBar(width, height);
    this.createMenuButton(width, height);

    // ESC 메뉴 패널
    this.createMenuPanel(width, height);

    // 조작법 힌트 (좌측 하단)
    this.createControlHints(width, height);

    // 좌측 끝 창 두 개 (기본 숨김)
    this.createLeftPanels();

    // NPC 대화창 — 화면 중앙, 기본 숨김. 채팅창을 가리지 않도록 살짝 위로 올린다.
    this.htmlDialog = new HtmlDialog(this, width / 2, height / 2 - 40);

    // 순간이동 북마크 목록 / 감정 결과 — 둘 다 화면 중앙, 기본 숨김.
    // 인벤토리(depth 150)에서 주문서를 더블클릭해 열리므로 컴포넌트 쪽에서
    // 이미 depth 210을 잡아 둔다 (여기서 다시 설정하지 않는다).
    this.bookmarkDialog = new BookmarkDialog(this, width / 2, height / 2);
    this.identifyDialog = new IdentifyDialog(this, width / 2, height / 2);

    // 개수 입력 창 — 겹친 아이템을 나눠 버릴 때 연다. 인벤토리에서 드래그하다
    // 열리므로 인벤토리(150)보다 위에 떠야 한다(컴포넌트가 depth 320을 잡는다).
    this.quantityDialog = new QuantityDialog(this, width / 2, height / 2);

    // 대상 선택 안내 배너 — 화면 상단 중앙, 기본 숨김.
    // 상단 조작법 힌트(y=10) 아래에 두어 겹치지 않게 한다.
    this.targetPrompt = new TargetPrompt(this, width / 2, 44, {
      onSubmitText: (text: string) => this.handleTextTargetSubmit(text),
      onCancel: () => this.cancelTargetSelection()
    });

    // 창을 X로 닫은 클릭이 월드 클릭으로 이어지지 않게 한다.
    // (인벤토리는 onClose 콜백에서 같은 플래그를 세운다)
    // BookmarkDialog는 목록 줄을 고르는 클릭에서도 이 이벤트를 쏜다 — 창이
    // 닫히는 클릭인 것은 마찬가지라, 안 받으면 고르는 순간 캐릭터가 걸어간다.
    [
      this.htmlDialog,
      this.equipmentPanel,
      this.characterDetailPanel,
      this.bookmarkDialog,
      this.identifyDialog,
    ].forEach((panel) => {
      panel.on('panelclose', () => {
        this.worldClickConsumed = true;
      });
    });
  }

  /**
   * 캐릭터 상세 정보 창과 장착 아이템 창을 화면 좌측 끝에 세로로 쌓는다.
   *
   * 아래쪽에는 상시 HUD인 CharacterStatsPanel(좌하단)이 있으므로 그 위까지만 쓴다.
   * 좌표를 상수로 적지 않고 각 창의 실제 크기에서 계산한다 — 창 크기를 바꿔도
   * 배치가 따라온다.
   */
  private createLeftPanels(): void {
    const margin = 10;
    const gap = 8;
    const topY = 50; // 상단 조작법 힌트 줄 아래

    this.characterDetailPanel = new CharacterDetailPanel(this, 0, 0);
    this.characterDetailPanel.setPosition(
      margin + this.characterDetailPanel.getPanelWidth() / 2,
      topY + this.characterDetailPanel.getPanelHeight() / 2
    );

    this.equipmentPanel = new EquipmentPanel(this, 0, 0);
    this.equipmentPanel.setPosition(
      margin + this.equipmentPanel.getPanelWidth() / 2,
      topY + this.characterDetailPanel.getPanelHeight() + gap + this.equipmentPanel.getPanelHeight() / 2
    );
  }

  /**
   * 캐릭터 상태 패널 생성 (좌하단)
   */
  private createCharacterStatsPanel(width: number, height: number): void {
    // 위치/레벨/카르마를 보여주는 상시 HUD이므로 기본적으로 화면에 표시된다.
    // (C 키/메뉴는 이 패널을 숨기고 다시 보이게 토글하는 용도)
    this.characterStatsPanel = new CharacterStatsPanel(this, 110, height - 60);
  }

  /**
   * 단축키 바 생성 (우하단)
   * F5~F12 슬롯을 2행 × 4열로 배치 (F5~F8 상단, F9~F12 하단)
   */
  private createQuickSlotBar(width: number, height: number): void {
    // 슬롯 4열 × 40px + 3gap × 4px + 패널 padding 16px = 188 → 컨테이너 우측에서 110px 떨어진 위치
    this.quickSlotBar = new QuickSlotBar(this, width - 110, height - 75);
    // 단축키 슬롯 클릭 콜백
    this.quickSlotBar.onSlotClick((slotIndex, item) => {
      if (!item) return;
      // 등록된 objId가 인벤토리에 없다 — 그대로 보내면 서버가 대상을 못 찾고
      // 조용히 끝나므로 무엇이 없는지 알려주고 멈춘다.
      if (item.missing) {
        this.chatBox.addSystemMessage(
          `F${slotIndex + 5}에 등록된 아이템이 인벤토리에 없습니다: ${item.label || item.id}`
        );
        return;
      }
      this.chatBox.addSystemMessage(`슬롯 F${slotIndex + 5} 사용: ${item.label || item.id}`);
      this.useItem(item.id);
    });

    // 드래그로 옮기거나 해제한 결과를 캐릭터별로 남긴다.
    this.quickSlotBar.onChange(() => this.saveQuickSlots());

    // 저장돼 있던 등록을 되살린다. 이 시점에는 인벤토리가 아직 없을 수 있으므로
    // 일단 missing 상태로 올려 두고, 목록이 도착하면 syncQuickSlots()가 채운다.
    this.restoreQuickSlots();
    // F5~F12 단축키 등록 — addCapture로 브라우저 기본 동작(F5 새로고침 등)을 차단한다.
    this.input.keyboard!.addCapture([
      Phaser.Input.Keyboard.KeyCodes.F5,
      Phaser.Input.Keyboard.KeyCodes.F6,
      Phaser.Input.Keyboard.KeyCodes.F7,
      Phaser.Input.Keyboard.KeyCodes.F8,
      Phaser.Input.Keyboard.KeyCodes.F9,
      Phaser.Input.Keyboard.KeyCodes.F10,
      Phaser.Input.Keyboard.KeyCodes.F11,
      Phaser.Input.Keyboard.KeyCodes.F12,
    ]);
    for (let i = 0; i < 8; i++) {
      const fKey = `F${i + 5}`;
      this.input.keyboard!.on(`keydown-${fKey}` as any, () => {
        this.quickSlotBar.triggerSlot(i);
      });
    }
  }

  /**
   * 메뉴 버튼 생성 (우상단)
   */
  private createMenuButton(width: number, height: number): void {
    this.menuButton = new MenuButton(this, width - 28, 28);
    this.menuButton.onClick = () => this.toggleMenu();
  }

  /**
   * ESC 메뉴 패널 생성
   */
  private createMenuPanel(width: number, height: number): void {
    this.menuPanel = this.add.container(width / 2, height / 2);
    this.menuPanel.setScrollFactor(0);
    this.menuPanel.setDepth(200);

    // 메뉴 항목.
    // 마지막 항목은 **계정 로그아웃이 아니라 캐릭터 선택 화면 복귀**다. 'Logout'이라는
    // 옛 문구가 그 동작을 잘못 알려 주고 있었고 폭도 가장 넓었으므로 한국어로 고쳤다
    // (동작 자체는 returnToCharSelect() 그대로). 나머지 UI도 한국어라 표기를 맞춘다.
    const menuItems = [
      { text: '[I] 인벤토리', action: () => this.toggleInventory() },
      { text: '[M] 지도', action: () => this.miniMap.toggle() },
      { text: '[C] 캐릭터 정보', action: () => this.showCharacterInfo() },
      { text: '[E] 장비', action: () => this.toggleEquipmentPanel() },
      { text: '[ESC] 메뉴 닫기', action: () => this.hideMenu() },
      { text: '캐릭터 선택으로 돌아가기', action: () => this.returnToCharSelect() },
    ];

    const titleStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '20px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold'
    };
    const itemStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '16px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff'
    };

    // 패널 크기는 **실제로 잰 글자 폭/높이에서 역산**한다. 폭 300 / 높이 280을
    // 고정해 두었더니 항목이 여섯 개가 되면서 마지막 항목('Character Select')이
    // 아래 테두리를 밟았다. 항목이 늘거나 문구가 길어져도 다시 새지 않도록,
    // 글자를 먼저 만들어 width/height를 재고 그 다음에 배경을 만든다.
    const title = this.add.text(0, 0, '=== 메뉴 ===', titleStyle);
    title.setOrigin(0.5);
    const itemTexts = menuItems.map((item) => {
      const t = this.add.text(0, 0, item.text, itemStyle);
      t.setOrigin(0.5);
      return t;
    });

    const padX = 30;          // 좌우 여백
    const padY = 22;          // 위아래 여백
    const titleGap = 26;      // 타이틀 아래 간격
    const itemStep = 40;      // 항목 간 세로 간격 (기존 배치 유지)
    const itemHeight = Math.max(...itemTexts.map((t) => t.height));

    const contentWidth = Math.max(title.width, ...itemTexts.map((t) => t.width));
    const panelWidth = Math.max(300, Math.ceil(contentWidth) + padX * 2);
    const panelHeight = Math.ceil(
      padY * 2 + title.height + titleGap + itemStep * (menuItems.length - 1) + itemHeight
    );

    // 배경 — 잰 크기로 만들고, 글자보다 먼저 컨테이너에 넣어 뒤에 깔리게 한다.
    const bg = this.add.rectangle(0, 0, panelWidth, panelHeight, 0x1a1a2e, 0.95);
    bg.setStrokeStyle(2, 0x4a4a6a);
    this.menuPanel.add(bg);

    const top = -panelHeight / 2;
    title.setY(top + padY + title.height / 2);
    this.menuPanel.add(title);

    const firstItemY = top + padY + title.height + titleGap + itemHeight / 2;
    itemTexts.forEach((menuText, index) => {
      menuText.setY(firstItemY + index * itemStep);
      // 컨테이너(menuPanel)의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
      // 그렇지 않으면 카메라가 플레이어를 따라 스크롤된 상태에서 클릭 히트테스트가
      // 화면 좌표가 아닌 스크롤이 반영된 월드 좌표로 계산되어 거의 항상 빗나간다
      // (캐릭터 선택 등 메뉴 항목이 클릭에 반응하지 않던 원인).
      menuText.setInteractive({ useHandCursor: true });
      menuText.setScrollFactor(0);
      menuText.on('pointerover', () => menuText.setStyle({ color: '#ffd700' }));
      menuText.on('pointerout', () => menuText.setStyle({ color: '#ffffff' }));
      menuText.on('pointerdown', menuItems[index].action);
      this.menuPanel.add(menuText);
    });
  }

  /**
   * 조작법 힌트 생성
   */
  private createControlHints(width: number, height: number): void {
    // 좌하단은 CharacterStatsPanel(중심 110, height-60)이 차지하므로 좌상단에 둔다.
    const hints = this.add.text(10, 10, 'Arrows: Move | Click: Attack | Z: Pick up | Enter: Chat | ESC: Menu', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#666666'
    });
    hints.setDepth(90);
    hints.setScrollFactor(0);
    // UI 카메라에 배정하기 위해 참조를 보관한다 (registerUiObjects 참고).
    this.controlHints = hints;
  }

  /**
   * 입력 설정
   */
  private setupInput(): void {
    // 키보드 입력 (이동은 방향키만 사용 — WASD는 채팅 입력과 겹쳐서 제외)
    this.keys = {
      up: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.UP),
      down: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN),
      left: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
      right: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT),
    };

    // 마우스 클릭 (공격/이동) — 열려 있는 UI 위 클릭이 그 아래 월드로 새어
    // 엉뚱한 공격/이동을 일으키지 않도록 막는다.
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // 창의 닫기 버튼이 방금 이 클릭을 소비했는지 먼저 확인하고 지운다.
      // (플래그가 남으면 다음 월드 클릭이 통째로 먹히므로 반드시 읽는 즉시 지운다)
      const consumedByPanel = this.worldClickConsumed;
      this.worldClickConsumed = false;

      if (!pointer.leftButtonDown()) return;
      // 창을 닫은 클릭은 여기서 끝이다 — 월드로 새어 나가면 캐릭터가 걸어간다.
      if (consumedByPanel) return;
      // 메뉴는 모달, 채팅 입력 중에는 조작이 아니라 타이핑이다.
      if (this.isMenuVisible || this.chatBox.isInputActive()) return;

      // 진입 직후에는 월드 클릭을 받지 않는다 (WORLD_CLICK_GRACE_MS).
      if (!this.worldClickEnabled) {
        this.noticeWorldNotReady();
        return;
      }

      // 하단 UI 영역(채팅창 상단 아래 전부)은 월드가 아니다.
      if (pointer.y >= this.worldClickBottomLimit) return;

      // 열려 있는 창(좌측 상세 정보/장비, 우측 인벤토리) 위 클릭은 월드가 아니다.
      if (this.isPointerOverOpenPanels(pointer)) return;

      // 대상 선택 모드(월드 엔티티 / 지면)에서는 클릭이 공격·이동이 아니라 대상 지정이다.
      if (this.pendingUse && (this.pendingUse.kind === 'entity' || this.pendingUse.kind === 'ground')) {
        this.handleWorldTargetPick(pointer);
        return;
      }

      this.handleAttack(pointer);
    });

    // ESC 키 — 채팅 입력 중이면 채팅 닫기(ChatBox 처리)이지 메뉴 토글이 아니다.
    // 대화창이 열려 있으면 그것부터 닫는다 (모달을 남긴 채 메뉴가 겹치지 않게).
    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.chatBox.isInputActive()) return;
      // 대상 선택 중이면 ESC는 메뉴 토글이 아니라 취소다.
      // (문자열 입력창이 떠 있을 때는 입력창 자신이 ESC를 먼저 처리하고 전파를 끊는다.)
      if (this.pendingUse) {
        // 북마크 창은 대상 선택 상태의 일부다 — clearTargetSelection()이 함께 닫는다.
        this.cancelTargetSelection();
        return;
      }
      // 열린 창 닫기. 위에 뜨는 창부터 접는다 (개수 입력창이 가장 앞이다).
      if (this.quantityDialog.isOpen()) {
        this.quantityDialog.hide();
        return;
      }
      if (this.bookmarkDialog.isOpen()) {
        this.bookmarkDialog.hide();
        return;
      }
      if (this.identifyDialog.isOpen()) {
        this.identifyDialog.hide();
        return;
      }
      if (this.htmlDialog.isOpen()) {
        this.htmlDialog.hide();
        return;
      }
      this.toggleMenu();
    });

    // 인벤토리 토글 (I) — 채팅 입력 중에는 타이핑으로 취급하고 무시
    this.input.keyboard!.on('keydown-I', () => {
      if (this.chatBox.isInputActive()) return;
      this.toggleInventory();
    });

    // 장착 아이템 창 토글 (E) — 채팅 입력 중에는 타이핑으로 취급하고 무시
    this.input.keyboard!.on('keydown-E', () => {
      if (this.chatBox.isInputActive()) return;
      this.toggleEquipmentPanel();
    });

    // 미니맵 토글 (M) — 채팅 입력 중에는 타이핑으로 취급하고 무시
    this.input.keyboard!.on('keydown-M', () => {
      if (this.chatBox.isInputActive()) return;
      this.miniMap.toggle();
    });

    // 캐릭터 상세 정보 토글 (C) — 채팅 입력 중에는 타이핑으로 취급하고 무시
    this.input.keyboard!.on('keydown-C', () => {
      if (this.chatBox.isInputActive()) return;
      this.showCharacterInfo();
    });

    // 줍기 (Z) — 발밑 아이템은 캐릭터에 가려 클릭하기 어렵다.
    // 채팅 입력 중에는 타이핑으로 취급하고 무시한다.
    this.input.keyboard!.on('keydown-Z', () => {
      if (this.chatBox.isInputActive()) return;
      this.pickUpNearest();
    });
  }

  /**
   * 네트워크 이벤트 리스닝 설정
   */
  private setupNetworkListeners(): void {
    // 캐릭터 스폰
    this.onNetwork('char_spawn', (data: any) => {
      this.handleCharSpawn(data);
    });

    // 엔티티 이동
    this.onNetwork('move_object', (data: any) => {
      this.handleObjectMove(data);
    });

    // 엔티티 제거 (게이트웨이 메시지 타입: object_remove)
    this.onNetwork('object_remove', (data: any) => {
      this.handleObjectRemove(data);
    });

    // 바닥에 놓인 아이템 (S_DropItem, opcode 3) — 내가 버린 것과 남이 버린 것,
    // 몹이 떨어뜨린 것이 모두 같은 경로로 온다.
    this.onNetwork('drop_item', (data: any) => {
      this.handleGroundItemDrop(data);
    });

    // 채팅 메시지 (클라이언트가 보낸 채팅의 로컬 에코 등)
    this.onNetwork('chat_message', (data: any) => {
      this.handleChatMessage(data);
    });

    // 서버가 브로드캐스트한 채팅 (주변 PC의 일반/외침/파티채팅, 귓속말 수신)
    this.onNetwork('server_chat', (data: any) => {
      this.handleServerChat(data);
    });

    // 맵 변경 — 클릭 이동 벽 충돌 판정 + 타일 배경 렌더링에 쓸 맵 통행 데이터를
    // 미리 로드해둔다. 데이터가 도착하면 회색 "로딩중" 타일을 실제 벽/통로로 갱신.
    // 서버 시스템 메시지 — GM 명령 응답(.gm 등), 공지, 경고가 모두 이 경로로 온다.
    // 구독하지 않으면 명령을 쳐도 결과가 화면에 남지 않는다.

    // S_SystemMessage: 본문 문자열이 그대로 온다.
    this.onNetwork('sys_message', (data: { message?: string } | string) => {
      const raw = typeof data === 'string' ? data : (data?.message ?? '');
      // 서버 문자열 앞에 제어문자가 남아 있으면 떼고 보여준다.
      const text = raw.replace(/^[\t\r\n]+/, '').trim();
      if (text) this.chatBox.addSystemMessage(text);
    });

    // S_ServerMessage: 번호 + 인자만 온다. 문장은 클라이언트가 조립한다
    // (원본 클라이언트 리소스에 있던 문자열 표 — src/data/serverMessages.ts).
    this.onNetwork('server_message', (data: { messageId?: number; args?: string[] } | string) => {
      if (typeof data === 'string') {
        // 옛 게이트웨이(문자열 전달) 호환.
        const text = data.replace(/^[\t\r\n]+/, '').trim();
        if (text) this.chatBox.addSystemMessage(text);
        return;
      }
      if (typeof data?.messageId !== 'number') return;
      const text = formatServerMessage(data.messageId, data.args ?? []);
      if (text) this.chatBox.addSystemMessage(text);
    });

    this.onNetwork('map_id', (data: { mapId: number; underwater?: boolean }) => {
      const changed = this.currentMapId !== data.mapId;
      this.currentMapId = data.mapId;
      // 맵이 바뀌면 옛 맵 기준으로 만든 경로는 전부 무의미하다 (좌표 공간이 다르다).
      if (changed) {
        this.clearMoveTarget();
        this.clearChase();
        // 바닥 아이템도 옛 맵의 좌표에 묶여 있다 — 남겨 두면 새 맵의 엉뚱한
        // 자리에 떠 있게 된다. 새 맵의 것은 서버가 다시 보내 준다.
        this.clearGroundItems();
      }
      this.miniMap.setMap(data.mapId);
      mapCollision.setCurrentMap(data.mapId, () => this.updateTileGrid(true));
      // 텔레포트 중이라도 여기서 페이드인하지 않는다. 서버는 S_MapID를 먼저,
      // 새 좌표(S_OwnCharPack)를 그 다음에 보내므로(Teleportation.java) 여기서
      // 화면을 되살리면 옛 좌표가 한 프레임 보이고 카메라가 새 위치까지
      // 길게 미끄러진다. 마무리는 좌표가 확정되는 snapPlayerToTile()에서 한다.
    });

    // S_Teleport (opcode 4) — 서버가 클라이언트의 응답을 기다리는 중이다.
    // 응답(teleport_ack)을 보내지 않으면 이동이 완결되지 않는다.
    this.onNetwork('teleport', (data: { objId: number }) => {
      this.handleTeleport(data);
    });

    // S_NpcTalkReturn (opcode 119) — NPC 대화 / 퀘스트 안내.
    this.onNetwork('show_html', (data: { objId: number; htmlId: string; args?: string[] }) => {
      this.handleShowHtml(data);
    });

    // S_IdentifyDesc (opcode 43) — 확인(감정) 주문서 결과.
    // 북마크 목록(bookmarks)은 GameState가 받아 보관하므로 여기서 구독하지 않는다
    // (같은 목록을 두 곳에서 들고 있으면 어느 쪽이 최신인지 알 수 없게 된다).
    this.onNetwork('identify_desc', (data: IdentifyDescPayload) => {
      this.handleIdentifyDesc(data);
    });

    // S_SkillSound (opcode 232) — 대상 위치에 스킬 이펙트.
    this.onNetwork('skill_sound', (data: { objId: number; gfxId: number }) => {
      this.playSkillEffect(data.objId, data.gfxId);
    });

    // 인벤토리
    this.onNetwork('inventory', (data: any) => {
      this.handleInventory(data);
    });

    // 연결 상태.
    //
    // 게임 중에 'connected'가 오면 WebSocket이 **재연결**된 것이다. 게이트웨이는
    // 연결마다 새 세션을 만들고(GatewayWebSocketHandler.onConnect) 그 세션에는
    // L1J 서버 연결이 없다 — 로그인 처리에서만 붙기 때문이다. 그래서 화면은
    // 멀쩡해 보여도 이동·채팅·명령이 서버에 전혀 닿지 않는다.
    // 그대로 두면 사용자가 먹통 상태를 눈치채지 못하므로 알리고 로그인 화면으로 돌린다.
    this.onNetwork('connected', () => {
      this.handleSessionLost('서버 연결이 끊어졌다가 다시 연결되었습니다.');
    });

    this.onNetwork('disconnected', () => {
      this.chatBox.addSystemMessage('서버 연결이 끊어졌습니다. 다시 연결하는 중...');
    });

    // 재연결 시도를 모두 소진한 경우.
    this.onNetwork('reconnect_failed', () => {
      this.handleSessionLost('서버에 다시 연결하지 못했습니다.');
    });

    // 다른 곳에서 같은 계정으로 로그인해 이 세션이 강제 종료된 경우.
    // 자격증명은 유지하되, 로그인 화면에서 곧바로 자동 재로그인이 일어나
    // 방금 접속한 세션을 다시 끊어버리는 핑퐁을 막기 위해 억제 플래그를 남긴다.
    this.onNetwork('force_logout', (data: { message?: string }) => {
      const message = data?.message || '다른 곳에서 같은 계정으로 로그인하여 연결이 종료되었습니다.';
      this.chatBox.addSystemMessage(message);
      storageManager.suppressNextAutoLogin();
      this.time.delayedCall(1500, () => {
        this.connectionManager?.disconnect();
        gameState.reset();
        this.scene.start('LoginScene');
      });
    });
  }

  /**
   * 게임 상태 이벤트 리스닝 설정
   */
  private setupGameStateListeners(): void {
    // 플레이어 스폰 — 스폰은 무조건 확정 위치이므로 즉시 스냅한다.
    // 미니맵도 함께 갱신해야 한다. 이동(player:position) 경로는 handleObjectMove가
    // 미니맵을 갱신하지만 스폰 경로에는 그런 곳이 없어, 여기서 빠뜨리면 접속 직후
    // 미니맵만 (0, 0)에 머무른다.
    gameEvents.on('player:set', (data: { objId: number; x: number; y: number; gfxId?: number | null }) => {
      this.snapPlayerToTile(data.x, data.y);
      this.miniMap.setPlayerPosition(this.playerTileX, this.playerTileY, this.currentHeading);

      // 자기 캐릭터의 외형(클래스/성별)은 이 경로로만 들어온다.
      // 게이트웨이가 char_spawn을 보내지 않으므로 handleCharSpawn은 실행되지 않는다.
      if (typeof data.gfxId === 'number') {
        this.playerCharacterId = inferCharacter(data.gfxId);
        this.playerWeapon = this.resolvePlayerWeapon();
        this.applyPlayerTexture();
      }
    });

    // 플레이어 이동 — 우리가 방금 보낸 이동의 에코일 수 있으므로 걸러서 반영한다.
    gameEvents.on('player:position', (data: { x: number; y: number; heading: number }) => {
      this.syncPlayerTileFromServer(data.x, data.y);
    });

    // 스탯 업데이트 (hp_update/mp_update/exp_update 등은 GameState가 수신해
    // 'player:stats'로 흘려보낸다 — GameScene은 이 경로 하나로만 받는다)
    gameEvents.on('player:stats', (data: { hp: number; mp: number; maxHp: number; maxMp: number; level?: number; exp?: number; maxExp?: number; lawful?: number }) => {
      this.statusBar.setStatus({
        hp: data.hp,
        maxHp: data.maxHp,
        mp: data.mp,
        maxMp: data.maxMp,
        level: data.level,
        exp: data.exp,
        maxExp: data.maxExp,
        lawful: data.lawful
      });

      // 좌하단 캐릭터 상태 패널도 갱신.
      // **payload에 실제로 들어 있는 필드만 넘긴다.** `?? 0` / `?? 1`로 빈 자리를
      // 채우면 HP가 변할 때마다(GameState.updateStats의 payload에는 exp/lawful이
      // 없다) 성향과 경험치가 0으로 덮어써진다. exp/lawful의 진짜 출처는
      // own_char_status(player:detail)와 lawful 패킷이고 패널이 직접 구독한다.
      const panelStats: Partial<CharacterStatsData> = {};
      if (data.level !== undefined) panelStats.level = data.level;
      if (data.exp !== undefined) panelStats.exp = data.exp;
      if (data.maxExp !== undefined) panelStats.maxExp = data.maxExp;
      if (data.lawful !== undefined) panelStats.lawful = data.lawful;
      if (Object.keys(panelStats).length > 0) {
        this.characterStatsPanel?.setStats(panelStats);
      }
    });

    // 인벤토리 변경 — 전체 수신(inventory), 단건 추가/삭제(add_item/delete_item),
    // 이름 변경(item_name)이 모두 GameState를 거쳐 여기로 모인다.
    // 화면 갱신 경로를 하나로 두어야 장착/해제가 어느 경로로 오든 반영된다.
    gameEvents.on('inventory:update', (data: { items: any[] }) => {
      const rawItems = data?.items ?? [];
      this.applyInventory(this.toInventoryItems(rawItems));
      // 무기 판별은 **가공 전** 이름으로 해야 한다. toInventoryItems가 장착
      // 접미사($9)를 떼고 이름도 한국어로 바꿔 버려서 그 뒤로는 알 수 없다.
      this.applyEquippedWeapon(rawItems);
    });

    // 순간이동 북마크 목록 (S_BookmarkLoad). 목록의 주인은 GameState이고 창은
    // 열릴 때 그 스냅샷을 받아 그린다. 다만 창이 이미 열려 있는 동안 목록이
    // 갱신되면(북마크 추가/삭제) 화면만 옛 목록으로 남으므로 다시 그린다.
    gameEvents.on('bookmarks:update', () => {
      if (this.bookmarkDialog?.isOpen()) this.openBookmarkDialog();
    });

    // 상세 스탯 — 좌측 상세 정보 창 갱신. 창이 닫혀 있어도 값은 최신으로 유지한다.
    gameEvents.on('player:detail', (stats: PlayerDetailStats) => {
      this.characterDetailPanel?.setStats(stats);
    });

    // 사망 — 진행 중이던 클릭 이동 목적지를 버리고 알린다.
    // (실제 이동 차단은 handleMovementInput / handleTileClick에서 gameState.isDead()로 한다)
    gameEvents.on('player:death', () => {
      this.clearMoveTarget();
      this.clearChase();
      this.chatBox.addSystemMessage('사망했습니다. 이동할 수 없습니다.');
      // 사망 애니메이션 시작 (5프레임 × 200ms = 1초 후 묘비로 고정).
      this.startPlayerDeathAnimation();
      this.openMenuOnDeath();
    });

    gameEvents.on('player:revive', () => {
      this.chatBox.addSystemMessage('부활했습니다.');
      // 부활 시 사망 애니메이션 상태 해제 (다음 프레임부터 idle로 복귀).
      this.clearPlayerDeathAnimation();
      this.closeMenuOnRevive();
    });

    // 엔티티 스폰
    gameEvents.on('entity:spawn', (data: { type: string; entity: any }) => {
      if (data.type === 'npc' || data.type === 'monster' || data.type === 'player') {
        this.spawnEntitySprite(data.entity, data.type);
      }
    });

    // 엔티티 제거
    gameEvents.on('entity:remove', (data: { objId: number }) => {
      this.removeEntitySprite(data.objId);
    });

    // 엔티티 이동
    gameEvents.on('entity:move', (data: { objId: number; x: number; y: number; heading: number; type: string }) => {
      this.updateEntitySprite(data.objId, data.x, data.y, data.heading);
    });

    // 엔티티 사망 (gameState.setDead(objId, true) 호출 시 발행됨).
    // 자기 플레이어는 위에서 별도 처리되므로, 여기서는 자기 외 엔티티의
    // 사망 애니메이션만 시작한다.
    gameEvents.on('entity:death', (data: { objId: number }) => {
      if (data.objId === gameState.getPlayerObjId()) return;
      this.startEntityDeathAnimation(data.objId);
    });

    gameEvents.on('entity:revive', (data: { objId: number }) => {
      if (data.objId === gameState.getPlayerObjId()) return;
      const entity = this.entitySprites.get(data.objId);
      if (!entity || !entity.deathAnim) return;
      entity.deathAnim = undefined;
      entity.frame = 0;
      // 몹 사망 연출(기울이기 + 흐리게)을 되돌린다. 캐릭터 경로에서는 둘 다 기본값이라 무해하다.
      entity.sprite.setAngle(0);
      entity.sprite.setAlpha(1);
      // 죽어 있는 동안 텍스처가 바뀌었을 수 있으므로 키를 비워 강제로 다시 붙인다.
      entity.currentTextureKey = null;
      this.applyEntityFrame(entity, 0);
    });

    // 장착 무기 변경 (S_CharVisualUpdate). 자기 캐릭터든 남이든 그림을 다시 고른다.
    gameEvents.on('entity:visual', (data: { objId: number; weapon: number; isPlayer: boolean }) => {
      this.applyVisualWeapon(data.objId, data.weapon, data.isPlayer);
    });

    // 성향치 변경 (S_Lawful). 자기 캐릭터일 때만 화면에 반영한다 —
    // 남의 성향은 지금 표시할 자리가 없다 (이름표 색은 서버가 알려주는 값이 따로 있다).
    gameEvents.on('entity:lawful', (data: { objId: number; lawful: number; isPlayer: boolean }) => {
      if (!data.isPlayer) return;
      // StatusBarData는 HP/MP를 필수로 받는다 — 성향만 바꾸려 해도 현재 값을
      // 함께 넘겨야 바가 0으로 초기화되지 않는다.
      const stats = gameState.getPlayerStats();
      this.statusBar.setStatus({
        hp: stats.hp,
        maxHp: stats.maxHp,
        mp: stats.mp,
        maxMp: stats.maxMp,
        level: stats.level,
        lawful: data.lawful,
      });
      this.characterStatsPanel?.setStats({ lawful: data.lawful });
    });

    // 변신 (S_ChangeShape). polyId는 몹의 gfxId이고 0이면 해제다.
    gameEvents.on('entity:poly', (data: { objId: number; polyId: number; weaponTakeoff: boolean; isPlayer: boolean }) => {
      this.applyEntityPoly(data.objId, data.polyId, data.isPlayer);
    });

    // 투명 (S_Invis).
    gameEvents.on('entity:invis', (data: { objId: number; invisType: number; invisible: boolean; isPlayer: boolean }) => {
      this.applyEntityInvis(data.objId, data.invisible, data.isPlayer);
    });

    // 광원 반경 (S_Light). 자기 캐릭터 것만 쓴다 — 남의 광원은 내 화면 밝기와
    // 무관하다. 반경 밖 바닥을 흐리게 그리는 것 외의 조명 효과는 없다
    // (updateTileGrid 참고).
    gameEvents.on('entity:light', (data: { objId: number; lightSize: number; isPlayer: boolean }) => {
      if (!data.isPlayer) return;
      if (this.playerLightTiles === data.lightSize) return;
      this.playerLightTiles = data.lightSize;
      // 캐시(카메라 중심 타일 + 플레이어 타일)가 그대로여도 밝기가 달라졌으므로
      // 강제로 다시 그린다.
      this.updateTileGrid(true);
    });

    // 제자리 방향 전환 (S_ChangeHeading). 좌표가 없는 패킷이라 위치는 그대로 두고
    // 그림만 돌린다.
    gameEvents.on('entity:heading', (data: { objId: number; heading: number; isPlayer: boolean }) => {
      this.applyEntityHeading(data.objId, data.heading, data.isPlayer);
    });

    // 외형 이상상태 색 (S_Poison). 서버는 색만 주고 무슨 상태인지는 알려주지 않는다.
    gameEvents.on('entity:poison', (data: { objId: number; poisonType: number; isPlayer: boolean }) => {
      this.applyEntityPoison(data.objId, data.poisonType, data.isPlayer);
    });

    // 칭호 (S_CharTitle). 이름표에 함께 그린다.
    gameEvents.on('entity:title', (data: { objId: number; title: string; isPlayer: boolean }) => {
      this.applyEntityTitle(data.objId, data.title, data.isPlayer);
    });

    // 전투 결과 (S_AttackPacket / S_AttackMissPacket).
    gameEvents.on('combat:attack', (data: AttackResultPayload & {
      attackerIsPlayer: boolean;
      targetIsPlayer: boolean;
    }) => {
      this.showAttackFeedback(data);
    });

    // 문 열림/닫힘 (S_Door). 통행 데이터를 고치고 벽 표시를 다시 그린다.
    gameEvents.on('map:door', (data: { x: number; y: number; direction: number; passable: boolean }) => {
      if (this.currentMapId === null) return;
      mapCollision.setDoorPassable(this.currentMapId, data.x, data.y, data.passable);
      // 문이 있던 타일의 벽 표시가 바뀌므로 캐시를 무시하고 다시 그린다.
      this.updateTileGrid(true);
    });

    // 로그인 공지 (S_CommonNews).
    gameEvents.on('server:news', (data: { text: string }) => {
      for (const line of data.text.split('\n')) {
        const trimmed = line.trim();
        if (trimmed) this.chatBox.addSystemMessage(trimmed);
      }
    });

    // 능력치 상승 (S_Strup / S_Dexup). 스탯 반영은 GameState가 이미 했다.
    gameEvents.on('player:statup', (data: { stat: string; value: number }) => {
      const label = STAT_UP_LABELS[data.stat] ?? data.stat;
      this.chatBox.addSystemMessage(`${label}이(가) ${data.value}(으)로 올랐습니다.`);
    });

    // 날씨 (S_Weather). 연출용 리소스가 없어 알림만 남긴다.
    gameEvents.on('world:weather', (data: { code: number }) => {
      const text = WEATHER_LABELS[data.code];
      if (text) this.chatBox.addSystemMessage(text);
    });

    // 상태이상 (S_Paralysis). 이동 차단 자체는 gameState.canMove()가 담당하므로
    // 여기서는 진행 중이던 클릭 이동을 버리고 사용자에게 알리기만 한다.
    gameEvents.on('player:paralysis', (data: { kind: number; paralyzed: boolean; teleportUnlock: boolean }) => {
      if (data.teleportUnlock) {
        // 서버가 텔레포트 대기 상태를 풀었다 — 잠금 해제는 GameState가 이미 했다.
        this.finishTeleport();
        return;
      }
      if (data.paralyzed) {
        this.clearMoveTarget();
        this.chatBox.addSystemMessage('몸이 굳어 움직일 수 없습니다.');
      } else {
        this.chatBox.addSystemMessage('다시 움직일 수 있습니다.');
      }
    });

    // 텔레포트 잠금이 풀리면(우리 처리, GameState 워치독, 서버의 대기 해제 중
    // 무엇이 먼저든) 화면을 되돌린다.
    gameEvents.on('player:teleport', (data: { teleporting: boolean }) => {
      if (!data.teleporting) this.finishTeleport();
    });
  }

  // ==================== 텔레포트 (S_Teleport / C_Teleport) ====================

  /**
   * S_Teleport 수신 — 화면을 덮고 이동을 잠근 뒤 서버에 응답한다.
   *
   * 서버는 이 응답(C_Teleport)을 받아야 `Teleportation.Teleportation(pc)`을
   * 실행한다. 응답하지 않으면 귀환/텔레포트 주문서가 소모만 되고 이동이
   * 완결되지 않는다 (실측으로 확인된 증상).
   */
  private handleTeleport(_data: { objId: number }): void {
    // GameState도 같은 이벤트로 잠금을 걸지만, 리스너 등록 순서에 기대지 않는다.
    // 이미 잠겨 있으면 값 변화가 없어 아무 일도 일어나지 않는다(워치독만 갱신).
    gameState.setTeleporting(true);

    this.teleportPending = true;

    // 진행 중이던 이동은 전부 버린다 — 서버는 곧 좌표를 통째로 갈아치운다.
    this.clearMoveTarget();
    this.isStepping = false;
    this.stepElapsed = 0;
    this.playerIsWalking = false;

    // 월드 카메라만 어둡게 한다 (UI 카메라는 별도라 채팅/상태바는 계속 보인다).
    this.cameras.main.fadeOut(TELEPORT_FADE_MS, 0, 0, 0);

    this.connectionManager?.getPacketHandler().sendTeleportAck();

    this.clearTeleportTimeout();
    this.teleportTimeout = this.time.delayedCall(TELEPORT_TIMEOUT_MS, () => {
      this.teleportTimeout = null;
      this.chatBox.addSystemMessage('텔레포트 응답이 없어 화면을 되돌립니다.');
      this.finishTeleport();
    });
  }

  /**
   * 새 위치가 도착했으므로 화면을 되살리고 잠금을 푼다.
   * 여러 신호(map_id / 스폰 / 상태이상 해제 / 워치독)가 겹쳐 들어오므로
   * 몇 번 불려도 안전하도록 첫 호출에서만 실제 작업을 한다.
   */
  private finishTeleport(): void {
    if (!this.teleportPending) return;
    this.teleportPending = false;

    this.clearTeleportTimeout();
    // setTeleporting(false)는 다시 'player:teleport'를 내지만, 위에서 이미
    // teleportPending을 내렸으므로 재귀는 한 번에 멈춘다.
    gameState.setTeleporting(false);
    this.cameras.main.fadeIn(TELEPORT_FADE_MS, 0, 0, 0);
  }

  private clearTeleportTimeout(): void {
    if (!this.teleportTimeout) return;
    this.teleportTimeout.remove(false);
    this.teleportTimeout = null;
  }

  // ==================== NPC 대화창 (S_NpcTalkReturn) ====================

  /**
   * show_html 수신 — 대화창을 연다.
   *
   * 서버는 문장을 보내지 않고 HTML 템플릿 이름과 인자만 보낸다. 본문 리소스가
   * 우리에게 없으므로 받은 그대로 보여주되, 대화 상대의 이름은 이미 알고 있는
   * 스폰 정보에서 찾아 제목에 채운다.
   */
  private handleShowHtml(data: { objId: number; htmlId: string; args?: string[] }): void {
    const objId = data?.objId ?? 0;

    // 응답이 왔으므로 침묵 감시를 푼다 (대상이 달라도 푼다 — 서버가 다른
    // objId로 창을 여는 경우가 있고, 그때 뒤늦게 "대답이 없다"고 찍히면 거짓말이 된다).
    this.clearNpcTalkWatch();

    // 창 제목에도 칭호(`"물약 상인^키리스"`의 `^` 뒷부분)는 빼고 이름만 쓴다 —
    // 화면의 이름표와 같은 이름이 보여야 누구와 말하는지 헷갈리지 않는다.
    const rawName =
      gameState.getNpc(objId)?.name
      ?? gameState.getMonster(objId)?.name
      ?? gameState.getPlayer(objId)?.charName
      ?? this.entitySprites.get(objId)?.nameText.text;
    const name = rawName ? stripNpcTitle(rawName) : rawName;

    this.htmlDialog.show({
      objId,
      htmlId: data?.htmlId ?? '',
      args: data?.args ?? [],
      title: name,
    });
    this.hideMenu();
  }

  // ==================== 스킬 이펙트 (S_SkillSound) ====================

  /**
   * 대상 위치에 원형 확산 이펙트를 띄운다.
   *
   * 원본 클라이언트의 스킬 그래픽(gfxId)에 해당하는 리소스가 없으므로 모양은
   * 하나로 통일하고, 색만 gfxId에서 뽑아 스킬마다 다르게 보이도록 한다.
   * 대상이 화면에 없으면(시야 밖에서 시전) 아무것도 그리지 않는다.
   */
  private playSkillEffect(objId: number, gfxId: number): void {
    const isPlayer = objId === gameState.getPlayerObjId();
    const entity = this.entitySprites.get(objId);
    if (!isPlayer && !entity) return;

    const tileX = isPlayer ? this.playerTileX : entity!.tileX;
    const tileY = isPlayer ? this.playerTileY : entity!.tileY;
    const { x, y } = tileToWorld(tileX, tileY);
    const centerY = y - PLAYER_BODY_HEIGHT;

    // gfxId를 색상환에 그대로 흩뿌린다 — 같은 스킬은 언제나 같은 색이 된다.
    const hue = ((gfxId % 360) + 360) % 360;
    const color = Phaser.Display.Color.HSVToRGB(hue / 360, 0.55, 1).color;

    const effect = this.add.graphics();
    effect.setDepth(this.depthFor(tileX, tileY) + LABEL_DEPTH_BONUS);
    effect.lineStyle(3, color, 1);
    effect.strokeCircle(0, 0, SKILL_EFFECT_RADIUS);
    effect.fillStyle(color, 0.22);
    effect.fillCircle(0, 0, SKILL_EFFECT_RADIUS);
    effect.setPosition(x, centerY);
    effect.setScale(0.2);
    this.registerWorldObjects(effect);

    this.tweens.add({
      targets: effect,
      scale: 1,
      alpha: 0,
      duration: SKILL_EFFECT_MS,
      ease: 'Quad.easeOut',
      onComplete: () => effect.destroy(),
    });
  }

  // ==================== 장착 무기 (S_CharVisualUpdate) ====================

  /**
   * 서버가 알려준 장착 무기 코드를 그림에 반영한다.
   *
   * 인벤토리 표시명에서 역산하는 applyEquippedWeapon()과 결과가 같아야 하지만,
   * 이쪽은 **남의 캐릭터에도 온다**는 점이 다르다. 몹은 무기 축이 없는 계열
   * 그림을 쓰므로 대상에서 제외한다.
   */
  private applyVisualWeapon(objId: number, weaponCode: number, isPlayer: boolean): void {
    const weapon = WEAPON_CODE_TO_TYPE[weaponCode] ?? 'sword';

    if (isPlayer) {
      if (this.playerWeapon === weapon) return;
      this.playerWeapon = weapon;
      this.applyPlayerTexture();
      return;
    }

    const entity = this.entitySprites.get(objId);
    if (!entity || entity.kind !== 'char') return;
    if (entity.weapon === weapon) return;

    entity.weapon = weapon;
    this.applyEntityFrame(entity, entity.frame);
  }

  // ==================== 변신 (S_ChangeShape) ====================

  /**
   * 변신 상태를 그림에 반영한다. polyId는 **변신할 몹의 gfxId**이고 0이면 해제다.
   *
   * 변신 그림은 새로 그리지 않고 몹 계열 SVG(mobSprites)를 그대로 빌려 쓴다 —
   * 12계열 × 8방향 × 5프레임이 이미 있고, 변신은 대부분 몹의 모습이다.
   * 계열이 같아 형체를 구분하기 어려운 문제는 몹과 같은 방식(이름 해시 tint)으로 던다.
   */
  private applyEntityPoly(objId: number, polyId: number, isPlayer: boolean): void {
    if (isPlayer) {
      this.applyPlayerPoly(polyId);
      return;
    }

    const entity = this.entitySprites.get(objId);
    if (!entity) return;

    if (polyId === 0) {
      // 해제 — 스폰 때의 그림 체계로 되돌린다.
      if (!entity.basePoly) return;
      entity.kind = entity.basePoly.kind;
      entity.mobFamily = entity.basePoly.mobFamily;
      entity.mobTintColor = entity.basePoly.mobTintColor;
      entity.mobImage = entity.basePoly.mobImage;
      entity.basePoly = undefined;
      // 캐릭터 그림에는 개체 색이 없다. 지우지 않으면 변신 색이 그대로 남는다.
      if (entity.kind === 'char') entity.sprite.clearTint();
      entity.currentTextureKey = null;
      this.applyEntityFrame(entity, entity.frame);
      return;
    }

    // 두 번 연속 변신해도 원래 모습은 처음 것만 기억한다.
    if (!entity.basePoly) {
      entity.basePoly = {
        kind: entity.kind,
        mobFamily: entity.mobFamily,
        mobTintColor: entity.mobTintColor,
        mobImage: entity.mobImage,
      };
    }

    const family = inferMobFamily(polyId, entity.nameText.text);
    entity.kind = 'mob';
    entity.mobFamily = family;
    entity.mobTintColor = mobTint(entity.nameText.text);
    // 변신한 모습에 실제 그림이 있으면 그것으로 (없으면 계열 SVG).
    entity.mobImage = mobImageFor(polyId) ?? undefined;
    entity.currentTextureKey = null;
    this.preloadMobFamilyOnce(family);
    this.applyEntityFrame(entity, entity.frame);
  }

  /**
   * 자기 캐릭터의 변신. 엔티티 쪽과 달리 스프라이트가 EntitySprite가 아니라
   * playerSprite 하나뿐이라, 계열만 기억해 두고 그림 고르는 곳
   * (applyPlayerTexture)에서 갈라 준다.
   */
  private applyPlayerPoly(polyId: number): void {
    if (polyId === 0) {
      if (!this.playerPolyFamily) return;
      this.playerPolyFamily = null;
      this.playerSprite.clearTint();
      this.playerCurrentTextureKey = null;
      this.applyPlayerTexture();
      this.chatBox.addSystemMessage('변신이 풀렸습니다.');
      return;
    }

    const family = inferMobFamily(polyId, this.playerNameText.text);
    if (this.playerPolyFamily === family) return;

    this.playerPolyFamily = family;
    this.playerPolyTint = mobTint(this.playerNameText.text);
    this.playerCurrentTextureKey = null;
    this.preloadMobFamilyOnce(family);
    this.applyPlayerTexture();
  }

  // ==================== 방향 전환 (S_ChangeHeading) ====================

  /**
   * 제자리 방향 전환을 그림에 반영한다. 위치는 건드리지 않는다 —
   * 서버가 좌표 없이 heading만 보내는 패킷이다.
   *
   * 방향별 그림을 고르는 경로는 이동 때와 같은 창구
   * (엔티티는 applyEntityFrame, 자기 캐릭터는 applyPlayerTexture)를 그대로 쓴다.
   * 그래서 변신 중이라면 applyPlayerTexture()가 몹 계열 그림으로 알아서 갈라 준다.
   */
  private applyEntityHeading(objId: number, heading: number, isPlayer: boolean): void {
    const direction = headingToDirection(heading);

    if (isPlayer) {
      // 걷는 중이면 무시한다 — 이동 보간이 매 걸음 방향을 정하고 있어서,
      // 여기서 덮어쓰면 다음 걸음에 곧바로 되돌아가며 튄다.
      if (this.playerIsWalking || this.isStepping) return;
      // 사망 연출 중에도 건드리지 않는다 (시체가 다시 일어서 보인다).
      if (this.playerDeathAnim) return;

      // 이후 이동·공격이 기준으로 삼는 값도 서버가 알려준 방향으로 맞춰 둔다.
      this.currentHeading = heading;
      if (direction === this.playerDirection) return;

      this.playerDirection = direction;
      this.applyPlayerTexture();
      return;
    }

    const entity = this.entitySprites.get(objId);
    if (!entity) return;
    if (entity.deathAnim) return;
    if (entity.direction === direction) return;

    entity.direction = direction;
    this.applyEntityFrame(entity, entity.frame);
  }

  // ==================== 투명 (S_Invis) ====================

  /**
   * 투명 상태를 그림에 반영한다. **완전히 숨기지 않고 반투명으로 남긴다.**
   *
   * 자기 캐릭터를 지우면 어디에 서 있는지 알 수 없어 조작 자체가 불가능하고,
   * 남의 캐릭터는 애초에 서버가 "보면 안 되는 대상"의 스폰을 보내지 않는다
   * (S_Invis가 왔다는 것은 이미 보이는 대상이 투명해졌다는 뜻이다). 그래서
   * 양쪽 다 "투명해졌다는 사실"만 알아보게 하는 편이 맞다.
   */
  private applyEntityInvis(objId: number, invisible: boolean, isPlayer: boolean): void {
    const alpha = invisible ? INVIS_ALPHA : 1;

    if (isPlayer) {
      this.playerSprite.setAlpha(alpha);
      this.playerNameText.setAlpha(alpha);
      // 그림자도 함께 흐려야 한다 — 몸만 사라지고 그림자가 남으면 위치가 그대로 들킨다.
      // SHADOW_ALPHA는 생성 시 fillAlpha로 이미 들어가 있으므로 여기서 곱하지 않는다.
      this.playerShadow?.setAlpha(alpha);
      this.chatBox.addSystemMessage(invisible ? '몸이 투명해졌습니다.' : '투명 상태가 풀렸습니다.');
      return;
    }

    const entity = this.entitySprites.get(objId);
    if (!entity) return;
    // 사망 연출이 알파를 tween으로 쓰고 있는 중이면 건드리지 않는다.
    if (entity.deathAnim) return;

    entity.sprite.setAlpha(alpha);
    entity.nameText.setAlpha(alpha);
    entity.shadow.setAlpha(alpha);
  }

  /**
   * 외형 이상상태 색을 스프라이트에 입힌다 (S_Poison).
   *
   * 정상(0)으로 돌아올 때 무조건 clearTint()를 부르면 안 된다 — 몹은 개체 구분용
   * tint(`mobTintColor`)를 평소에도 쓰고 있어서, 그것까지 지우면 색이 빠진 채로
   * 남는다. 그래서 몹은 원래 색으로 되돌리고, 그 외에만 tint를 걷어낸다.
   */
  private applyEntityPoison(objId: number, poisonType: number, isPlayer: boolean): void {
    const tint = POISON_TINTS[poisonType];

    if (isPlayer) {
      if (tint !== undefined) this.playerSprite.setTint(tint);
      else this.playerSprite.clearTint();
      return;
    }

    const entity = this.entitySprites.get(objId);
    if (!entity) return;

    if (tint !== undefined) {
      entity.sprite.setTint(tint);
    } else if (entity.kind === 'mob' && entity.mobTintColor !== undefined) {
      entity.sprite.setTint(entity.mobTintColor);
    } else {
      entity.sprite.clearTint();
    }
  }

  /**
   * 칭호를 이름표에 반영한다 (S_CharTitle).
   *
   * 이름표에는 칭호를 이름 앞에 꺾쇠로 붙인다. 칭호가 빈 문자열이면 해제이므로
   * 이름만 남긴다. 원래 이름은 GameState가 들고 있는 것을 다시 읽어 쓴다 —
   * 이름표 텍스트를 파싱해 되돌리려 하면 칭호가 두 번 붙는 사고가 난다.
   */
  private applyEntityTitle(objId: number, title: string, isPlayer: boolean): void {
    const baseName = isPlayer
      ? (this.playerBaseName || this.playerNameText.text)
      : (gameState.getPlayer(objId)?.charName
        ?? gameState.getNpc(objId)?.name
        ?? gameState.getMonster(objId)?.name);

    if (!baseName) return;
    const label = title ? `<${title}> ${baseName}` : baseName;

    if (isPlayer) {
      this.playerNameText.setText(label);
      return;
    }
    const entity = this.entitySprites.get(objId);
    if (!entity) return;
    entity.nameText.setText(label);
  }

  /**
   * 전투 결과를 대상 위에 잠깐 띄운다 (S_AttackPacket / S_AttackMissPacket).
   *
   * ⚠ 피해량은 띄우지 않는다 — 이 패킷의 damage 자리에 서버는 언제나 1을 쓴다
   * (`AttackResultPayload` 주석 참고). 실제로 깎인 HP는 뒤따라오는 S_HpUpdate로만
   * 알 수 있으므로, 여기서는 명중/빗나감만 알린다.
   */
  private showAttackFeedback(data: AttackResultPayload & {
    attackerIsPlayer: boolean;
    targetIsPlayer: boolean;
  }): void {
    // 나와 무관한 남들끼리의 공격까지 전부 띄우면 화면이 시끄럽다.
    if (!data.attackerIsPlayer && !data.targetIsPlayer) return;

    const target = data.targetIsPlayer
      ? this.playerSprite
      : this.entitySprites.get(data.targetObjId)?.sprite;
    if (!target) return;

    // 공격자가 남(몹·타인)이면 대상 쪽으로 몸을 내미는 몸짓을 준다 — 이게
    // 없으면 몹이 나를 때려도 미동조차 없어 누가 쳤는지 알 수 없다.
    // (내 공격의 몸짓·파편은 playAttackAnimation 쪽이 이미 처리한다.)
    const attacker = data.attackerIsPlayer
      ? undefined
      : this.entitySprites.get(data.attackerObjId);
    const attackAngle = attacker
      ? Math.atan2(target.y - attacker.sprite.y, target.x - attacker.sprite.x)
      : Math.random() * Math.PI * 2;
    if (attacker && !attacker.deathAnim && attacker.sprite.visible) {
      this.lungeEntitySprite(attacker, attackAngle);
    }

    // 내가 맞았다 — 화면이 살짝 흔들리고 몸에서 파편이 튄다.
    // UI는 별도 카메라가 그리므로 채팅창·인벤토리는 흔들리지 않는다.
    if (data.hit && data.targetIsPlayer) {
      this.cameras.main.shake(PLAYER_HIT_SHAKE_MS, PLAYER_HIT_SHAKE_INTENSITY);
      this.spawnHitSpark(
        target.x,
        target.y - PLAYER_BODY_HEIGHT,
        attackAngle,
        HIT_SPARK_DEFAULT_COLOR,
        this.playerTileX,
        this.playerTileY,
      );
    }

    const text = data.hit ? '명중' : 'MISS';
    const color = data.hit
      ? (data.targetIsPlayer ? '#ff6666' : '#ffdd55')
      : '#aaaaaa';

    const label = this.add.text(target.x, target.y - ENTITY_LABEL_OFFSET - 12, text, {
      fontSize: '13px',
      color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    label.setOrigin(0.5);
    label.setDepth(DEPTH_COMBAT_FEEDBACK);

    // 위로 떠오르며 사라진다. 트윈이 끝나면 반드시 스스로를 파괴해야 —
    // 전투가 길어질수록 텍스트 오브젝트가 쌓여 프레임이 무너진다.
    this.tweens.add({
      targets: label,
      y: label.y - 24,
      alpha: 0,
      duration: COMBAT_FEEDBACK_MS,
      ease: 'Cubic.easeOut',
      onComplete: () => label.destroy(),
    });

    // 맞은 쪽을 짧게 붉게 번쩍인다. 이미 이상상태 tint가 올라가 있으면
    // 건드리지 않는다 — 되돌릴 원래 색을 여기서 판단할 수 없다.
    if (data.hit && gameState.getEntityPoisonType(data.targetObjId) === 0) {
      const hitSprite = target;
      const entity = this.entitySprites.get(data.targetObjId);
      hitSprite.setTint(0xff8888);
      this.time.delayedCall(120, () => {
        if (!hitSprite.active) return;
        if (entity?.kind === 'mob' && entity.mobTintColor !== undefined) {
          hitSprite.setTint(entity.mobTintColor);
        } else {
          hitSprite.clearTint();
        }
      });
    }
  }

  /**
   * 플레이어 스프라이트 생성.
   * origin을 (0.5, 1)로 두고 타일 중심에 배치하므로 캐릭터가 타일 바닥을 딛고
   * 서 있는 것처럼 보인다.
   *
   * 본체는 SVG 텍스처가 올라가는 Phaser.Image다. 첫 진입 시에는 아직 캐릭터 ID를
   * 모를 수 있으므로 fallback으로 작은 텍스트(P)를 그려두고, 추후
   * setPlayerCharacter()가 호출되면 실제 SVG로 교체한다.
   */
  private createPlayerSprite(): void {
    const { x, y } = tileToWorld(this.playerTileX, this.playerTileY);

    // 로컬에서 사용하는 fallback 텍스트 (SVG 로드 실패/지연 시 가시성 보장).
    // 정상 흐름에서는 빠르게 SVG로 덮어쓰여 보이지 않는다.
    const fallbackStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '24px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      backgroundColor: '#1a1a2e',
      padding: { x: 6, y: 3 },
    };
    const fallback = this.add.text(x, y, 'P', fallbackStyle);
    fallback.setOrigin(0.5, 1);

    // Image는 텍스처가 등록되어야 생성 가능. 첫 프레임엔 임시 키로 placeholder
    // 텍스처(1x1 투명 캔버스)를 만들어 Image 객체를 만들고, 이후 실제 캐릭터
    // SVG 텍스처가 로드되면 setTexture로 교체한다.
    if (!this.textures.exists('__placeholder_player__')) {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      this.textures.addCanvas('__placeholder_player__', canvas);
    }
    // 발밑 그림자를 본체보다 먼저 만든다 (생성 순서와 무관하게 depth로 정렬되지만,
    // 같은 depth일 때의 기본 순서까지 안전하게 맞춰 둔다).
    this.playerShadow = this.createFootShadow(x, y);

    this.playerSprite = this.add.image(x, y, '__placeholder_player__');
    this.playerSprite.setOrigin(0.5, SPRITE_FOOT_ORIGIN_Y);
    // fallback 텍스트를 잠시 같이 두고, 실제 텍스처가 준비되면 fallback을 숨긴다.
    // (스프라이트를 add한 직후엔 depth가 동일해 fallback이 위에 보이는데, 캐릭터
    //  로드 후엔 Image가 위에 보이도록 Image를 fallback보다 살짝 앞 depth로 둔다.)
    this.playerSprite.setDepth(0);
    fallback.setDepth(-1);

    // 플레이어 이름 — CharSelectScene에서 캐릭터 선택 시 이미 localStorage에
    // 저장해둔 서버 등록 캐릭터명을 사용한다 (없으면 'Player'로 폴백).
    const charName = storageManager.loadCredentials()?.charName || 'Player';
    this.playerNameText = this.add.text(x, y - PLAYER_LABEL_OFFSET, charName, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 4, y: 2 }
    });
    this.playerNameText.setOrigin(0.5, 1);

    // 플레이어 본체/이름표는 월드 오브젝트 = 메인 카메라 전용 (줌 적용 대상)
    this.registerWorldObjects(this.playerSprite, this.playerNameText);
    // fallback은 visible=false로 두고 로드 실패시에만 켠다.
    fallback.setVisible(false);
    this.playerFallbackText = fallback;

    this.renderPlayerAt(x, y);
  }

  /** SVG 로드 전 임시로 보여주는 텍스트 (로컬 헬퍼 참조). */
  private playerFallbackText!: Phaser.GameObjects.Text;

  /** 플레이어 발밑 그림자. */
  private playerShadow!: Phaser.GameObjects.Ellipse;

  /**
   * 발밑 그림자를 하나 만든다 (플레이어·엔티티 공용).
   *
   * 스프라이트는 발이 타일 중심에 오도록 그려지므로(origin y = SPRITE_FOOT_ORIGIN_Y)
   * 타일 중심에 그대로 두면 정확히 발밑이 된다.
   */
  private createFootShadow(x: number, y: number): Phaser.GameObjects.Ellipse {
    const shadow = this.add.ellipse(x, y, SHADOW_RADIUS_X * 2, SHADOW_RADIUS_Y * 2, 0x000000, SHADOW_ALPHA);
    this.registerWorldObjects(shadow);
    return shadow;
  }

  // ==================== 엔티티 텍스처 적용 (캐릭터 / 몹 공통 창구) ====================

  /**
   * 엔티티의 프레임을 바꾸고 텍스처를 다시 붙인다.
   *
   * 스폰·방향 전환·걷기 프레임 진행·부활이 전부 이 한 곳을 지난다. 캐릭터
   * 아틀라스와 몹 SVG의 차이는 여기서만 갈리므로, 호출자는 어느 쪽인지 몰라도 된다.
   */
  private applyEntityFrame(entity: EntitySprite, frame: number): void {
    entity.frame = frame;
    if (entity.kind === 'mob') {
      this.applyMobTexture(entity);
    } else {
      this.applyCharEntityTexture(entity);
    }
  }

  /**
   * 캐릭터 아틀라스 프레임을 적용한다.
   * 아틀라스가 아직 없으면 비동기로 로드를 걸고 완료 시점에 다시 적용한다.
   */
  private applyCharEntityTexture(entity: EntitySprite): void {
    const key = `char|${entity.characterId}|${entity.weapon}|${entity.direction}|${entity.frame}`;
    if (entity.currentTextureKey === key) return;
    entity.currentTextureKey = key;

    const show = (): boolean => {
      if (!applyWalkSprite(this, entity.sprite, entity.characterId, entity.weapon, entity.direction, entity.frame)) {
        return false;
      }
      entity.sprite.setVisible(true);
      entity.fallbackText.setVisible(false);
      GameScene.syncHitArea(entity.sprite);
      return true;
    };

    if (show()) return;

    loadCharWalkAtlas(this, entity.characterId, entity.weapon)
      .then(() => {
        // 그 사이 다른 텍스처로 바뀌었거나 엔티티가 사라졌으면 적용하지 않는다.
        if (entity.currentTextureKey !== key) return;
        if (!this.entitySprites.has(entity.objId)) return;
        show();
      })
      .catch(() => {
        entity.fallbackText.setText(entity.fallbackLabel);
        entity.fallbackText.setVisible(true);
      });
  }

  /**
   * 몹 계열 SVG를 적용한다.
   *
   * 몹 SVG는 8방향이 전부 있어 캐릭터처럼 좌우 반전이 필요 없다(캐릭터 아틀라스는
   * W/SW/NW를 저장하지 않고 flipX로 만든다). 이전에 캐릭터 텍스처를 쓰던 스프라이트가
   * 넘어올 수 있으므로 flipX는 명시적으로 꺼 준다.
   *
   * 로드가 끝내 실패하면 캐릭터 경로로 강등해 기존 동작으로 안전하게 떨어진다.
   */
  private applyMobTexture(entity: EntitySprite): void {
    // 실제 몹 그림이 있으면 그쪽이 우선이다 (계열 SVG는 "짐승형" 정도만 알려준다).
    if (entity.mobImage && this.applyMobImage(entity, entity.mobImage)) return;

    const family = entity.mobFamily;
    if (!family) {
      this.fallbackEntityToCharacter(entity);
      return;
    }

    const key = mobTextureKey(family, entity.direction, entity.frame);
    if (entity.currentTextureKey === key) return;
    entity.currentTextureKey = key;

    // 이미 받아둔 텍스처는 동기 경로로 즉시 붙인다 — Promise를 거치면 프레임마다
    // 한 틱씩 늦어져 걷기가 끊겨 보인다.
    if (this.textures.exists(key)) {
      this.showMobTexture(entity, key);
      return;
    }

    loadMobSprite(this, family, entity.direction, entity.frame)
      .then((loadedKey) => {
        if (entity.currentTextureKey !== key) return;
        if (!this.entitySprites.has(entity.objId)) return;
        this.showMobTexture(entity, loadedKey);
      })
      .catch(() => {
        this.fallbackEntityToCharacter(entity);
      });
  }

  /**
   * 실사 몹 그림을 적용한다.
   *
   * 스프라이트시트 한 장에 프레임이 모두 들어 있으므로 텍스처 키는 하나이고
   * **프레임 번호만 바뀐다**. 그래서 중복 적용 판정에 쓰는 currentTextureKey에는
   * 키와 프레임을 함께 담는다 — 키만 담으면 걷기 프레임이 갱신되지 않는다.
   *
   * @returns 이 경로로 처리했으면 true. 그림을 끝내 못 쓰면 false를 돌려 계열
   *          SVG 경로로 넘긴다.
   */
  private applyMobImage(entity: EntitySprite, image: MobImageMeta): boolean {
    const key = mobImageTextureKey(image.slug);
    const frameIndex = mobImageFrameIndex(image, entity.frame);
    const stamp = `${key}#${frameIndex}`;
    if (entity.currentTextureKey === stamp) return true;

    if (this.textures.exists(key)) {
      entity.currentTextureKey = stamp;
      this.showMobImage(entity, image, key, frameIndex);
      return true;
    }

    entity.currentTextureKey = stamp;
    loadMobImage(this, image)
      .then((loadedKey) => {
        if (!this.entitySprites.has(entity.objId)) return;
        if (entity.mobImage !== image) return;   // 그 사이 다른 그림으로 바뀌었다
        this.showMobImage(entity, image, loadedKey, mobImageFrameIndex(image, entity.frame));
      })
      .catch(() => {
        // 그림을 못 받았다 — 이 몹은 앞으로 계열 SVG로 그린다.
        entity.mobImage = undefined;
        entity.currentTextureKey = null;
        if (this.entitySprites.has(entity.objId)) this.applyMobTexture(entity);
      });
    return true;
  }

  /**
   * 실사 몹 그림을 스프라이트에 올린다.
   *
   * 계열 SVG와 다른 점 셋:
   *  - **tint를 걷어낸다.** 이름 해시 색은 같은 계열을 구분하려던 장치라, 제 색을
   *    가진 그림에 곱하면 색만 탁해진다.
   *  - **크기를 원본 캔버스 기준으로 잡는다.** 잘라낸 프레임 크기로 맞추면 개구리가
   *    드래곤만큼 커진다 (MobImageMeta.srcHeight 주석 참고).
   *  - **발이 그림 아래끝이다.** 계열 SVG는 캔버스 안에 여백을 두고 그려 origin이
   *    0.833이지만, 실사 그림은 여백을 잘라내서 아래끝이 곧 발이다.
   */
  private showMobImage(
    entity: EntitySprite,
    image: MobImageMeta,
    textureKey: string,
    frameIndex: number,
  ): void {
    const sprite = entity.sprite;
    sprite.setTexture(textureKey, frameIndex);
    sprite.setFlipX(false);
    sprite.clearTint();
    sprite.setOrigin(0.5, 1);

    // 원본 캔버스를 MOB_IMAGE_CANVAS_PX로 줄인다고 보고, 그 안에서 몹이 차지하던
    // 비율만큼만 그린다(덩치 차이 보존). 다만 너무 작거나 크면 잘라낸다.
    const rawHeight = image.frameHeight * (MOB_IMAGE_CANVAS_PX / image.srcHeight);
    const height = Phaser.Math.Clamp(rawHeight, MOB_IMAGE_MIN_PX, MOB_IMAGE_MAX_PX);
    const scale = height / image.frameHeight;
    sprite.setDisplaySize(image.frameWidth * scale, height);

    sprite.setVisible(true);
    entity.fallbackText.setVisible(false);
    GameScene.syncHitArea(sprite);
  }

  /** 준비된 몹 텍스처를 스프라이트에 올리고 개체 색을 입힌다. */
  private showMobTexture(entity: EntitySprite, textureKey: string): void {
    entity.sprite.setTexture(textureKey);
    entity.sprite.setFlipX(false);
    entity.sprite.setTint(entity.mobTintColor ?? 0xffffff);
    // 실사 그림에서 되돌아온 경우를 대비해 배치 규약을 계열 SVG 쪽으로 원복한다
    // (그림마다 origin·크기를 바꿔 두므로 남아 있으면 다음 그림이 어긋난다).
    entity.sprite.setOrigin(0.5, SPRITE_FOOT_ORIGIN_Y);
    entity.sprite.setScale(1);
    entity.sprite.setVisible(true);
    entity.fallbackText.setVisible(false);
    GameScene.syncHitArea(entity.sprite);
  }

  /**
   * 클릭/마우스오버 판정 영역을 현재 텍스처 크기에 맞춘다.
   *
   * 엔티티 스프라이트는 1x1 placeholder 텍스처로 만들어진 뒤 setInteractive()가
   * 걸린다. Phaser는 그때의 프레임 크기로 hitArea 사각형을 한 번 잡고, 이후
   * setTexture()로 그림이 바뀌어도 그 사각형을 갱신하지 않는다. 그대로 두면
   * 판정 영역이 1x1로 남아 이름표가 사실상 뜨지 않는다.
   */
  private static syncHitArea(sprite: Phaser.GameObjects.Image): void {
    const hitArea = sprite.input?.hitArea as Phaser.Geom.Rectangle | undefined;
    if (!hitArea || typeof hitArea.setSize !== 'function') return;
    if (hitArea.width === sprite.width && hitArea.height === sprite.height) return;
    hitArea.setPosition(0, 0);
    hitArea.setSize(sprite.width, sprite.height);
  }

  /**
   * 몹 그림을 끝내 못 구한 엔티티를 캐릭터 스프라이트로 되돌린다.
   * 한 번 강등되면 다시 몹으로 올라가지 않는다 — 같은 파일을 계속 재시도하며
   * 매 프레임 네트워크 요청을 쏟아내는 것을 막는다.
   */
  private fallbackEntityToCharacter(entity: EntitySprite): void {
    if (entity.kind === 'char') return;
    entity.kind = 'char';
    entity.mobFamily = undefined;
    // 실사 그림도 함께 버린다 — 남겨 두면 applyMobTexture가 다시 그쪽으로 간다.
    entity.mobImage = undefined;
    entity.sprite.clearTint();
    // 실사 그림이 바꿔 둔 배치 규약을 캐릭터 쪽으로 원복한다.
    entity.sprite.setOrigin(0.5, SPRITE_FOOT_ORIGIN_Y);
    entity.sprite.setScale(1);
    entity.currentTextureKey = null;
    this.applyCharEntityTexture(entity);
  }

  /**
   * 캐릭터 스폰 핸들러
   */
  private handleCharSpawn(data: any): void {
    const { objId, x, y, gfxId, heading, charName } = data;

    // GameState 업데이트
    gameState.setPlayer(objId, x, y);

    // 플레이어 캐릭터 식별 (gfxId → CharacterId)
    this.playerCharacterId = inferCharacter(gfxId);
    this.playerWeapon = this.resolvePlayerWeapon();
    this.playerDirection = headingToDirection(heading ?? this.currentHeading);
    this.playerFrame = 0;
    this.playerIsWalking = false;
    this.playerWalkFrameTimer = 0;
    this.applyPlayerTexture();
    // 칭호(S_CharTitle)가 붙어도 원래 이름으로 되돌릴 수 있게 따로 보관한다.
    this.playerBaseName = charName || 'Player';
    this.playerNameText.setText(this.playerBaseName);

    // 위치 업데이트 (서버 타일 좌표를 그대로 스냅)
    this.snapPlayerToTile(x, y);
    this.currentHeading = heading ?? this.currentHeading;

    // 미니맵 업데이트
    this.miniMap.setPlayerPosition(x, y, heading);

    // 시스템 메시지
    this.chatBox.addSystemMessage(`Spawned: ${charName} at (${x}, ${y})`);
  }

  /**
   * 플레이어의 현재 (characterId, weapon, direction, frame)에 해당하는 atlas
   * 프레임을 적용한다. atlas가 로드돼 있으면 즉시 적용, 아니면 비동기로 로드 후
   * 다시 적용한다.
   */
  private applyPlayerTexture(): void {
    // 변신 중이면 캐릭터 아틀라스 대신 몹 계열 그림을 쓴다 (S_ChangeShape).
    // 그림을 고르는 창구가 여기 하나뿐이라, 방향 전환·걷기 프레임·부활 등
    // 모든 호출자가 자동으로 변신한 모습을 따라간다.
    if (this.playerPolyFamily) {
      this.applyPlayerMobTexture(this.playerPolyFamily);
      return;
    }

    const applyNow = (): boolean => {
      return applyWalkSprite(
        this,
        this.playerSprite,
        this.playerCharacterId,
        this.playerWeapon,
        this.playerDirection,
        this.playerFrame,
      );
    };
    if (applyNow()) {
      this.playerSprite.setVisible(true);
      this.playerFallbackText.setVisible(false);
      return;
    }
    loadCharWalkAtlas(this, this.playerCharacterId, this.playerWeapon)
      .then(() => {
        if (applyNow()) {
          this.playerSprite.setVisible(true);
          this.playerFallbackText.setVisible(false);
        }
      })
      .catch(() => {
        this.playerFallbackText.setText('P');
        this.playerFallbackText.setVisible(true);
      });
  }

  /**
   * 변신 중인 자기 캐릭터에 몹 계열 그림을 적용한다 (엔티티 쪽 applyMobTexture와 같은 규칙).
   *
   * 몹 SVG는 8방향이 모두 있어 좌우 반전이 필요 없으므로 flipX는 꺼 준다
   * (캐릭터 아틀라스를 쓰던 스프라이트가 그대로 넘어온다).
   * 그림을 못 받으면 원래 캐릭터 모습을 그대로 둔다 — 변신이 안 보일 뿐,
   * 여기서 폴백 텍스트를 띄우면 자기 캐릭터가 통째로 사라진다.
   *
   * 사망 연출(advancePlayerDeathFrame)은 캐릭터 사망 아틀라스를 쓰므로 변신 중
   * 죽으면 원래 모습으로 쓰러진다. 몹에는 쓸 사망 그림이 없어 그대로 둔다.
   */
  private applyPlayerMobTexture(family: MobFamily): void {
    const key = mobTextureKey(family, this.playerDirection, this.playerFrame);

    const show = (): void => {
      this.playerSprite.setTexture(key);
      this.playerSprite.setFlipX(false);
      this.playerSprite.setTint(this.playerPolyTint);
      this.playerSprite.setVisible(true);
      this.playerFallbackText.setVisible(false);
    };

    // 이미 받아둔 텍스처는 즉시 붙인다 — Promise를 거치면 걷기가 한 틱씩 늦는다.
    if (this.textures.exists(key)) {
      show();
      return;
    }

    loadMobSprite(this, family, this.playerDirection, this.playerFrame)
      .then(() => {
        // 그 사이 변신이 풀렸거나 방향/프레임이 바뀌었으면 적용하지 않는다.
        if (this.playerPolyFamily !== family) return;
        if (mobTextureKey(family, this.playerDirection, this.playerFrame) !== key) return;
        show();
      })
      .catch(() => { /* 변신 그림을 못 받으면 원래 모습 그대로 둔다 */ });
  }

  /**
   * 객체 이동 핸들러
   */
  private handleObjectMove(data: any): void {
    const { objId, x, y, heading } = data;

    // 플레이어 자신의 이동은 GameState에서 처리.
    // (gameState.updatePlayerPosition -> 'player:position' -> syncPlayerTileFromServer)
    if (objId === gameState.getPlayerObjId()) {
      gameState.updatePlayerPosition(x, y, heading);
      this.miniMap.setPlayerPosition(x, y, heading);
      return;
    }

    // 다른 엔티티 이동.
    // 화면 갱신은 gameState가 내보내는 'entity:move'가 맡는다 — 여기서 직접
    // updateEntitySprite를 부르면 같은 이동이 두 번 처리되고, 두 번째 호출은
    // 타일이 이미 갱신된 상태라 방금 시작한 이동 보간을 취소해 버린다.
    // (자기 캐릭터가 'player:position'을 거치는 것과 같은 구조)
    gameState.updateEntityPosition(objId, x, y, heading);
  }

  /**
   * 객체 제거 핸들러
   */
  private handleObjectRemove(data: any): void {
    const { objId } = data;
    // 바닥 아이템과 엔티티는 같은 objId 공간을 쓴다. 누군가 주웠거나 사라진
    // 아이템도 이 패킷으로 통지되므로 양쪽에서 지운다.
    if (this.removeGroundItem(objId)) return;
    this.removeEntitySprite(objId);
    gameState.removeEntity(objId);
  }

  /**
   * 채팅 메시지 핸들러
   * GameState도 chat_message를 직접 구독해 addChatMessage하므로 여기서는
   * ChatBox 표시만 담당한다 (이중 저장 방지).
   */
  private handleChatMessage(data: any): void {
    const { type, sender, message } = data;
    this.chatBox.addMessage({
      type: type || 'all',
      sender: sender || 'Unknown',
      message: message || '',
      timestamp: Date.now()
    });
  }

  /**
   * 서버 브로드캐스트 채팅 핸들러 (opcode 76/255, S_ChatPacket)
   * 일반/외침/파티채팅은 서버가 자신에게도 되돌려 보내므로, sendChat()에서
   * 이미 로컬 에코로 표시한 자신의 메시지는 여기서 중복 표시하지 않는다.
   */
  private handleServerChat(data: any): void {
    const senderId: number | undefined = data.senderId;
    const isOwnEcho = senderId !== undefined && senderId !== 0 && senderId === gameState.getPlayerObjId();
    if (isOwnEcho) return;

    const sender = data.senderName
      || gameState.getPlayer(senderId || 0)?.charName
      || 'Unknown';

    // 게이트웨이 channel명("normal"/"chat_party"/"whisper"/...)을 ChatBox 채널로 매핑.
    // channel이 비었거나 "unknown_N"이면 원본 chatType으로 되살린다 — 그러지 않으면
    // 게이트웨이가 모르는 타입이 전부 일반 채팅으로 뭉개진다.
    const channel = serverChatChannelName(data);
    const channelMap: Record<string, string> = {
      normal: 'all',
      shout: 'shout',
      world: 'world',
      clan: 'clan',
      party: 'party',
      chat_party: 'party',
      trade: 'trade',
      alliance: 'alliance',
      whisper: 'tell'
    };
    const type = channelMap[channel] || 'all';

    this.chatBox.addMessage({
      type: type as any,
      sender,
      message: data.message || '',
      timestamp: Date.now()
    });

    gameState.addChatMessage({
      type,
      sender,
      message: data.message || '',
      timestamp: Date.now()
    });
  }

  /**
   * 인벤토리 핸들러
   * 게이트웨이는 아이템 배열 자체를 payload로 보낸다 ({ items: [...] } 래핑이 아님).
   */
  private handleInventory(data: any): void {
    const items = Array.isArray(data) ? data : (data?.items ?? []);
    gameState.setInventory(items);

    // 화면 갱신은 gameState가 내보내는 'inventory:update'가 맡는다.
    // 여기서 또 그리면 같은 목록을 두 번 그리게 된다.
  }

  /**
   * 변환이 끝난 인벤토리를 관련 창들에 반영한다.
   * 수신 경로(handleInventory)와 씬 진입 시 복원 경로(hydrate)가 같은 곳을 거치게 한다.
   */
  private applyInventory(items: InventoryItem[]): void {
    this.inventory.updateItems(items);
    this.equipmentPanel.setItems(items);
    this.syncQuickSlots(items);
  }

  // ==================== 퀵슬롯 (F5~F12) ====================

  /**
   * 퀵슬롯 등록을 보관할 키. 서버가 아니라 브라우저에 남기므로 캐릭터명을 쓴다.
   * (계정 단위로 묶으면 같은 계정의 다른 캐릭터가 서로의 등록을 덮어쓴다.)
   */
  private quickSlotOwner(): string {
    return storageManager.loadCredentials()?.charName || '';
  }

  /**
   * 인벤토리에서 끌어다 놓은 아이템을 슬롯에 등록한다.
   *
   * 같은 아이템이 다른 슬롯에 이미 있으면 그쪽을 비운다 — 원본 클라이언트처럼
   * 한 아이템은 한 자리만 차지하게 해서 "F7을 눌렀는데 F5 것이 나가는" 혼동을 없앤다.
   */
  private assignQuickSlot(slotIndex: number, item: InventoryItem): void {
    for (let i = 0; i < this.quickSlotBar.getSlotCount(); i++) {
      if (i !== slotIndex && this.quickSlotBar.getItem(i)?.id === item.id) {
        this.quickSlotBar.setItem(i, null);
      }
    }

    this.quickSlotBar.setItem(slotIndex, this.toQuickSlotItem(item), true);
    this.chatBox.addSystemMessage(`F${slotIndex + 5}에 등록: ${item.name}`);
  }

  /**
   * 라벨에서 개수 꼬리를 떼는 것이 중요하다 — 슬롯은 개수를 `count`로 따로
   * 그리므로 "물약 (6) x6"이 되어 버리고, 무엇보다 **재바인딩의 비교 기준**이
   * 개수 하나에 흔들리면 안 된다 (stripCountSuffix 주석 참조).
   */
  private toQuickSlotItem(item: InventoryItem): QuickSlotItem {
    return {
      id: item.id,
      gfxId: item.gfxId,
      count: item.count,
      label: stripCountSuffix(item.name),
      enchantLevel: item.enchantLevel,
      identified: item.identified,
      equipped: item.equipped,
    };
  }

  private saveQuickSlots(): void {
    const owner = this.quickSlotOwner();
    if (!owner) return;

    const slots: (StoredQuickSlot | null)[] = this.quickSlotBar
      .getItems()
      .map((item) =>
        item ? { id: item.id, gfxId: item.gfxId ?? 0, name: item.label ?? '' } : null
      );
    storageManager.saveQuickSlots(owner, slots);
  }

  private restoreQuickSlots(): void {
    const owner = this.quickSlotOwner();
    if (!owner) return;

    const saved = storageManager.loadQuickSlots(owner);
    saved.forEach((entry, index) => {
      if (!entry || index >= this.quickSlotBar.getSlotCount()) return;
      // 인벤토리가 아직 없으므로 일단 '없음'으로 올린다. 목록이 도착하면
      // syncQuickSlots()가 실제 아이템을 찾아 채운다.
      this.quickSlotBar.setItem(index, {
        id: entry.id,
        gfxId: entry.gfxId,
        label: entry.name,
        missing: true,
      });
    });
  }

  /**
   * 인벤토리가 갱신될 때마다 퀵슬롯 표시를 맞춘다.
   *
   * 등록은 objId로 하지만 objId는 영원하지 않다 — 포션을 다 쓰고 새로 사면
   * 이름도 종류도 같은데 objId만 다른 아이템이 들어온다. 그때마다 등록이
   * 풀리면 단축키를 쓸 수가 없으므로, objId가 사라졌을 때 **같은 gfxId + 같은
   * 이름**이면서 아직 다른 슬롯이 쓰지 않는 아이템을 찾아 objId를 갈아 끼운다.
   * 그마저 없으면 등록을 지우지 않고 흐리게(missing) 남겨 둔다.
   */
  private syncQuickSlots(items: InventoryItem[]): void {
    const byId = new Map(items.map((it) => [it.id, it]));
    const slots = this.quickSlotBar.getItems();

    // 이미 살아 있는 등록이 점유한 objId. 재바인딩이 이것들을 뺏어가지 않게 한다.
    const claimed = new Set<number>(
      slots.filter((s) => s && byId.has(s.id)).map((s) => (s as QuickSlotItem).id)
    );

    let rebound = false;

    slots.forEach((slot, index) => {
      if (!slot) return;

      const found = byId.get(slot.id);
      if (found) {
        this.quickSlotBar.setItem(index, this.toQuickSlotItem(found));
        return;
      }

      // 개수 꼬리를 뗀 이름끼리 견준다 — "물약 (7)"과 "물약 (6)"은 같은 물약이다.
      const wanted = stripCountSuffix(slot.label ?? '');
      const replacement = items.find(
        (it) =>
          !claimed.has(it.id) &&
          it.gfxId === (slot.gfxId ?? 0) &&
          stripCountSuffix(it.name) === wanted
      );
      if (replacement) {
        claimed.add(replacement.id);
        this.quickSlotBar.setItem(index, this.toQuickSlotItem(replacement));
        rebound = true;
        return;
      }

      // 인벤토리에 없다 — 등록은 남기고 표시만 흐리게 한다.
      if (!slot.missing) {
        this.quickSlotBar.setItem(index, { ...slot, missing: true });
      }
    });

    // objId가 바뀐 등록은 다음 접속에서도 그대로여야 하므로 저장을 갱신한다.
    if (rebound) this.saveQuickSlots();
  }

  /**
   * 장착 무기를 캐릭터 그림에 반영한다.
   *
   * 서버는 장착 무기를 따로 알려주지 않으므로 인벤토리에서 역산한다. 이 값은
   * 그림(검/활/지팡이를 든 스프라이트)과 공격 모션(근접/원거리)을 함께 정한다.
   *
   * @param rawItems 게이트웨이가 보낸 그대로의 아이템 목록. 장착 접미사가
   *                 남아 있어야 무기를 가려낼 수 있다.
   */
  private applyEquippedWeapon(rawItems: any[]): void {
    const weapon = equippedWeaponType(rawItems);
    if (weapon === this.playerWeapon) return;

    this.playerWeapon = weapon;
    this.applyPlayerTexture();
  }

  /**
   * 지금 들고 있는 무기를 인벤토리에서 다시 읽는다. 그림 갱신은 호출자 몫이다.
   *
   * 스폰 경로에서 쓴다 — 스폰은 인벤토리보다 나중에 올 수 있고, 그때 무기를
   * 맨손으로 되돌리면 들고 있던 검이 그림에서 사라진다.
   */
  private resolvePlayerWeapon(): WeaponType {
    return equippedWeaponType(gameState.getInventory());
  }

  /**
   * 게이트웨이 아이템 payload를 InventoryItem으로 변환한다.
   *
   * 장착 여부는 서버가 표시명 끝에 붙이는 접미사로만 알 수 있다 — 판별에 쓰고
   * 화면에는 원본 클라이언트 리소스 키($9/$117)가 노출되지 않게 떼어낸다.
   */
  private toInventoryItems(items: any[]): InventoryItem[] {
    return items.map((item: any, index: number) => {
      const rawName = item.name || 'Unknown Item';
      return {
        id: item.id || item.itemId || index,
        gfxId: item.gfxId || 0,
        count: item.count || 1,
        // 서버가 보내는 $숫자 리소스 키를 사람이 읽을 이름으로 바꾼다.
        // 여기서 한 번만 해석하면 인벤토리·장비창이 같은 이름을 쓴다.
        name: resolveItemName(stripEquippedSuffix(rawName)),
        identified: item.identified !== false,
        enchantLevel: item.enchantLevel || 0,
        slot: item.slot ?? index,
        equipped: isEquippedName(rawName),
      };
    });
  }

  /**
   * 타일 좌표를 서버 범위(0~65535)로 자른다.
   */
  private clampTile(v: number): number {
    return Math.max(0, Math.min(MAX_TILE, Math.round(v)));
  }

  /**
   * 아이소메트릭 깊이 값. tileDepth()의 절대값을 플레이어 기준 상대값으로 환산해
   * UI depth(90~200)와 충돌하지 않는 좁은 범위로 눌러 담는다.
   */
  private depthFor(tx: number, ty: number): number {
    const d = (tileDepth(tx, ty) - this.depthOrigin) * DEPTH_SCALE;
    return Math.max(-DEPTH_LIMIT, Math.min(DEPTH_LIMIT, d));
  }

  /**
   * 플레이어 스프라이트/이름표를 주어진 월드 좌표에 그리고 카메라 목표를 갱신한다.
   * (논리 타일 좌표는 건드리지 않는다 — 스텝 보간 중에도 매 프레임 호출된다.)
   */
  private renderPlayerAt(worldX: number, worldY: number): void {
    // 씬 초기화 순서상 스프라이트보다 서버 이벤트가 먼저 도착할 수 있다.
    if (!this.playerSprite) {
      this.cameraTargetX = worldX;
      this.cameraTargetY = worldY;
      return;
    }

    // 공격 몸짓은 몸통에만 준다 — 이름표까지 흔들리면 시선이 분산된다.
    this.playerSprite.setPosition(worldX + this.attackLunge.x, worldY + this.attackLunge.y);
    this.playerNameText.setPosition(worldX, worldY - PLAYER_LABEL_OFFSET);
    // 그림자는 발이 딛는 자리에 남는다 — 공격 몸짓으로 몸이 앞뒤로 움직여도
    // 그림자까지 따라가면 땅에서 미끄러지는 것처럼 보인다.
    this.playerShadow?.setPosition(worldX, worldY);

    const depth = this.depthFor(this.playerTileX, this.playerTileY);
    this.playerSprite.setDepth(depth);
    this.playerNameText.setDepth(depth + LABEL_DEPTH_BONUS);
    this.playerShadow?.setDepth(depth + SHADOW_DEPTH_OFFSET);

    this.cameraTargetX = worldX;
    this.cameraTargetY = worldY;
  }

  /**
   * 플레이어의 논리 타일 좌표를 확정하고 화면에 반영한다.
   * 진행 중이던 이동(스텝/클릭 목적지)은 전부 취소된다 — 스폰/텔레포트/서버 보정용.
   */
  private snapPlayerToTile(tx: number, ty: number): void {
    this.playerTileX = this.clampTile(tx);
    this.playerTileY = this.clampTile(ty);
    this.isStepping = false;
    this.stepElapsed = 0;
    this.clearMoveTarget();

    // 스프라이트도 idle 상태로 되돌리고 현재 heading 방향 SVG를 다시 그린다.
    this.playerIsWalking = false;
    this.playerWalkFrameTimer = 0;
    this.playerFrame = 0;
    this.playerDirection = headingToDirection(this.currentHeading);
    this.applyPlayerTexture();

    this.refreshDepthOrigin();

    const { x, y } = tileToWorld(this.playerTileX, this.playerTileY);
    this.renderPlayerAt(x, y);

    // 스폰/서버 보정으로 위치가 확정됐다 — 텔레포트를 기다리고 있었다면 여기가 끝이다.
    // 카메라는 이 프레임의 lerp 없이 곧바로 새 좌표로 옮긴다. 그러지 않으면
    // 페이드인이 시작되는 순간 화면이 옛 위치에서 새 위치로 길게 미끄러진다.
    if (this.teleportPending) {
      this.cameras.main.setScroll(x - this.cameras.main.width / 2, y - this.cameras.main.height / 2);
      this.finishTeleport();
    }
  }

  /**
   * 서버가 알려준 자기 위치를 반영한다.
   * 우리가 방금 시작한 타일 스텝의 도착지(또는 현재 서 있는 타일)와 같으면
   * 자기 이동의 에코이므로 무시한다 — 그렇지 않으면 매 스텝마다 보간이 끊긴다.
   * 그 외(재접속/텔레포트/서버 보정)에는 스냅하고 이동을 취소한다.
   */
  private syncPlayerTileFromServer(tx: number, ty: number): void {
    const cx = this.clampTile(tx);
    const cy = this.clampTile(ty);

    if (this.isStepping && cx === this.stepToTileX && cy === this.stepToTileY) return;
    if (!this.isStepping && cx === this.playerTileX && cy === this.playerTileY) return;

    this.snapPlayerToTile(cx, cy);
  }

  /**
   * depth 기준점을 현재 플레이어 타일로 옮기고 모든 스프라이트의 depth를 다시 계산한다.
   */
  private refreshDepthOrigin(): void {
    this.depthOrigin = tileDepth(this.playerTileX, this.playerTileY);

    const playerDepth = this.depthFor(this.playerTileX, this.playerTileY);
    this.playerSprite?.setDepth(playerDepth);
    this.playerNameText?.setDepth(playerDepth + LABEL_DEPTH_BONUS);
    this.playerShadow?.setDepth(playerDepth + SHADOW_DEPTH_OFFSET);

    this.entitySprites.forEach((entity) => {
      const depth = this.depthFor(entity.tileX, entity.tileY);
      entity.sprite.setDepth(depth);
      entity.nameText.setDepth(depth + LABEL_DEPTH_BONUS);
      entity.healthBar?.setDepth(depth + LABEL_DEPTH_BONUS);
      entity.shadow.setDepth(depth + SHADOW_DEPTH_OFFSET);
    });
  }

  /**
   * gfxId가 **플레이어 캐릭터 그림**을 가리키는가.
   *
   * `inferCharacter()`는 매핑에 없는 gfxId를 전부 'prince_m'으로 폴백시킨다.
   * 그래서 폴백과 진짜 군주(gfxId 0)를 구분할 수 없는데, 여기서는 폴백 쪽으로
   * 해석한다 — NPC/몬스터가 군주 그림을 쓰는 경우보다, 매핑에 없는 몬스터가
   * 왕자로 보이는 사고가 압도적으로 흔하기 때문이다(이 코드가 고치려는 증상).
   * 기사/요정/마법사 등 명시적으로 매핑된 그림은 그대로 캐릭터 경로를 탄다.
   */
  private static isPlayerLikeGfx(gfxId: number): boolean {
    return inferCharacter(gfxId) !== 'prince_m';
  }

  /**
   * 엔티티의 표시명과 폴백 글자를 한 번에 정한다.
   *
   * **이름 해석은 반드시 이 함수 하나만 거친다.** 스폰·갱신·재스폰 경로가 각자
   * 이름을 만들면 같은 몹이 경로에 따라 다르게 보인다.
   *
   * 우선순위:
   *   ① 서버가 준 이름 — `resolveNpcName()`으로 name_id를 풀고 `stripNpcTitle()`로
   *      칭호(`"물약 상인^키리스"`의 `^` 뒷부분)를 뗀다.
   *   ② gfxId 폴백표(`NPC_NAME_BY_GFX`) — 서버 `npcs.name_id`가 비어 있는 NPC가
   *      179종 있어 ①이 빈 문자열로 나온다. 그 179종 중 158종을 이 표가 되찾는다.
   *      (표의 값은 생성 시점에 이미 칭호가 떼어져 있다)
   *   ③ 그래도 못 찾았고 원문이 `$숫자`뿐이면 그 원문 — 무엇이 왔는지 추적할 수
   *      있어야 하므로 이름표에는 남긴다.
   *   ④ 종류별 총칭.
   *
   * 폴백 글자(`fallbackLabel`)는 텍스처 로드가 실패했을 때 화면에 그리는 값이다.
   * ①②로 진짜 이름을 찾았으면 그 이름을, 못 찾았으면 종전대로 한 글자
   * ('M'/'N'/'P')를 쓴다 — `$958`이나 'Monster'를 크게 그려 봐야 소용이 없다.
   */
  private static resolveEntityLabels(
    entity: any,
    type: 'npc' | 'monster' | 'player',
  ): { displayName: string; fallbackLabel: string } {
    const raw = String(entity?.name ?? entity?.charName ?? '').trim();

    // ① 서버 이름. GameState가 이미 resolveNpcName을 거친 값을 넣어주지만 다른
    //    경로로 들어온 원본($숫자)일 수도 있어 한 번 더 통과시킨다(멱등).
    //    이름이 비어서 GameState가 끼워 넣은 자리표시자('NPC' 등)는 이름이 아니다 —
    //    여기서 걸러내야 ②의 gfxId 폴백표까지 내려간다.
    const named = stripNpcTitle(resolveNpcName(raw));
    const resolved = named && !UNRESOLVED_NAME_ID.test(named) && !NAME_PLACEHOLDERS.has(named);
    const fromServer = resolved ? named : '';

    // ② gfxId 폴백표. 표는 DB `npcs`에서 뽑은 것이라 **다른 플레이어에게는 쓰지
    //    않는다** — 플레이어도 gfxId 0~13(직업/성별 그림)을 쓰므로, 이름을 못 받은
    //    플레이어가 같은 gfxId를 쓰는 NPC 이름('블랙 티거' 등)으로 보이게 된다.
    const fromGfx =
      fromServer || type === 'player' ? '' : (NPC_NAME_BY_GFX[Number(entity?.gfxId)] ?? '');

    const known = fromServer || fromGfx;
    const generic = type === 'monster' ? 'Monster' : type === 'player' ? 'Player' : 'NPC';
    const letter = type === 'monster' ? 'M' : type === 'player' ? 'P' : 'N';

    return {
      displayName: known || named || generic, // ③ → ④
      fallbackLabel: known || letter,
    };
  }

  /**
   * 엔티티 스프라이트 생성
   *
   * 본체는 SVG 텍스처가 올라가는 Phaser.Image. 텍스처가 비동기로 로드되는 동안은
   * 임시 텍스트(첫 글자)를 fallback으로 함께 그려, 로드 전이라도 위치/타입이
   * 즉시 보이도록 한다. 로드가 끝나면 fallback을 숨기고 Image를 표시한다.
   *
   * 몬스터/NPC는 캐릭터 아틀라스가 아니라 **몹 계열 SVG**를 쓴다. 캐릭터 그림은
   * 14종뿐이라 매핑에 없는 gfxId가 전부 군주로 떨어졌고, 그 결과 모든 몬스터가
   * 왕자 모습이었다. mobSprites가 gfxId/이름으로 12계열 중 하나를 고르고,
   * 이름 해시 색(setTint)으로 같은 계열 안의 개체를 구분한다.
   */
  private spawnEntitySprite(entity: any, type: 'npc' | 'monster' | 'player'): void {
    if (this.entitySprites.has(entity.objId)) return;

    // entity.x / entity.y 는 서버 타일 좌표다. 화면 좌표는 여기서 파생시킨다.
    const tileX = this.clampTile(entity.x);
    const tileY = this.clampTile(entity.y);
    const { x, y } = tileToWorld(tileX, tileY);
    const depth = this.depthFor(tileX, tileY);

    // 캐릭터 식별자/방향/무기
    const characterId = inferCharacter(entity.gfxId);
    const weapon = inferWeapon(entity.gfxId, type);
    const direction = headingToDirection(entity.heading ?? 0);

    // 표시명/폴백 글자. 계열 판정과 개체 색이 이 이름에 걸려 있으므로
    // 스프라이트보다 먼저 정한다. 해석 규칙은 resolveEntityLabels() 한 곳에 있다.
    const { displayName, fallbackLabel } = GameScene.resolveEntityLabels(entity, type);

    // 플레이어와, 플레이어 외형을 쓰는 NPC만 캐릭터 아틀라스를 쓴다.
    const useMob = type !== 'player' && !GameScene.isPlayerLikeGfx(entity.gfxId);
    const mobFamily = useMob ? inferMobFamily(entity.gfxId, displayName) : undefined;
    const mobTintColor = useMob ? mobTint(displayName) : undefined;
    // 실제 몹 그림이 있는 gfxId인가. 계열 SVG는 이제 폴백이다.
    const mobImage = useMob ? (mobImageFor(entity.gfxId) ?? undefined) : undefined;

    // fallback 텍스트 (SVG 로드 전 가시성 보장). 이름을 찾았으면 한 글자 대신
    // 이름을 그린다 — 몹이 전부 'M'으로만 보이던 원인이 여기였다.
    const fallbackColor =
      type === 'monster' ? '#ff4444' : type === 'player' ? '#66ccff' : '#ffaa00';
    const fallbackStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: fallbackLabel.length > 1 ? ENTITY_FALLBACK_NAME_FONT : ENTITY_FALLBACK_LETTER_FONT,
      fontFamily: 'Arial, sans-serif',
      color: fallbackColor,
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 2 },
    };
    const fallback = this.add.text(x, y, fallbackLabel, fallbackStyle);
    fallback.setOrigin(0.5, 1);
    fallback.setDepth(depth - 0.001); // Image보다 살짝 아래

    // 임시 placeholder 텍스처로 Image 객체 생성 후 실제 SVG로 교체
    if (!this.textures.exists('__placeholder_entity__')) {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      this.textures.addCanvas('__placeholder_entity__', canvas);
    }
    const shadow = this.createFootShadow(x, y);
    shadow.setDepth(depth + SHADOW_DEPTH_OFFSET);

    const sprite = this.add.image(x, y, '__placeholder_entity__');
    sprite.setOrigin(0.5, SPRITE_FOOT_ORIGIN_Y);
    sprite.setDepth(depth);

    // 이름 텍스트. 여러 엔티티가 좌표상 겹칠 때 라벨이 뭉개지지 않도록
    // 평소에는 숨겨두고, 스프라이트에 마우스를 올렸을 때만 표시한다.
    const nameText = this.add.text(x, y - ENTITY_LABEL_OFFSET, displayName, {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
      backgroundColor: '#000000aa',
      padding: { x: 3, y: 1 }
    });
    nameText.setOrigin(0.5, 1);
    nameText.setDepth(depth + LABEL_DEPTH_BONUS);
    nameText.setVisible(false);

    // 마우스 오버 시 이름 표시
    sprite.setInteractive({ useHandCursor: true });
    sprite.on('pointerover', () => nameText.setVisible(true));
    sprite.on('pointerout', () => nameText.setVisible(false));

    // HP 바 (몬스터만)
    let healthBar: Phaser.GameObjects.Graphics | undefined;
    if (type === 'monster') {
      healthBar = this.add.graphics();
      healthBar.setDepth(depth + LABEL_DEPTH_BONUS);
    }

    // 엔티티 본체/이름표/HP바/폴백 텍스트는 전부 월드 오브젝트 = 메인 카메라 전용
    this.registerWorldObjects(sprite, nameText, fallback);
    if (healthBar) {
      this.registerWorldObjects(healthBar);
    }

    const entitySprite: EntitySprite = {
      objId: entity.objId,
      sprite,
      shadow,
      nameText,
      healthBar,
      type,
      tileX,
      tileY,
      kind: useMob ? 'mob' : 'char',
      mobFamily,
      mobTintColor,
      mobImage,
      characterId,
      weapon,
      direction,
      frame: 0,
      isWalking: false,
      walkFrameTimer: 0,
      currentTextureKey: null,
      fallbackLabel,
      fallbackText: fallback,
      // 처음 스폰된 시점을 기준으로 잡아, 첫 이동의 보간 시간이 과도해지지 않게 한다.
      lastMoveAt: this.time.now,
    };

    // Map에 먼저 넣는다 — 텍스처 로드 완료 콜백이 "아직 살아 있는 엔티티인가"를
    // entitySprites로 확인하므로, 나중에 넣으면 첫 적용이 통째로 버려질 수 있다.
    this.entitySprites.set(entity.objId, entitySprite);

    if (mobFamily) this.preloadMobFamilyOnce(mobFamily);

    // 텍스처 비동기 적용 (실패 시 fallback 텍스트 유지)
    this.applyEntityFrame(entitySprite, 0);

    // 미니맵에 엔티티 추가
    this.syncMiniMapEntities();
  }

  // ==================== 바닥 아이템 ====================

  /**
   * 바닥 아이템 통지(S_DropItem → `drop_item`)를 화면에 반영한다.
   *
   * 서버는 **시야에 들어올 때마다** 같은 objId를 다시 보낸다(재접속·이동으로 시야가
   * 갱신될 때도 온다). 그래서 이미 있는 objId면 새로 만들지 않고 좌표·개수만 고친다 —
   * 매번 만들면 같은 아이템이 여러 장 겹쳐 쌓인다.
   *
   * 게이트웨이는 S_Trap도 같은 구조라 이 경로로 흘려보낸다(둘 다 opcode 3에
   * `looksLikeDropItem` 시그니처를 만족한다). 함정은 count가 0으로 오므로 개수
   * 표시만 빠지고, 주우려 해도 서버가 지면 인벤토리에서 찾지 못해 조용히 끝난다.
   */
  private handleGroundItemDrop(data: {
    objId: number;
    x: number;
    y: number;
    gfxId: number;
    count: number;
    name?: string;
  }): void {
    if (typeof data?.objId !== 'number') return;

    const tileX = this.clampTile(data.x);
    const tileY = this.clampTile(data.y);
    const count = typeof data.count === 'number' && data.count > 0 ? data.count : 1;
    // 서버 표시명은 `$키` 형태라 인벤토리와 같은 해석기를 거쳐야 바닥과 손 안의
    // 이름이 일치한다. 개수 꼬리는 여기서 떼어 둔다 — S_DropItem은 겹친 아이템의
    // 이름을 `"아데나 (1500)"`처럼 만들어 보내므로(서버 S_DropItem.java), 그대로
    // 두고 라벨에서 개수를 또 붙이면 "아데나 (1500) (1500)"이 된다.
    // 개수의 진실은 언제나 count 필드다.
    const name = stripCountSuffix(resolveItemName(data.name ?? '')) || '아이템';

    const existing = this.groundItems.get(data.objId);
    if (existing) {
      existing.tileX = tileX;
      existing.tileY = tileY;
      existing.count = count;
      existing.name = name;
      existing.label.setText(this.groundItemLabelText(name, count));
      this.placeGroundItem(existing);
      return;
    }

    const { x, y } = tileToWorld(tileX, tileY);
    // 바닥 아이템은 서 있는 것들보다 뒤에 그린다 — 같은 타일에 몹이 서 있으면
    // 몹이 앞이어야 한다. depthFor()가 주는 값에서 아주 조금만 뺀다.
    const depth = this.depthFor(tileX, tileY) - LABEL_DEPTH_BONUS;

    if (!this.textures.exists('__placeholder_entity__')) {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      this.textures.addCanvas('__placeholder_entity__', canvas);
    }
    const sprite = this.add.image(x, y, '__placeholder_entity__');
    sprite.setOrigin(0.5, 0.5);
    sprite.setDepth(depth);
    sprite.setDisplaySize(GROUND_ITEM_ICON_SIZE, GROUND_ITEM_ICON_SIZE);

    // 이름표는 엔티티와 같은 규약으로 평소에는 숨긴다 — 바닥에 여러 개가 놓이면
    // 인접 타일의 이름표끼리 겹쳐 어느 것이 무엇인지 오히려 읽을 수 없다.
    const label = this.add.text(x, y - GROUND_ITEM_LABEL_OFFSET, this.groundItemLabelText(name, count), {
      fontSize: GROUND_ITEM_LABEL_FONT,
      fontFamily: 'Arial, sans-serif',
      color: '#ffe9a0',
      backgroundColor: '#000000aa',
      padding: { x: 2, y: 0 },
    });
    label.setOrigin(0.5, 1);
    label.setDepth(depth + LABEL_DEPTH_BONUS);
    label.setVisible(false);

    sprite.setInteractive({ useHandCursor: true });
    sprite.on('pointerover', () => label.setVisible(true));
    sprite.on('pointerout', () => label.setVisible(false));

    this.registerWorldObjects(sprite, label);

    const groundItem: GroundItemSprite = {
      objId: data.objId,
      sprite,
      label,
      tileX,
      tileY,
      name,
      count,
      gfxId: data.gfxId ?? 0,
    };
    this.groundItems.set(data.objId, groundItem);

    // 아이콘은 인벤토리와 같은 카테고리 표를 쓴다. 이미 받아둔 텍스처면 즉시 붙고,
    // 아니면 받아 오는 동안 placeholder(1px)라 사실상 이름표만 보인다.
    const kind = groundItemIconKind(groundItem.gfxId, name);
    const key = itemIconTextureKey(kind);
    if (this.textures.exists(key)) {
      this.applyGroundItemTexture(groundItem, key);
    } else {
      void loadItemIcon(this, kind)
        .then((loadedKey) => {
          // 그 사이 주워졌을 수 있다 — 살아 있는 것만 갱신한다.
          if (this.groundItems.get(groundItem.objId) !== groundItem) return;
          this.applyGroundItemTexture(groundItem, loadedKey);
        })
        .catch(() => { /* 이름표만으로도 위치는 알 수 있다 */ });
    }
  }

  /** 바닥 아이템 이름표 문구. 겹친 것은 개수를 함께 보여준다. */
  private groundItemLabelText(name: string, count: number): string {
    return count > 1 ? `${name} (${count})` : name;
  }

  private applyGroundItemTexture(item: GroundItemSprite, textureKey: string): void {
    item.sprite.setTexture(textureKey);
    // setTexture()는 새 프레임 크기로 width/height를 되돌리므로 크기를 다시 잡는다.
    item.sprite.setDisplaySize(GROUND_ITEM_ICON_SIZE, GROUND_ITEM_ICON_SIZE);
    // 히트 영역은 처음 붙였던 1px placeholder 크기로 남아 있다 — 새 텍스처 크기에
    // 맞춰 주지 않으면 마우스를 올려도 이름표가 뜨지 않는다.
    GameScene.expandGroundHitArea(item.sprite);
  }

  /**
   * 바닥 아이템의 마우스 판정 영역을 텍스처 크기 + 여유로 잡는다.
   *
   * 엔티티의 {@link GameScene.syncHitArea}와 달리 여유를 준다 — 이유는
   * GROUND_ITEM_HIT_MARGIN 주석 참고. 좌표는 스케일 이전의 로컬 값이므로
   * `displayWidth`가 아니라 `width`를 기준으로 계산해야 한다.
   */
  private static expandGroundHitArea(sprite: Phaser.GameObjects.Image): void {
    const hitArea = sprite.input?.hitArea as Phaser.Geom.Rectangle | undefined;
    if (!hitArea || typeof hitArea.setTo !== 'function') return;
    const padX = sprite.width * GROUND_ITEM_HIT_MARGIN;
    const padY = sprite.height * GROUND_ITEM_HIT_MARGIN;
    hitArea.setTo(-padX, -padY, sprite.width + padX * 2, sprite.height + padY * 2);
  }

  /** 타일 좌표에서 화면 위치와 깊이를 다시 계산해 붙인다. */
  private placeGroundItem(item: GroundItemSprite): void {
    const { x, y } = tileToWorld(item.tileX, item.tileY);
    const depth = this.depthFor(item.tileX, item.tileY) - LABEL_DEPTH_BONUS;
    item.sprite.setPosition(x, y);
    item.sprite.setDepth(depth);
    item.label.setPosition(x, y - GROUND_ITEM_LABEL_OFFSET);
    item.label.setDepth(depth + LABEL_DEPTH_BONUS);
  }

  /** 바닥 아이템 하나를 화면에서 지운다. 없는 objId면 아무 일도 하지 않는다. */
  private removeGroundItem(objId: number): boolean {
    const item = this.groundItems.get(objId);
    if (!item) return false;
    item.sprite.destroy();
    item.label.destroy();
    this.groundItems.delete(objId);
    return true;
  }

  /**
   * 바닥 아이템을 전부 지운다 (맵 이동 / 세션 종료).
   *
   * 맵이 바뀌면 좌표 공간 자체가 달라지므로 옛 맵의 바닥 아이템을 남겨 두면
   * 새 맵의 엉뚱한 자리에 아이템이 떠 있게 된다.
   */
  private clearGroundItems(): void {
    this.groundItems.forEach((item) => {
      item.sprite.destroy();
      item.label.destroy();
    });
    this.groundItems.clear();
  }

  /**
   * 한 몹 계열의 스프라이트(8방향 × 5프레임)를 계열당 한 번만 미리 받아둔다.
   * 실패는 삼킨다 — 못 받은 것은 필요할 때 loadMobSprite()가 다시 요청한다.
   */
  private preloadMobFamilyOnce(family: MobFamily): void {
    if (this.preloadedMobFamilies.has(family)) return;
    this.preloadedMobFamilies.add(family);
    void preloadMobFamily(this, family).catch(() => { /* 최적화일 뿐이다 */ });
  }

  /**
   * 현재 entitySprites 상태를 미니맵 마커에 반영한다.
   * 스폰/제거/이동 모든 경로에서 호출해 마커 위치가 실시간으로 갱신되게 한다.
   */
  private syncMiniMapEntities(): void {
    // MiniMap은 타일 좌표를 받는다 (화면 픽셀이 아니다).
    this.miniMap.setEntities(
      Array.from(this.entitySprites.values()).map(e => ({
        x: e.tileX,
        y: e.tileY,
        type: e.type as 'npc' | 'monster' | 'player' | 'item' | 'portal',
        name: e.nameText.text
      }))
    );
  }

  /**
   * 엔티티 스프라이트 업데이트. (tx, ty)는 서버 타일 좌표다.
   *
   * 서버가 알려준 heading으로 방향을 바꾸고, 이동이 감지되면 walking animation을
   * 켠다 (frame 0→1→2→3→4 루프). 멈춘 지점이 같으면 그대로 idle(frame 0)로
   * 돌아간다.
   */
  private updateEntitySprite(objId: number, tx: number, ty: number, heading: number): void {
    const entity = this.entitySprites.get(objId);
    if (!entity) return;

    const newTileX = this.clampTile(tx);
    const newTileY = this.clampTile(ty);
    const prevTileX = entity.tileX;
    const prevTileY = entity.tileY;
    const moved = newTileX !== entity.tileX || newTileY !== entity.tileY;
    const newDirection = headingToDirection(heading);

    entity.tileX = newTileX;
    entity.tileY = newTileY;
    entity.direction = newDirection;
    entity.isWalking = moved;
    if (moved) {
      entity.walkFrameTimer = 0;
      // frame은 updateEntityWalkAnimations()에서 누적 시간 기준으로 advance.
    } else {
      // 멈췄으면 idle 프레임으로 복귀
      entity.frame = 0;
      entity.isWalking = false;
    }

    const { x, y } = tileToWorld(entity.tileX, entity.tileY);
    const depth = this.depthFor(entity.tileX, entity.tileY);

    // 인접 한 칸 이동이면 화면상 위치를 보간한다. 대각 이동은 타일 델타 합이 2다.
    // 그보다 크게 뛰면 텔레포트나 위치 재동기화이므로 즉시 옮긴다 —
    // 먼 거리를 미끄러지듯 보여주면 실제 위치와 한참 어긋난다.
    const tileStep = Math.abs(newTileX - prevTileX) + Math.abs(newTileY - prevTileY);

    if (moved && tileStep <= 2) {
      const now = this.time.now;
      const gap = entity.lastMoveAt > 0 ? now - entity.lastMoveAt : ENTITY_MOVE_DEFAULT_MS;
      entity.lastMoveAt = now;

      entity.move = {
        // 진행 중이던 보간이 있으면 그 지점에서 이어 간다 (튐 방지).
        fromX: entity.sprite.x,
        fromY: entity.sprite.y,
        toX: x,
        toY: y,
        elapsed: 0,
        duration: Phaser.Math.Clamp(gap, ENTITY_MOVE_MIN_MS, ENTITY_MOVE_MAX_MS),
      };
    } else if (moved) {
      // 텔레포트·위치 재동기화 — 미끄러뜨리지 않고 즉시 옮긴다.
      entity.lastMoveAt = this.time.now;
      entity.move = undefined;
      entity.sprite.setPosition(x, y);
      entity.shadow.setPosition(x, y);
      entity.nameText.setPosition(x, y - ENTITY_LABEL_OFFSET);
    }
    // 타일이 그대로면(같은 좌표 재통지 등) 화면을 건드리지 않는다.
    // 진행 중인 보간을 끊으면 그 자리에서 튄다.

    // 깊이는 논리 좌표 기준으로 즉시 반영한다 (그림이 따라오는 동안에도 정렬이 맞아야 한다).
    entity.sprite.setDepth(depth);
    entity.shadow.setDepth(depth + SHADOW_DEPTH_OFFSET);
    entity.nameText.setDepth(depth + LABEL_DEPTH_BONUS);

    if (entity.healthBar) {
      entity.healthBar.clear();
      entity.healthBar.setDepth(depth + LABEL_DEPTH_BONUS);
    }

    // 방향이 바뀌었으면 텍스처도 바뀐다 — 캐릭터/몹 어느 쪽이든 같은 창구를 쓴다.
    // (사망 연출 중에는 건드리지 않는다. 시체가 다시 일어서 보인다.)
    if (!entity.deathAnim) {
      this.applyEntityFrame(entity, entity.frame);
    }

    // 엔티티 이동 시 미니맵 마커 위치도 갱신
    this.syncMiniMapEntities();
  }

  /**
   * 엔티티 스프라이트 제거
   */
  private removeEntitySprite(objId: number): void {
    const entity = this.entitySprites.get(objId);
    if (!entity) return;

    // 이 대상에게 미뤄둔 공격이 있으면 함께 지운다 (updatePendingStrike도 막지만,
    // 사라진 objId를 들고 있을 이유가 없다).
    if (this.pendingStrikeTargetId === objId) this.clearPendingStrike();

    entity.sprite.destroy();
    entity.shadow.destroy();
    entity.nameText.destroy();
    entity.healthBar?.destroy();
    // 폴백 텍스트도 함께 지운다 — 남겨두면 몬스터가 사라진 자리에 'M'만 떠 있게 된다.
    entity.fallbackText.destroy();
    this.entitySprites.delete(objId);

    // 미니맵 업데이트
    this.syncMiniMapEntities();
  }

  /**
   * 클릭 처리 (공격 또는 이동).
   * 포인터의 화면 좌표를 그대로 쓰면 안 된다 — 카메라 스크롤이 반영된 월드 좌표를
   * 얻은 뒤 타일 좌표로 변환해야 한다.
   */
  private handleAttack(pointer: Phaser.Input.Pointer): void {
    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const clickTile = worldToTile(world.x, world.y);
    const targetTileX = this.clampTile(clickTile.tx);
    const targetTileY = this.clampTile(clickTile.ty);

    // 자기가 서 있는 타일을 클릭한 경우는 무시
    if (targetTileX === this.playerTileX && targetTileY === this.playerTileY) return;

    // 방향은 화면 각도가 아니라 타일 델타로 계산해야 서버와 일치한다.
    this.currentHeading = headingFromTileDelta(
      targetTileX - this.playerTileX,
      targetTileY - this.playerTileY
    );

    // 바닥 아이템을 **직접** 맞혔으면 줍는 것이 우선이다. 아이콘을 빗맞힌 경우는
    // 엔티티 판정 뒤로 미룬다 — 몹 발밑에 아이템이 있을 때 클릭이 공격 대신
    // 줍기로 새면 전투 중에 위험하다.
    const directGroundItem = this.pickGroundItemDirect(world);
    if (directGroundItem) {
      this.clearChase();
      this.beginPickup(directGroundItem);
      return;
    }

    // 클릭 대상 찾기 (공격/대상 선택이 같은 판정을 쓴다 — pickEntityAt).
    const targetEntity = this.pickEntityAt(world, targetTileX, targetTileY);

    if (targetEntity) {
      const target = targetEntity;

      if (target.type === 'npc') {
        // NPC는 치지 않고 말을 건다 (C_NpcTalk). 멀면 다가간 뒤 건다.
        this.beginNpcTalk(target);
        return;
      }

      if (target.type === 'monster') {
        this.beginAttack(target);
        return;
      }
      // 다른 플레이어 클릭은 공격하지 않고 그 지점으로 이동한다 (아래 폴스루).
    }

    // 빈 땅을 클릭했으면 쫓던 것을 그만둔다 — 사용자가 다른 곳으로 가겠다는 뜻이다.
    this.clearChase();

    // 엔티티가 없는 자리라면 그 타일 근처의 바닥 아이템을 줍는 것으로 본다
    // (작은 아이콘을 빗맞힌 클릭 구제 — pickGroundItemNear 주석 참고).
    const nearbyGroundItem = this.pickGroundItemNear(targetTileX, targetTileY);
    if (nearbyGroundItem) {
      this.beginPickup(nearbyGroundItem);
      return;
    }

    // 사망 중에는 목적지 자체를 받지 않는다. handleMovementInput만 막으면 클릭이
    // 목적지로 쌓여 있다가 부활하는 순간 엉뚱한 곳으로 걸어간다.
    if (gameState.isDead()) return;

    // 클릭한 타일을 목적지로 삼아 한 타일씩 자동으로 걸어간다 (handleMovementInput에서 처리).
    // 여기서 벽을 우회하는 경로를 미리 만들어 둔다 — 직선으로만 가면 첫 걸음이 막히는
    // 순간 멈춰서 벽 뒤로는 영영 갈 수 없다.
    // 연결이 끊긴 상태에서도 로컬 클릭 이동은 가능해야 한다.
    this.setMoveTarget(targetTileX, targetTileY, true);
  }

  /**
   * 클릭 지점의 엔티티를 고른다 (공격 대상 / 아이템 대상 공용).
   *
   * 1순위는 **그림을 직접 맞혔는지**다. 아이소메트릭에서 스프라이트(48px)는 타일
   * (높이 24px) 위로 솟아 있어서, 몹의 몸통이나 머리를 클릭하면 발밑 타일보다
   * 1~2칸 위 타일이 계산된다. 타일 좌표만으로 판정하면 화면에서는 분명히 몹을
   * 눌렀는데 대상으로 잡히지 않고 그 자리로 걸어가 버린다 — 활처럼 떨어져서
   * 공격해야 하는 경우에 특히 드러난다.
   * 그림을 빗맞혔으면 클릭한 타일 근처의 엔티티로 폴백한다.
   *
   * @param world       카메라 스크롤이 반영된 월드 좌표 (화면 좌표가 아니다)
   * @param tileX       클릭 지점의 타일 좌표
   * @param tileY       클릭 지점의 타일 좌표
   * @param includeDead 사망 연출 중인 대상도 후보에 넣을지 (부활 스크롤 use_type 8)
   */
  private pickEntityAt(
    world: { x: number; y: number },
    tileX: number,
    tileY: number,
    includeDead: boolean = false
  ): EntitySprite | null {
    let picked: EntitySprite | null = null;
    let bestDepth = -Infinity;

    this.entitySprites.forEach((entity) => {
      if (!entity.sprite.visible) return;
      if (entity.deathAnim && !includeDead) return; // 이미 죽어 쓰러진 것은 대상이 아니다
      if (!entity.sprite.getBounds().contains(world.x, world.y)) return;

      // 스프라이트가 겹칠 때는 앞쪽(depth가 큰) 것을 고른다.
      if (entity.sprite.depth > bestDepth) {
        bestDepth = entity.sprite.depth;
        picked = entity;
      }
    });

    if (!picked) {
      let minDist = Infinity;
      this.entitySprites.forEach((entity) => {
        if (entity.deathAnim && !includeDead) return;
        const dist = tileDistance(entity.tileX, entity.tileY, tileX, tileY);
        if (dist < minDist && dist <= CLICK_PICK_RANGE) {
          minDist = dist;
          picked = entity;
        }
      });
    }

    // forEach 콜백 안의 대입은 TS의 흐름 분석이 추적하지 못해 여기서 null로 좁혀진다.
    return picked as EntitySprite | null;
  }

  /** 지금 든 무기가 닿는 거리(타일). */
  private currentReach(): number {
    return this.playerWeapon === 'bow' ? RANGED_REACH_TILES : MELEE_REACH_TILES;
  }

  /**
   * 몹을 클릭했을 때의 처리.
   *
   * 닿는 거리면 그 자리에서 친다. 멀면 쫓아간다 — 근접 무기로 떨어진 적을
   * 클릭했는데 제자리에서 허공을 베면 원거리 무기처럼 보인다. 쫓는 동안에도
   * 대상은 계속 움직이므로 목적지 갱신은 update 루프가 이어받는다.
   */
  private beginAttack(target: EntitySprite): void {
    // 다른 대상을 새로 지목했다면 이전 예약은 버린다 (예약 자리는 하나뿐이다).
    if (this.pendingStrikeTargetId !== target.objId) this.clearPendingStrike();

    // 사거리는 서버와 같은 직선 거리(max(|dx|,|dy|))로 잰다. 맨해튼 거리로 재면
    // 대각선으로 딱 붙은 적이 2로 나와 근접 사거리 1을 넘고, 적이 서 있는 칸으로
    // 걸어가려다 경로를 못 찾아 붙어서도 영영 못 친다. tileLineDistance 주석 참고.
    const distance = tileLineDistance(
      this.playerTileX,
      this.playerTileY,
      target.tileX,
      target.tileY,
    );

    if (distance <= this.currentReach()) {
      this.clearChase();
      this.clearMoveTarget();
      this.strike(target);
      return;
    }

    // 사망 중에는 쫓지도 않는다. 목적지만 쌓아 두면 부활하는 순간 달려나간다.
    if (gameState.isDead()) return;

    this.chaseIntent = 'attack';
    this.attackTargetId = target.objId;
    this.chasePathGoal = { tx: target.tileX, ty: target.tileY };
    this.setMoveTarget(target.tileX, target.tileY, false, CHASE_MAX_PATH_NODES);
  }

  // ==================== NPC 대화 요청 (C_NpcTalk) ====================

  /**
   * NPC를 클릭했을 때의 처리. 몹을 클릭했을 때(beginAttack)와 같은 구조다.
   *
   * 붙어 있으면 그 자리에서 말을 걸고, 멀면 다가간 뒤 건다 — 추격 자체는
   * 공격과 같은 통로(attackTargetId + updateAttackChase)를 쓰고 "닿았을 때
   * 무엇을 하는가"만 chaseIntent로 가른다. 거리를 두는 이유는
   * NPC_TALK_REACH_TILES 주석 참고 (대화창의 버튼이 서버에서 무시된다).
   */
  private beginNpcTalk(target: EntitySprite): void {
    // NPC에게 말을 걸기로 했으면 미뤄둔 공격은 없던 일로 한다.
    this.clearPendingStrike();

    const distance = tileLineDistance(
      this.playerTileX,
      this.playerTileY,
      target.tileX,
      target.tileY,
    );

    if (distance <= NPC_TALK_REACH_TILES) {
      this.clearChase();
      this.clearMoveTarget();
      this.talkToNpc(target);
      return;
    }

    // 사망 중에는 다가가지도 않는다 (공격 쪽과 같은 이유).
    if (gameState.isDead()) return;

    this.chaseIntent = 'talk';
    this.attackTargetId = target.objId;
    this.chasePathGoal = { tx: target.tileX, ty: target.tileY };
    this.setMoveTarget(target.tileX, target.tileY, false, CHASE_MAX_PATH_NODES);
  }

  /** 실제로 말을 건다 — 상대 쪽으로 돌아선 뒤 C_NpcTalk를 보낸다. */
  private talkToNpc(target: EntitySprite): void {
    this.currentHeading = headingFromTileDelta(
      target.tileX - this.playerTileX,
      target.tileY - this.playerTileY,
    );
    // 대상을 바라보게 한다 (공격과 같은 창구 — 이름만 attack일 뿐 방향만 맞춘다).
    this.faceAttackTarget();

    if (!this.connectionManager || !this.connectionManager.isConnected()) {
      this.chatBox.addSystemMessage('서버와 연결되어 있지 않아 말을 걸 수 없습니다.');
      return;
    }

    this.connectionManager.getPacketHandler().sendNpcTalk(target.objId);
    this.startNpcTalkWatch(target.objId, target.nameText.text);
  }

  /**
   * 응답 침묵 감시를 건다. 대화 데이터가 없는 NPC는 서버가 아무 패킷도 보내지
   * 않으므로(NPC_TALK_SILENCE_MS 주석 참고), 조용히 끝나면 그 사실을 알린다.
   */
  private startNpcTalkWatch(objId: number, name: string): void {
    this.clearNpcTalkWatch();
    this.npcTalkPending = {
      objId,
      name,
      timer: this.time.delayedCall(NPC_TALK_SILENCE_MS, () => {
        const pending = this.npcTalkPending;
        this.npcTalkPending = null;
        if (!pending) return;
        this.chatBox.addSystemMessage(`${pending.name}은(는) 아무 대답이 없습니다.`);
      }),
    };
  }

  /** 침묵 감시를 푼다 (응답이 왔거나 다른 대상에게 다시 말을 걸었을 때). */
  private clearNpcTalkWatch(): void {
    if (!this.npcTalkPending) return;
    this.npcTalkPending.timer.remove(false);
    this.npcTalkPending = null;
  }

  /**
   * 서버 `getCurrentWeapon()`이 지금 무엇일지에 대한 후보 무기코드 전부.
   *
   * 두 출처를 합집합으로 본다. 어느 쪽도 단독으로는 믿을 수 없기 때문이다.
   *
   *  - `gameState.getPlayerWeapon()` — 정확하지만 **거의 오지 않는다**.
   *    출처가 opcode 113뿐이고 서버는 장착/해제 때 그것을 보내지 않는다
   *    (WEAPON_CODE_CANDIDATES 주석 참조). 텔레포트 뒤에나 한 번 온다.
   *  - 인벤토리 표시명(`equippedWeaponType`) — 접속 직후부터 있지만 근접 무기가
   *    전부 'sword' 한 갈래로 접혀 있어 정확한 코드를 모른다.
   *
   * 값이 오래됐을 수 있다는 점도 합집합이어야 하는 이유다. 예를 들어 맨손으로
   * 텔레포트해 113(0)을 받은 뒤 활을 들면 113은 다시 오지 않으므로, 인벤토리를
   * 함께 보지 않으면 영영 맨손 속도로 계산한다.
   */
  private attackActCandidates(): number[] {
    const codes = new Set<number>(WEAPON_CODE_CANDIDATES[this.playerWeapon] ?? [0]);
    // 113으로 받은 값이 있으면(0 = 맨손이거나 아직 못 받았거나) 후보에 더한다.
    const known = gameState.getPlayerWeapon();
    if (known > 0) codes.add(known);
    // 공격 act_id = 무기코드 + 1.
    return [...codes].map((code) => code + 1);
  }

  /**
   * 서버가 요구하는 공격 최소 간격(ms).
   *
   * `AcceleratorChecker.getRightInterval(ATTACK)`이 쓰는 값을 그대로 계산한 뒤
   * (`SprTable.getAttackSpeed(gfx, weapon + 1)`), 판정식을 뒤집어 통과 하한을 구하고
   * 지터 여유를 곱한다. 상수들의 근거는 SERVER_CHECK_STRICTNESS /
   * ATTACK_GATE_MARGIN 주석 참조.
   *
   * 무기코드를 하나로 특정할 수 없으므로 **후보 중 가장 느린 값**을 쓴다. 활은
   * 맨손의 두 배 가까이 느려서(spr 61: 800 → 1440) 여기서 틀리면 곧바로
   * 강제 접속 종료다.
   */
  private attackGateMs(): number {
    const gfxId = gameState.getPlayerGfxId();
    let base: number | null = null;
    if (gfxId !== null) {
      for (const actId of this.attackActCandidates()) {
        const speed = attackSpeedOf(gfxId, actId);
        // 후보 하나라도 모르면 표 전체의 최저 속도로 간다 (아래 폴백).
        if (speed === null) { base = null; break; }
        if (base === null || speed > base) base = speed;
      }
    }
    // 모르면 표에서 가장 느린 값으로 간다 — 빠른 쪽으로 폴백하면 강제 접속 종료다.
    const needed = (base ?? SLOWEST_ATTACK_MS) / SERVER_CHECK_STRICTNESS;
    return needed * ATTACK_GATE_MARGIN;
  }

  /** 지금 공격 패킷을 보내도 서버 판정을 통과하는가. */
  private canSendAttackNow(): boolean {
    return performance.now() - this.lastAttackSentAt >= this.attackGateMs();
  }

  /**
   * 한 대 친다 — 단, **서버가 허락하는 속도까지만**.
   *
   * 게이트가 닫혀 있으면 그 공격을 버리지 않고 한 칸짜리 자리에 예약해 두고
   * (`pendingStrikeTargetId`), 열리는 순간 `updatePendingStrike()`가 쏜다.
   * 그래야 몹을 연타했을 때 "허용되는 최대 속도로 자동 연타"되는 느낌이 난다.
   *
   * 막힌 동안에는 **모션을 재생하지 않는다.** 클릭마다 베는 시늉이 나오면
   * 화면상 타격 횟수와 서버가 실제로 처리한 횟수가 어긋나 보인다.
   * 대신 방향만은 즉시 돌린다 — 대상을 등지고 서 있는 편이 더 어색하다.
   */
  private strike(target: EntitySprite): void {
    this.currentHeading = headingFromTileDelta(
      target.tileX - this.playerTileX,
      target.tileY - this.playerTileY,
    );

    if (!this.canSendAttackNow()) {
      this.faceAttackTarget();
      this.pendingStrikeTargetId = target.objId;
      this.attackGateDeferrals++;
      logger.debug(
        `attack gate: ${Math.round(this.attackGateMs() - (performance.now() - this.lastAttackSentAt))}ms 대기`,
        'game',
      );
      return;
    }

    this.sendStrike(target);
  }

  /** 게이트를 통과한 공격을 실제로 내보낸다 (모션 + 패킷 + 송신 시각 갱신). */
  private sendStrike(target: EntitySprite): void {
    this.playAttackAnimation(target);

    if (this.connectionManager && this.connectionManager.isConnected()) {
      this.connectionManager.send({
        type: 'attack',
        payload: {
          targetId: target.objId,
          // 서버가 사거리를 판정할 대상 타일 좌표 (C_Attack은 좌표를 함께 읽는다).
          x: target.tileX,
          y: target.tileY,
        },
      });
    }

    // 연결이 끊겨 실제로 못 보냈더라도 시각은 갱신한다. 끊긴 동안 눌러댄 만큼
    // 재접속 직후 몰아서 나가면 그 자리에서 위반이 된다.
    this.lastAttackSentAt = performance.now();
  }

  /**
   * 게이트에 막혀 미뤄둔 공격을 쏠 때가 됐는지 매 프레임 확인한다.
   *
   * 예약해 둔 사이에 상황이 바뀌었을 수 있으므로 **쏘기 직전에 다시 검사한다** —
   * 대상이 아직 있는지, 죽지 않았는지, 사거리 안인지, 내가 살아 있는지.
   */
  private updatePendingStrike(): void {
    if (this.pendingStrikeTargetId === null) return;

    const target = this.entitySprites.get(this.pendingStrikeTargetId);
    if (!target || target.deathAnim || !target.sprite.visible) {
      // 대상이 사라졌거나 쓰러졌다 — 허공을 치지 않는다.
      this.pendingStrikeTargetId = null;
      return;
    }

    if (gameState.isDead()) {
      this.pendingStrikeTargetId = null;
      return;
    }

    if (!this.canSendAttackNow()) return;

    const distance = tileLineDistance(
      this.playerTileX,
      this.playerTileY,
      target.tileX,
      target.tileY,
    );
    if (distance > this.currentReach()) {
      // 예약해 둔 사이에 서로 멀어졌다. 추격 중이라면 다시 닿는 순간
      // updateAttackChase()가 strike()를 불러 새로 예약한다.
      this.pendingStrikeTargetId = null;
      return;
    }

    this.pendingStrikeTargetId = null;
    this.currentHeading = headingFromTileDelta(
      target.tileX - this.playerTileX,
      target.tileY - this.playerTileY,
    );
    this.sendStrike(target);
  }

  /** 미뤄둔 공격을 취소한다 (대상 소멸·사망·씬 종료). */
  private clearPendingStrike(): void {
    this.pendingStrikeTargetId = null;
  }

  /**
   * 쫓아가던 대상에 닿았는지 매 프레임 확인한다.
   *
   * 닿으면 걸음을 멈추고 친다. 대상이 도망가면 목적지를 그쪽으로 계속 옮긴다.
   * 대상이 죽거나 사라지면 추격 자체를 그만둔다 — 시체가 있던 자리로 계속
   * 걸어가면 안 된다.
   */
  private updateAttackChase(): void {
    if (this.attackTargetId === null) return;

    const target = this.entitySprites.get(this.attackTargetId);
    if (!target || target.deathAnim || !target.sprite.visible) {
      this.clearChase();
      this.clearMoveTarget();
      return;
    }

    if (!gameState.canMove()) return;

    const distance = tileLineDistance(
      this.playerTileX,
      this.playerTileY,
      target.tileX,
      target.tileY,
    );

    // 닿았다고 볼 거리는 목적에 따라 다르다 — 대화는 무기 사거리와 무관하다.
    const reach = this.chaseIntent === 'talk' ? NPC_TALK_REACH_TILES : this.currentReach();

    if (distance > reach) {
      // 아직 멀다 — 움직이는 대상을 따라 목적지를 갱신한다.
      //
      // 갱신은 대상이 **타일을 실제로 옮겼을 때만** 한다. 매 프레임 경로를 다시 만들면
      // 60fps × 수백~수천 노드가 되어 메인 스레드가 죽는다. 대상이 제자리에 있는 동안은
      // 이미 만들어 둔 경로를 그대로 소비한다.
      if (
        this.chasePathGoal === null ||
        this.chasePathGoal.tx !== target.tileX ||
        this.chasePathGoal.ty !== target.tileY
      ) {
        // 탐색 실패로 목적지가 비워져도 이 기준값은 남긴다 — 대상이 다시 움직이기
        // 전까지 같은 실패를 되풀이하지 않기 위함이다.
        this.chasePathGoal = { tx: target.tileX, ty: target.tileY };
        this.setMoveTarget(target.tileX, target.tileY, false, CHASE_MAX_PATH_NODES);
      }
      return;
    }

    // 닿았다. 걸음을 멈추고 친다(또는 말을 건다). (진행 중인 한 스텝은 그대로
    // 끝나게 둔다 — 타일 사이에서 끊으면 위치가 서버와 어긋난다.)
    this.clearMoveTarget();
    this.clearChase();

    if (this.chaseIntent === 'talk') {
      this.chaseIntent = 'attack';
      this.talkToNpc(target);
      return;
    }

    this.strike(target);
  }

  /**
   * 공격 모션을 재생한다.
   *
   * 손에 든 무기로 근접과 원거리가 갈린다. 근접은 대상에게 파고들며 베고,
   * 원거리는 제자리에서 반동을 받으며 화살을 날린다. 맞는 쪽 반응은 근접이면
   * 즉시, 원거리면 화살이 닿는 순간에 나온다 — 그래야 화살이 도착하기도 전에
   * 몹이 먼저 움찔하는 어긋남이 없다.
   */
  private playAttackAnimation(target: EntitySprite): void {
    this.faceAttackTarget();

    const from = tileToWorld(this.playerTileX, this.playerTileY);
    // 발밑이 아니라 몸통 높이를 겨눈다. 스프라이트는 발이 타일 중심에 오도록
    // 그려지므로 그대로 쓰면 궤적과 화살이 바닥을 긁는다.
    const originY = from.y - PLAYER_BODY_HEIGHT;
    const angle = Math.atan2(target.sprite.y - originY, target.sprite.x - from.x);

    if (this.playerWeapon === 'bow') {
      this.playRangedAttack(target, from.x, originY, angle);
    } else {
      this.playMeleeAttack(target, from.x, originY, angle);
    }
  }

  /**
   * 공격하는 순간 대상 쪽을 바라보게 한다. 방향은 handleAttack이 타일 델타로
   * 이미 정해 두었다(currentHeading). 등을 돌린 채 베는 그림이 나오지 않게 한다.
   */
  private faceAttackTarget(): void {
    const direction = headingToDirection(this.currentHeading);
    if (direction === this.playerDirection) return;

    this.playerDirection = direction;
    this.applyPlayerTexture();
  }

  /** 근접: 대상 쪽으로 파고들며 베기 궤적을 남긴다. */
  private playMeleeAttack(
    target: EntitySprite,
    originX: number,
    originY: number,
    angle: number,
  ): void {
    this.lungeBody(
      Math.cos(angle) * MELEE_LUNGE_DISTANCE,
      Math.sin(angle) * MELEE_LUNGE_DISTANCE,
    );
    this.spawnSlashArc(originX, originY, angle);
    this.playHitReaction(target, angle);
  }

  /** 원거리: 반동으로 물러나며 화살을 날린다. */
  private playRangedAttack(
    target: EntitySprite,
    originX: number,
    originY: number,
    angle: number,
  ): void {
    // 반동은 쏘는 방향의 반대다.
    this.lungeBody(
      -Math.cos(angle) * RANGED_RECOIL_DISTANCE,
      -Math.sin(angle) * RANGED_RECOIL_DISTANCE,
    );
    this.spawnArrow(target, originX, originY, angle);
  }

  /**
   * 몸통에 공격 몸짓을 준다. 스프라이트 좌표가 아니라 오프셋을 tween한다 —
   * renderPlayerAt()이 매 프레임 위치를 다시 쓰기 때문에 좌표를 직접 움직이면
   * 다음 프레임에 지워진다.
   */
  private lungeBody(dx: number, dy: number): void {
    // 연타로 몸짓이 겹치면 오프셋이 0으로 돌아오지 못하고 캐릭터가 밀린 채로
    // 남는다. 진행 중인 것은 버리고 원위치에서 새로 시작한다.
    // (remove()는 onComplete를 부르지 않으므로 여기서 직접 되돌린다.)
    this.attackLungeTween?.remove();
    this.attackLunge.x = 0;
    this.attackLunge.y = 0;

    this.attackLungeTween = this.tweens.add({
      targets: this.attackLunge,
      x: dx,
      y: dy,
      duration: ATTACK_LUNGE_MS,
      ease: 'Quad.easeOut',
      yoyo: true,
      onComplete: () => {
        this.attackLunge.x = 0;
        this.attackLunge.y = 0;
        this.attackLungeTween = null;
      },
    });
  }

  /** 베기 궤적. 대상 방향으로 벌어진 호가 휘둘러지며 흐려진다. 색은 무기별. */
  private spawnSlashArc(originX: number, originY: number, angle: number): void {
    const color = SLASH_COLORS[this.playerWeapon] ?? 0xf2f7ff;
    const arc = this.add.graphics();
    arc.lineStyle(3, color, 0.95);
    arc.beginPath();
    arc.arc(0, 0, SLASH_RADIUS, -SLASH_SPREAD, SLASH_SPREAD);
    arc.strokePath();
    // 안쪽에 흐린 호를 한 겹 더 — 선 하나일 때보다 궤적에 두께감이 생긴다.
    arc.lineStyle(2, color, 0.4);
    arc.beginPath();
    arc.arc(0, 0, SLASH_RADIUS - 7, -SLASH_SPREAD * 0.8, SLASH_SPREAD * 0.8);
    arc.strokePath();

    arc.setPosition(originX, originY);
    arc.setRotation(angle - SLASH_SWING / 2);
    // 자기 몸보다 앞에 그려야 궤적이 캐릭터에 가려지지 않는다.
    arc.setDepth(this.depthFor(this.playerTileX, this.playerTileY) + LABEL_DEPTH_BONUS);
    this.registerWorldObjects(arc);

    this.tweens.add({
      targets: arc,
      rotation: angle + SLASH_SWING / 2,
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: SLASH_FADE_MS,
      ease: 'Quad.easeOut',
      onComplete: () => arc.destroy(),
    });
  }

  /** 화살. 대상까지 날아가 닿는 순간 피격 반응을 낸다. */
  private spawnArrow(
    target: EntitySprite,
    originX: number,
    originY: number,
    angle: number,
  ): void {
    const arrow = this.add.rectangle(originX, originY, ARROW_LENGTH, 2, 0xffe9a8);
    arrow.setRotation(angle);
    arrow.setDepth(this.depthFor(this.playerTileX, this.playerTileY) + LABEL_DEPTH_BONUS);
    this.registerWorldObjects(arrow);

    const distance = tileLineDistance(
      this.playerTileX,
      this.playerTileY,
      target.tileX,
      target.tileY,
    );
    const flightMs = Math.max(
      ARROW_MIN_MS,
      Math.min(ARROW_MAX_MS, distance * ARROW_MS_PER_TILE),
    );

    this.tweens.add({
      targets: arrow,
      // 목표는 쏘는 시점의 그림 위치다. 날아가는 동안 몹이 움직여도 화살이
      // 따라붙지는 않는다 — 유도 화살처럼 보이는 편이 더 어색하다.
      x: target.sprite.x,
      y: target.sprite.y - PLAYER_BODY_HEIGHT,
      duration: flightMs,
      ease: 'Linear',
      onComplete: () => {
        arrow.destroy();
        // 화살이 날아가는 사이에 몹이 죽어 사라졌을 수 있다.
        if (target.sprite.active) this.playHitReaction(target, angle);
      },
    });
  }

  /**
   * 맞은 쪽 반응. 타격 지점에 파편이 튀고, 타격 방향으로 밀렸다 돌아온다.
   * angle을 모르면(공격자 위치 불명) 무작위 방향으로 흔들리기만 한다.
   *
   * 내 공격 전용이다 — 파편 색을 내 무기에서 뽑는다. 남이 때린 반응은
   * showAttackFeedback()이 spawnHitSpark를 직접 부른다.
   */
  private playHitReaction(target: EntitySprite, angle?: number): void {
    const originalX = target.sprite.x;
    const originalY = target.sprite.y;

    this.spawnHitSpark(
      originalX,
      originalY - PLAYER_BODY_HEIGHT,
      angle ?? Math.random() * Math.PI * 2,
      SLASH_COLORS[this.playerWeapon] ?? HIT_SPARK_DEFAULT_COLOR,
      target.tileX,
      target.tileY,
    );

    const dx = angle !== undefined
      ? Math.cos(angle) * HIT_KNOCKBACK_PX
      : (Math.random() - 0.5) * 10;
    const dy = angle !== undefined
      ? Math.sin(angle) * HIT_KNOCKBACK_PX
      : (Math.random() - 0.5) * 10;

    this.tweens.add({
      targets: target.sprite,
      x: originalX + dx,
      y: originalY + dy,
      alpha: 0.55,
      duration: 50,
      yoyo: true,
      onComplete: () => {
        target.sprite.setPosition(originalX, originalY);
        target.sprite.setAlpha(1);
      }
    });
  }

  /**
   * 타격 지점 이펙트 — 섬광 링이 퍼지고 파편이 타격이 뚫고 나가는 방향
   * 앞쪽 반원으로 흩어진다. 좌표는 몸통 높이 기준으로 넘겨받는다.
   */
  private spawnHitSpark(
    x: number,
    y: number,
    angle: number,
    color: number,
    tileX: number,
    tileY: number,
  ): void {
    const depth = this.depthFor(tileX, tileY) + LABEL_DEPTH_BONUS;

    const flash = this.add.circle(x, y, HIT_FLASH_RADIUS, 0xffffff, 0.85);
    flash.setScale(0.3);
    flash.setDepth(depth);
    this.registerWorldObjects(flash);
    this.tweens.add({
      targets: flash,
      scale: 1,
      alpha: 0,
      duration: HIT_FLASH_MS,
      ease: 'Quad.easeOut',
      onComplete: () => flash.destroy(),
    });

    for (let i = 0; i < HIT_SPARK_COUNT; i++) {
      const spread = angle + (Math.random() - 0.5) * Math.PI * 0.9;
      const dist = HIT_SPARK_DISTANCE * (0.6 + Math.random() * 0.8);
      const shard = this.add.rectangle(x, y, 4, 2, color);
      shard.setRotation(spread);
      shard.setDepth(depth);
      this.registerWorldObjects(shard);
      this.tweens.add({
        targets: shard,
        x: x + Math.cos(spread) * dist,
        // 살짝 위로 뜨며 흩어져야 파편이 바닥에 깔리는 느낌이 없다.
        y: y + Math.sin(spread) * dist - 5,
        alpha: 0,
        scale: 0.4,
        duration: HIT_SPARK_MS,
        ease: 'Cubic.easeOut',
        onComplete: () => shard.destroy(),
      });
    }
  }

  /**
   * 남(몹·타인) 공격자의 몸짓 — 대상 쪽으로 짧게 내밀었다 돌아온다.
   *
   * 내 캐릭터는 renderPlayerAt()이 매 프레임 위치를 다시 쓰므로 이 방식이
   * 통하지 않고 attackLunge 오프셋을 쓴다. 이 함수는 남의 스프라이트 전용이다.
   */
  private lungeEntitySprite(entity: EntitySprite, angle: number): void {
    const originalX = entity.sprite.x;
    const originalY = entity.sprite.y;
    this.tweens.add({
      targets: entity.sprite,
      x: originalX + Math.cos(angle) * ENEMY_LUNGE_PX,
      y: originalY + Math.sin(angle) * ENEMY_LUNGE_PX,
      duration: ENEMY_LUNGE_MS,
      ease: 'Quad.easeOut',
      yoyo: true,
      onComplete: () => entity.sprite.setPosition(originalX, originalY),
    });
  }

  /**
   * 채팅 메시지 전송
   */
  private sendChat(message: string, channel: 'all' | 'shout' | 'world' | 'clan' | 'party' | 'trade' | 'system' | 'tell'): void {
    // 게이트웨이가 'tell'(귓속말)을 인식하지 못해 일반챗으로 강등된다 —
    // ChatBox에서 이미 차단하지만, 다른 경로로 들어와도 'all'로 폴백한다.
    if (channel === 'tell') {
      this.chatBox.addSystemMessage('귓속말은 아직 지원되지 않아 일반 채팅으로 전송됩니다.');
      channel = 'all';
    }

    // 서버는 '.'으로 시작하는 일반 채팅을 대화가 아니라 GM 명령으로 처리하고
    // (C_Chat → GMCommands), 주변에 퍼뜨리지 않는다. 결과는 시스템 메시지로 돌아온다.
    const isCommand = channel === 'all' && message.startsWith('.') && message.length > 1;

    // 채팅 기록에 추가 (명령도 위/아래 키로 다시 꺼내 쓸 수 있어야 한다)
    this.chatHistory.unshift(message);
    if (this.chatHistory.length > 50) {
      this.chatHistory.pop();
    }

    // 로컬에 먼저 표시.
    // 명령은 남의 눈에 보이지도, 서버가 되돌려주지도 않으므로 대화처럼 찍지 않는다.
    if (!isCommand) {
      this.chatBox.addMessage({
        type: channel,
        sender: storageManager.loadCredentials()?.charName || 'Player',
        message: message,
        timestamp: Date.now()
      });
    }

    // 서버로 전송
    if (this.connectionManager && this.connectionManager.isConnected()) {
      this.connectionManager.send({
        type: 'chat',
        payload: {
          message: message,
          channel: channel
        }
      });
    }
  }

  // ==================== 아이템 사용 / 대상 선택 상태 머신 ====================

  /**
   * use_type → 무엇을 골라야 하는지.
   *
   * 서버 `C_UseItem` 생성자의 if/else 사슬과 **1:1로 대응**한다. 값을 바꾸기 전에
   * 반드시 원본(src/jp/l1j/server/packets/client/C_UseItem.java)을 다시 확인할 것 —
   * 여기가 어긋나면 패킷 길이가 맞지 않아 서버가 조용히 아무 일도 하지 않는다.
   *
   * useType이 undefined(게이트웨이가 알려주지 않은 경우)면 'none'이다. 그래야
   * 지금까지 동작하던 즉시 전송 경로가 그대로 유지된다.
   */
  private static targetKindFor(useType: number | undefined): UseTargetKind {
    switch (useType) {
      case 7:  // 確認スクロール(identify)
      case 14: // choice
      case 26: // 武器強化スクロール(dai)
      case 27: // 防具強化スクロール(zel)
        return 'inventory';
      case 8:  // 復活スクロール(res)
      case 30: // スペルスクロール(spell_buff)
        return 'entity';
      case 5:  // spell_long
      case 17: // spell_short
      case 39: // spell_point
      case 42: // 魔法の釣り竿(fishing_rod)
        return 'ground';
      case 16: // 変身スクロール(sosc)
        return 'text';
      case 6:  // テレポートスクロール(ntele)
      case 29: // 祝福されたテレポートスクロール(btele)
        return 'bookmark';
      case 28: // ブランクスクロール(blank) — 스킬 목록 UI가 없다
        return 'unsupported';
      default:
        return 'none';
    }
  }

  /**
   * 아이템 사용 (인벤토리 더블클릭 / 퀵슬롯 등의 진입점)
   *
   * 서버 C_UseItem은 **아이템 자신의 use_type**으로 itemObjId 뒤에 읽을 바이트 수를
   * 정한다. 그 형태를 맞추지 못하면 서버가 버퍼 끝을 넘어 읽다 조용히 끝나므로
   * (확인/강화 스크롤 무반응의 원인), 인벤토리가 가진 useType을 함께 보낸다.
   *
   * 다만 길이만 맞춘다고 끝이 아니다 — 대상 objId/좌표/문자열이 0이나 빈 값이면
   * 서버가 대상을 찾지 못해 결과적으로 또 무반응이다. 그래서 대상이 필요한
   * use_type은 곧바로 보내지 않고 **대상 선택 모드**로 들어간다.
   */
  private useItem(itemId: number): void {
    if (!this.connectionManager || !this.connectionManager.isConnected()) return;

    // 다른 아이템 사용을 시작하면 진행 중이던 대상 선택은 조용히 접는다.
    // (안내 문구 없이 접는다 — 사용자가 스스로 다른 아이템을 고른 것이므로.)
    if (this.pendingUse) this.clearTargetSelection();

    const useType = gameState.getInventory().find((i) => i.id === itemId)?.useType;
    const kind = GameScene.targetKindFor(useType);

    if (kind === 'none') {
      // 포션·음식·장비 장착·귀환 주문서(73) 등 — 지금까지처럼 즉시 전송.
      this.sendUseItem(itemId, useType);
      return;
    }

    if (kind === 'unsupported') {
      this.chatBox.addSystemMessage(
        '아직 지원하지 않는 아이템입니다 (대상 목록 UI 미구현). 사용하지 않았습니다.'
      );
      return;
    }

    this.beginTargetSelection({ kind, itemId, useType: useType as number });
  }

  /**
   * use_item 메시지를 실제로 내보내는 유일한 창구.
   *
   * payload 조립을 여기 한곳에 모아 즉시 전송 경로와 대상 선택 경로가
   * 서로 다른 모양의 메시지를 만들지 않게 한다.
   */
  private sendUseItem(itemId: number, useType: number | undefined, extra?: UseItemExtra): void {
    if (!this.connectionManager || !this.connectionManager.isConnected()) return;
    this.connectionManager.send({
      type: 'use_item',
      payload: {
        itemId,
        ...(useType !== undefined ? { useType } : {}),
        ...(extra ?? {}),
      }
    });
  }

  /**
   * 대상 선택 모드 진입. 종류별로 다른 것은 "안내 문구"와 "어디를 클릭받는가"뿐이고,
   * 상태 보관·취소·완료는 모두 같은 경로를 탄다.
   */
  private beginTargetSelection(pending: PendingItemUse): void {
    this.pendingUse = pending;

    switch (pending.kind) {
      case 'inventory':
        // 창이 닫혀 있으면 고를 수가 없다.
        if (!this.inventory.visible) this.inventory.setVisible(true);
        this.inventory.setTargetSelectMode(true);
        this.targetPrompt.showMessage('대상 아이템을 인벤토리에서 클릭하세요. (ESC 취소)');
        break;
      case 'entity':
        this.targetPrompt.showMessage('대상을 화면에서 클릭하세요. (ESC 취소)');
        break;
      case 'ground':
        this.targetPrompt.showMessage('사용할 지점을 화면에서 클릭하세요. (ESC 취소)');
        break;
      case 'text':
        this.targetPrompt.showTextInput(
          '변신할 이름을 입력하고 Enter를 누르세요. (ESC 취소)',
          '변신할 이름'
        );
        break;
      case 'bookmark':
        // 창 자체가 안내와 취소 버튼을 갖고 있어 상단 배너는 띄우지 않는다.
        // 북마크가 0개여도 창은 연다 — "없다"는 사실을 보여주고 사용자가 닫으면
        // 아무것도 보내지 않으므로 주문서가 소모되지 않는다.
        this.openBookmarkDialog();
        break;
    }
  }

  /**
   * 북마크 목록 창을 현재 목록으로 연다(이미 열려 있으면 다시 그린다).
   *
   * 목록의 진실은 GameState이고 이 창은 열리는 시점의 스냅샷을 받는다.
   * 콜백을 매번 새로 넘기므로 재호출해도 선택/취소 경로는 그대로다.
   */
  private openBookmarkDialog(): void {
    this.bookmarkDialog.show(
      gameState.getBookmarks(),
      (bookmark) => this.handleBookmarkPick(bookmark),
      // 창을 닫은 것은 곧 사용 취소다. 기존 취소 경로와 완전히 같게 흐른다.
      () => this.cancelTargetSelection()
    );
  }

  /**
   * 대상 선택 모드를 끝낸다 (완료·취소·다른 아이템 사용 시작 공통).
   * 상태·안내 배너·인벤토리 모드를 한 번에 되돌려 어느 하나만 남는 일이 없게 한다.
   */
  private clearTargetSelection(): void {
    this.pendingUse = null;
    this.targetPrompt.hide();
    this.inventory.setTargetSelectMode(false);
    // 북마크 창도 대상 선택 UI의 하나다. pendingUse를 **먼저** 비웠으므로
    // hide()가 부르는 onCancel(= cancelTargetSelection)은 곧바로 되돌아나간다
    // (재귀 없음). 고르기로 닫힌 경우엔 창이 이미 숨겨져 있어 no-op이다.
    this.bookmarkDialog?.hide();
  }

  /** 사용자가 명시적으로 취소했을 때 (ESC / 인벤토리 닫기). 안내를 한 줄 남긴다. */
  private cancelTargetSelection(): void {
    if (!this.pendingUse) return;
    this.clearTargetSelection();
    this.chatBox.addSystemMessage('아이템 사용을 취소했습니다.');
  }

  /** 인벤토리가 닫힐 때. 인벤토리 대상을 고르는 중이었을 때만 접는다. */
  private cancelInventoryTargetSelection(): void {
    if (this.pendingUse?.kind !== 'inventory') return;
    this.cancelTargetSelection();
  }

  /**
   * 대상 선택 모드에서 인벤토리 슬롯을 클릭했다 (use_type 7/14/26/27).
   * 클릭한 아이템의 objId가 그대로 서버가 읽을 targetId가 된다.
   */
  private handleInventoryTargetPick(targetItemId: number): void {
    const pending = this.pendingUse;
    if (!pending || pending.kind !== 'inventory') return;

    if (targetItemId === pending.itemId) {
      // 스크롤 자신을 대상으로 지정할 수는 없다. 모드는 유지해 다시 고르게 한다.
      this.chatBox.addSystemMessage('사용하려는 아이템 자신은 대상으로 지정할 수 없습니다.');
      return;
    }

    this.clearTargetSelection();
    // 확인(감정) 주문서만은 "무엇을 감정했는지"를 응답에서 알 수 없다.
    // 보내는 이 순간이 대상을 아는 유일한 시점이므로 여기서 기억해 둔다.
    if (pending.useType === 7) {
      this.pendingIdentifyTargetId = targetItemId;
      this.pendingIdentifyAt = Date.now();
    }
    this.sendUseItem(pending.itemId, pending.useType, { targetId: targetItemId });
  }

  /**
   * 북마크 목록에서 목적지를 골랐다 (use_type 6 / 29).
   *
   * `mapId`는 서버 `C_UseItem`이 `readH()`로 **읽기만 하고 쓰지 않는다** — 원본을
   * 직접 확인했다: 지역변수 `mapid`는 174행에서 0으로 선언되고 190행의
   * `mapid = readH();` 이후 파일 어디에서도 참조되지 않는다. 실제 목적지는
   * `useTeleportScroll()`이 `pc.getBookMark(objid)`로 찾은 북마크의
   * `getLocX()/getLocY()/getMapId()`가 정한다. 그러니 여기서 보내는 값은
   * **자리 채우기**이고 0으로 충분하다 (2바이트를 빠뜨리면 뒤따르는 objId가
   * 어긋나 서버가 엉뚱한 북마크를 찾는다).
   */
  private handleBookmarkPick(bookmark: BookmarkEntry): void {
    const pending = this.pendingUse;
    if (!pending || pending.kind !== 'bookmark') return;

    this.clearTargetSelection();
    this.sendUseItem(pending.itemId, pending.useType, { mapId: 0, targetId: bookmark.id });
    // 이후는 기존 흐름 그대로다 — 서버가 S_Teleport를 보내면 teleport 핸들러가
    // teleport_ack로 답하고, 이어 오는 map_id/좌표로 이동이 확정된다.
  }

  /**
   * 감정 결과 표시 (S_IdentifyDesc).
   *
   * 제목(어느 아이템인가)은 보낸 쪽이 기억해 둔 objId로 인벤토리에서 찾는다.
   * 기억이 없거나 만료됐으면 서버가 args[0]에 실어 보낸 이름을 그대로 쓴다 —
   * 그것도 같은 아이템의 이름이므로 창을 비워 두는 것보다 낫다.
   */
  private handleIdentifyDesc(data: IdentifyDescPayload): void {
    if (!data || typeof data.messageId !== 'number') return;

    const rawArgs = Array.isArray(data.args) ? data.args : [];
    // 서버는 인자에 `$227`(축복된)/`$숫자` 같은 리소스 키를 그대로 싣는다.
    const args = rawArgs.map((arg) => resolveMessageArg(arg ?? ''));

    const title = this.takeIdentifyTargetName() || args[0] || '';

    const build = IDENTIFY_DESC_LINES[data.messageId];
    const lines = build
      ? (build(args).filter((line): line is string => !!line))
      // 표에 없는 번호 — 문장을 지어내지 않고 nameResolver의 공용 조립기에 맡긴다.
      : [formatServerMessage(data.messageId, rawArgs)];

    this.identifyDialog.show(title, lines);
  }

  /**
   * 기억해 둔 감정 대상의 표시명을 꺼내고 기억을 비운다.
   * 유효 시간(IDENTIFY_TARGET_TTL_MS)을 넘겼으면 없는 것으로 본다.
   */
  private takeIdentifyTargetName(): string {
    const targetId = this.pendingIdentifyTargetId;
    const at = this.pendingIdentifyAt;
    this.pendingIdentifyTargetId = null;
    this.pendingIdentifyAt = 0;

    if (targetId === null) return '';
    if (Date.now() - at > IDENTIFY_TARGET_TTL_MS) return '';

    const item = gameState.getInventory().find((i) => i.id === targetId);
    if (!item) return '';
    // 인벤토리 원본 이름은 아직 $숫자 키와 장착 접미사가 붙어 있다
    // (화면용 변환은 toInventoryItems가 하지만 GameState에는 원본이 남는다).
    return resolveItemName(stripEquippedSuffix(item.name || ''));
  }

  /**
   * 대상 선택 모드에서 월드를 클릭했다 (entity / ground).
   *
   * 좌표는 반드시 **서버 타일 좌표**로 보낸다 — 카메라 스크롤이 반영된 월드
   * 좌표를 구한 뒤 worldToTile()로 변환한다. 화면 픽셀을 그대로 보내면 안 된다.
   */
  private handleWorldTargetPick(pointer: Phaser.Input.Pointer): void {
    const pending = this.pendingUse;
    if (!pending) return;

    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const clickTile = worldToTile(world.x, world.y);
    const tileX = this.clampTile(clickTile.tx);
    const tileY = this.clampTile(clickTile.ty);

    // 부활 스크롤(8)의 대상은 이미 쓰러진 캐릭터다 — 사망 연출 중인 것도 후보에 넣는다.
    const includeDead = pending.useType === 8;
    const picked = this.pickEntityAt(world, tileX, tileY, includeDead);

    // 자기 자신을 겨눈 경우. 자기 캐릭터는 entitySprites에 없으므로 따로 판정한다.
    const selfObjId = gameState.getPlayerObjId();
    const isSelf =
      !picked &&
      selfObjId !== null &&
      (this.playerSprite.getBounds().contains(world.x, world.y) ||
        (tileX === this.playerTileX && tileY === this.playerTileY));

    const targetId = picked ? picked.objId : (isSelf ? (selfObjId as number) : 0);

    if (pending.kind === 'entity') {
      if (targetId === 0) {
        // 대상이 없으면 모드를 유지한 채 다시 고르게 한다 (아이템을 낭비하지 않는다).
        this.chatBox.addSystemMessage('그 자리에는 대상이 없습니다. 다시 선택하세요.');
        return;
      }
      this.clearTargetSelection();
      this.sendUseItem(pending.itemId, pending.useType, { targetId });
      return;
    }

    // ground — 좌표가 본체다. 5/17/39는 objId도 이어서 읽으므로 있으면 채우고 없으면 0으로
    // 둔다 (게이트웨이가 use_type 42에서는 objId 자리를 아예 쓰지 않는다).
    this.clearTargetSelection();
    this.sendUseItem(pending.itemId, pending.useType, { targetId, x: tileX, y: tileY });
  }

  /** 문자열 입력 완료 (use_type 16 변신 스크롤). */
  private handleTextTargetSubmit(text: string): void {
    const pending = this.pendingUse;
    if (!pending || pending.kind !== 'text') return;

    this.clearTargetSelection();
    this.sendUseItem(pending.itemId, pending.useType, { text });
  }

  // ==================== 아이템 내려놓기 (C_DropItem) ====================

  /**
   * 인벤토리에서 월드로 끌어다 놓았다 — 그 타일에 아이템을 내려놓는다.
   *
   * 겹친 아이템(count > 1)은 몇 개를 놓을지 먼저 묻는다. 창을 띄우는 동안에도
   * 착지 타일은 **놓은 순간의 좌표로 고정**한다 — 대답하는 사이 캐릭터가 움직여도
   * 사용자가 가리킨 자리에 놓이게 하기 위해서다.
   *
   * @param pointer 드래그를 놓은 지점 (화면 좌표)
   */
  private dropItemAt(item: InventoryItem, pointer: Phaser.Input.Pointer): void {
    // 장착 중인 것은 서버가 거절한다(메시지 125) — 보내기 전에 알려 준다.
    if (item.equipped) {
      this.chatBox.addSystemMessage('장착 중인 아이템은 버릴 수 없습니다. 먼저 해제하세요.');
      return;
    }

    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const clicked = worldToTile(world.x, world.y);
    const tileX = this.clampTile(clicked.tx);
    const tileY = this.clampTile(clicked.ty);

    // 서버는 드롭 좌표를 검증하지 않는다 — 사거리는 클라이언트가 지키는 규칙이다.
    // 서버와 같은 직선 거리(max(|dx|,|dy|))로 재야 대각선이 손해 보지 않는다.
    const distance = tileLineDistance(this.playerTileX, this.playerTileY, tileX, tileY);
    if (distance > GROUND_DROP_RANGE) {
      this.chatBox.addSystemMessage(
        `너무 멉니다. ${GROUND_DROP_RANGE}칸 이내에만 내려놓을 수 있습니다.`
      );
      return;
    }

    // 벽 안쪽에 놓으면 서버는 그대로 받아 주지만 아무도 주울 수 없다.
    // (맵 데이터를 아직 못 받았으면 isPassable이 true를 주므로 막지 않는다.)
    if (this.currentMapId !== null && !mapCollision.isPassable(this.currentMapId, tileX, tileY)) {
      this.chatBox.addSystemMessage('그 자리에는 내려놓을 수 없습니다.');
      return;
    }

    const maxCount = Math.max(1, item.count);
    if (maxCount <= 1) {
      this.sendDropItem(item, 1, tileX, tileY);
      return;
    }

    this.quantityDialog.show(
      {
        title: '몇 개를 내려놓을까요?',
        itemName: stripCountSuffix(item.name),
        max: maxCount,
        confirmLabel: '버리기',
      },
      (count) => this.sendDropItem(item, count, tileX, tileY)
    );
  }

  /**
   * C_DropItem 전송. 성공 여부는 응답으로만 알 수 있다 — 인벤토리 쪽은
   * `delete_item`/`item_amount`가, 바닥 쪽은 `drop_item`이 온다. 서버가 거절하면
   * `server_message`(125 장착 중 / 210 거래 불가 / 77 여관 안)만 온다.
   */
  private sendDropItem(item: InventoryItem, count: number, tileX: number, tileY: number): void {
    if (!this.connectionManager || !this.connectionManager.isConnected()) {
      this.chatBox.addSystemMessage('서버와 연결되어 있지 않아 아이템을 내려놓을 수 없습니다.');
      return;
    }
    this.connectionManager.getPacketHandler().sendDropItem(item.id, count, tileX, tileY);
  }

  // ==================== 아이템 줍기 (C_PickUpItem) ====================

  /**
   * 바닥 아이템을 클릭했을 때의 처리.
   *
   * 사거리(3칸) 안이면 바로 줍고, 멀면 그쪽으로 걸어간 뒤 도착하면 줍는다
   * (`pendingPickup` — 몹 추격과 달리 대상이 움직이지 않으므로 경로를 다시 짜지
   * 않고 도착 여부만 매 프레임 확인한다).
   */
  private beginPickup(item: GroundItemSprite): void {
    if (gameState.isDead()) return;

    const distance = tileLineDistance(this.playerTileX, this.playerTileY, item.tileX, item.tileY);
    if (distance <= GROUND_PICKUP_RANGE) {
      this.pickUpItem(item);
      return;
    }

    // 걸어가서 줍는다. 쫓던 것이 있으면 그만둔다 — 사용자가 다른 것을 골랐다.
    this.clearChase();
    this.pendingPickup = { objId: item.objId, expiresAt: this.time.now + PICKUP_WALK_TIMEOUT_MS };
    this.setMoveTarget(item.tileX, item.tileY, true);
  }

  /**
   * 걸어가서 줍기로 한 아이템이 사거리에 들어왔는지 매 프레임 확인한다.
   *
   * 아이템이 그 사이 사라졌거나(남이 주웠다) 시간이 지나면(길이 막혀 도착하지
   * 못했다) 조용히 포기한다 — 계속 남겨 두면 한참 뒤 엉뚱한 순간에 줍기가 나간다.
   */
  private updatePendingPickup(): void {
    const pending = this.pendingPickup;
    if (!pending) return;

    const item = this.groundItems.get(pending.objId);
    if (!item) {
      this.pendingPickup = null;
      return;
    }

    if (this.time.now > pending.expiresAt) {
      this.pendingPickup = null;
      this.chatBox.addSystemMessage(`${item.name}까지 가지 못했습니다.`);
      return;
    }

    const distance = tileLineDistance(this.playerTileX, this.playerTileY, item.tileX, item.tileY);
    if (distance > GROUND_PICKUP_RANGE) return;

    this.pendingPickup = null;
    this.clearMoveTarget();
    this.pickUpItem(item);
  }

  /**
   * 실제로 줍는다 (C_PickUpItem 전송).
   *
   * **좌표는 아이템이 놓인 타일이다** — 서버가 그 좌표의 지면 인벤토리에서 objId를
   * 찾기 때문에 플레이어 좌표를 보내면 아무 일도 일어나지 않는다. 서버는 실패를
   * 알려주지 않는 경우가 많으므로(사거리 초과·유령 상태 등은 무응답) 보내기 전에
   * 확인할 수 있는 것은 여기서 확인한다.
   */
  private pickUpItem(item: GroundItemSprite): void {
    if (!this.connectionManager || !this.connectionManager.isConnected()) {
      this.chatBox.addSystemMessage('서버와 연결되어 있지 않아 아이템을 주울 수 없습니다.');
      return;
    }

    // 아이템 쪽으로 돌아선다 (줍는 동작이 보이지 않아도 방향은 맞춰 둔다).
    this.currentHeading = headingFromTileDelta(
      item.tileX - this.playerTileX,
      item.tileY - this.playerTileY,
    );

    this.connectionManager
      .getPacketHandler()
      .sendPickupItem(item.objId, Math.max(1, item.count), item.tileX, item.tileY);
  }

  /**
   * 아이콘을 **직접** 맞힌 바닥 아이템 (엔티티의 pickEntityAt 1순위와 같은 판정).
   *
   * 여러 개가 겹쳐 있으면 앞쪽(depth가 큰) 것을 고른다.
   * 직격만 보는 이유는 handleAttack의 호출부 주석 참고 — 빗맞힌 클릭이 공격 대신
   * 줍기로 새면 전투 중에 위험하다.
   */
  private pickGroundItemDirect(world: { x: number; y: number }): GroundItemSprite | null {
    let picked: GroundItemSprite | null = null;
    let bestDepth = -Infinity;

    this.groundItems.forEach((item) => {
      if (!item.sprite.visible) return;
      if (!item.sprite.getBounds().contains(world.x, world.y)) return;
      if (item.sprite.depth > bestDepth) {
        bestDepth = item.sprite.depth;
        picked = item;
      }
    });

    // forEach 콜백 안의 대입은 TS 흐름 분석이 추적하지 못해 여기서 null로 좁혀진다.
    return picked as GroundItemSprite | null;
  }

  /**
   * 클릭한 타일 근처의 바닥 아이템 (아이콘을 빗맞혔을 때의 폴백).
   *
   * 아이소메트릭에서 작은 아이콘은 정확히 누르기 어렵다 — 엔티티 판정이 같은
   * 이유로 타일 폴백을 두는 것과 같다.
   */
  private pickGroundItemNear(tileX: number, tileY: number): GroundItemSprite | null {
    let picked: GroundItemSprite | null = null;
    let minDist = Infinity;

    this.groundItems.forEach((item) => {
      const dist = tileDistance(item.tileX, item.tileY, tileX, tileY);
      if (dist < minDist && dist <= GROUND_ITEM_PICK_RANGE) {
        minDist = dist;
        picked = item;
      }
    });

    return picked as GroundItemSprite | null;
  }

  /**
   * 사거리 안에서 가장 가까운 바닥 아이템을 줍는다 (Z 키).
   *
   * 발밑 아이템은 아이콘이 캐릭터에 가려 클릭하기 어렵다 — 원본 클라이언트에도
   * 같은 이유로 줍기 단축키가 있다.
   */
  private pickUpNearest(): void {
    if (gameState.isDead()) return;

    let nearest: GroundItemSprite | null = null;
    let minDist = Infinity;
    this.groundItems.forEach((item) => {
      const dist = tileLineDistance(this.playerTileX, this.playerTileY, item.tileX, item.tileY);
      if (dist <= GROUND_PICKUP_RANGE && dist < minDist) {
        minDist = dist;
        nearest = item;
      }
    });

    if (!nearest) {
      this.chatBox.addSystemMessage('주울 만한 것이 가까이 없습니다.');
      return;
    }

    this.pickUpItem(nearest as GroundItemSprite);
  }

  /**
   * 아이템 장착
   */
  private equipItem(itemId: number): void {
    // Gateway는 equip_item 프로토콜을 지원하지 않음
    // 서버에서 처리하므로 로그만 출력
    console.log('Equip item:', itemId);
  }

  /**
   * 메뉴 토글
   */
  private toggleMenu(): void {
    this.isMenuVisible = !this.isMenuVisible;
    this.menuPanel.setVisible(this.isMenuVisible);
    this.menuButton.setOpen(this.isMenuVisible);

    // 메뉴가 보일 때 게임 입력 일시정지.
    // 진행 중인 한 타일 스텝은 취소하지 않는다 — 서버는 스텝 시작 시점에 이미
    // 도착 타일을 기록했으므로, 클라이언트도 그 타일까지 완주해야 위치가 일치한다.
    if (this.isMenuVisible) {
      this.clearMoveTarget();
    } else {
      // 사용자가 직접 닫았다면 더 이상 "사망이 연 메뉴"가 아니다 —
      // 그대로 두면 부활 순간 닫힌 메뉴를 또 닫으려 든다.
      this.menuOpenedByDeath = false;
    }
  }

  /**
   * 메뉴 숨기기
   */
  private hideMenu(): void {
    this.isMenuVisible = false;
    this.menuPanel.setVisible(false);
    // 햄버거 버튼 모양도 함께 되돌린다 — 이걸 빼면 패널은 닫혔는데 버튼만
    // 열린 모양으로 남아, 다음 클릭이 toggleMenu()의 반전에서 어긋난다.
    this.menuButton.setOpen(false);
    this.menuOpenedByDeath = false;
  }

  /**
   * 사망하면 메뉴를 자동으로 연다.
   *
   * 죽은 채로는 걷지도 싸우지도 못하므로 다음 행동(캐릭터 선택으로 돌아가기 등)은
   * 메뉴에만 있다. 다만 **곧바로 열지 않는다** — 사망 연출(5프레임 × 200ms)이
   * 화면 한가운데 패널에 가려지면 무엇 때문에 멈췄는지 알 수 없다.
   *
   * 이미 열려 있으면 그대로 둔다 (자동으로 연 것으로 표시하지도 않는다 —
   * 사용자가 스스로 연 메뉴를 부활 때 닫아버리면 안 되므로).
   */
  private openMenuOnDeath(): void {
    this.deathMenuTimer?.remove();
    this.deathMenuTimer = this.time.delayedCall(DEATH_MENU_DELAY_MS, () => {
      this.deathMenuTimer = null;
      // 기다리는 사이에 부활했을 수 있다.
      if (!gameState.isDead()) return;
      if (this.isMenuVisible) return;
      this.menuOpenedByDeath = true;
      this.toggleMenu();
    });
  }

  /** 부활하면 사망 때문에 열렸던 메뉴만 닫는다 (직접 연 메뉴는 건드리지 않는다). */
  private closeMenuOnRevive(): void {
    this.deathMenuTimer?.remove();
    this.deathMenuTimer = null;
    if (!this.menuOpenedByDeath) return;
    this.hideMenu();
  }

  /**
   * 인벤토리 토글
   */
  private toggleInventory(): void {
    this.inventory.setVisible(!this.inventory.visible);
    if (this.inventory.visible) {
      this.hideMenu();
    } else {
      // 창을 닫으면 인벤토리에서 대상을 고를 수 없다 — 대기 상태를 남기지 않는다.
      this.cancelInventoryTargetSelection();
    }
  }

  /**
   * 캐릭터 정보 표시
   */
  private showCharacterInfo(): void {
    // 토글 대상은 좌하단 상시 HUD가 아니라 좌측의 상세 정보 창이다.
    this.characterDetailPanel.toggle();
    if (this.characterDetailPanel.visible) {
      // 최신 값으로 맞춰 연다. 창이 닫혀 있는 동안에도 스탯은 계속 들어오지만
      // 열리는 순간의 값이 화면과 어긋나지 않도록 한 번 더 반영한다.
      const stats = gameState.getDetailStats();
      if (stats) this.characterDetailPanel.setStats(stats);
      this.hideMenu();
    }
  }

  /**
   * 장착 아이템 창 토글.
   */
  private toggleEquipmentPanel(): void {
    this.equipmentPanel.toggle();
    if (this.equipmentPanel.visible) {
      this.hideMenu();
    }
  }

  /**
   * 게임 세션이 유효하지 않게 되었을 때 알리고 로그인 화면으로 되돌린다.
   *
   * 재연결된 WebSocket은 **로그인 전 상태**라 캐릭터 선택 화면도 동작하지 않는다
   * (캐릭터 목록을 받으려면 세션에 L1J 연결이 있어야 한다). 그래서 로그인 화면까지
   * 되돌린다.
   *
   * 자동 로그인은 한 번 건너뛴다 — 곧바로 다시 접속해 버리면 사용자가 무슨 일이
   * 있었는지 알 수 없고, 서버에 이전 세션이 남아 있으면 중복 로그인으로 또 끊긴다.
   */
  private handleSessionLost(reason: string): void {
    if (this.sessionLostHandled) return;
    this.sessionLostHandled = true;

    this.chatBox.addSystemMessage(`${reason} 로그인 화면으로 돌아갑니다.`);
    storageManager.suppressNextAutoLogin();
    gameState.resetInGame();

    // 메시지를 읽을 시간을 준 뒤 이동한다.
    this.time.delayedCall(1800, () => {
      this.scene.start('LoginScene');
    });
  }

  /**
   * 진입 유예 중에 클릭했을 때 한 번만 알린다.
   * 안내가 없으면 몇 초간 클릭이 통째로 먹히는 것처럼 보인다.
   */
  private noticeWorldNotReady(): void {
    if (this.worldNotReadyNoticed) return;
    this.worldNotReadyNoticed = true;
    this.chatBox.addSystemMessage('월드를 불러오는 중입니다. 잠시 후 이동할 수 있습니다.');
  }

  /**
   * 포인터가 열려 있는 창 위에 있는지.
   *
   * 창이 열렸다고 화면 전체의 클릭을 막지 않는다 — 인벤토리를 펼쳐둔 채로도
   * 걷고 싸울 수 있어야 한다. 가리는 것은 창이 실제로 덮고 있는 영역뿐이다.
   */
  /**
   * 드래그를 놓은 지점이 "월드"인가 — 즉 아이템을 바닥에 내려놓아도 되는 자리인가.
   *
   * 월드 클릭 판정(setupInput의 pointerdown)과 같은 기준을 쓴다: 하단 UI 영역이
   * 아니고, 열려 있는 창 위도 아니어야 한다. 판정이 헐거우면 창을 조작하려던
   * 드래그가 아이템 버리기로 이어지는데, 버린 아이템은 되돌릴 수 없다.
   */
  private isWorldDropPoint(pointer: Phaser.Input.Pointer): boolean {
    if (!this.worldClickEnabled) return false;
    if (pointer.y >= this.worldClickBottomLimit) return false;
    return !this.isPointerOverOpenPanels(pointer);
  }

  private isPointerOverOpenPanels(pointer: Phaser.Input.Pointer): boolean {
    const over = (panel: { visible: boolean; x: number; y: number } | undefined,
                  width: number, height: number): boolean => {
      if (!panel?.visible) return false;
      return (
        Math.abs(pointer.x - panel.x) <= width / 2 &&
        Math.abs(pointer.y - panel.y) <= height / 2
      );
    };

    return (
      over(this.characterDetailPanel, this.characterDetailPanel?.getPanelWidth() ?? 0,
           this.characterDetailPanel?.getPanelHeight() ?? 0) ||
      over(this.equipmentPanel, this.equipmentPanel?.getPanelWidth() ?? 0,
           this.equipmentPanel?.getPanelHeight() ?? 0) ||
      over(this.inventory, this.inventory?.getPanelWidth() ?? 0,
           this.inventory?.getPanelHeight() ?? 0) ||
      over(this.htmlDialog, this.htmlDialog?.getPanelWidth() ?? 0,
           this.htmlDialog?.getPanelHeight() ?? 0) ||
      over(this.bookmarkDialog, this.bookmarkDialog?.getPanelWidth() ?? 0,
           this.bookmarkDialog?.getPanelHeight() ?? 0) ||
      over(this.identifyDialog, this.identifyDialog?.getPanelWidth() ?? 0,
           this.identifyDialog?.getPanelHeight() ?? 0) ||
      over(this.quantityDialog, this.quantityDialog?.getPanelWidth() ?? 0,
           this.quantityDialog?.getPanelHeight() ?? 0) ||
      over(this.targetPrompt, this.targetPrompt?.getPanelWidth() ?? 0,
           this.targetPrompt?.getPanelHeight() ?? 0) ||
      // 미니맵은 원점이 중앙이 아니라 맵 영역의 좌상단이고 프레임/텍스트가
      // 바깥으로 삐져나온다. 중앙 기준 헬퍼(over)로는 판정할 수 없어 컴포넌트가
      // 직접 계산한다. pointer.worldX/Y가 아니라 화면 좌표를 넘겨야 한다 —
      // 미니맵은 setScrollFactor(0)이라 카메라 스크롤과 무관하다.
      (this.miniMap?.containsScreenPoint(pointer.x, pointer.y) ?? false)
    );
  }

  /**
   * ESC 메뉴 [Logout] — 계정 로그아웃이 아니라 현재 캐릭터만 게임 월드에서
   * 내보내고 캐릭터 선택 화면으로 복귀한다. 계정 세션/WebSocket/L1J TCP
   * 연결은 그대로 유지되므로(서버의 C_NewCharSelect 처리), 다시 로그인할
   * 필요 없이 곧바로 다른 캐릭터를 선택하거나 같은 캐릭터로 재접속할 수 있다.
   */
  private returnToCharSelect(): void {
    try {
      this.connectionManager?.send({ type: 'return_to_char_select' });
    } catch (err) {
      console.error('Failed to send return_to_char_select:', err);
    }
    gameState.resetInGame();
    this.scene.start('CharSelectScene');
  }

  // ==================== 이동 (타일 단위) ====================

  /**
   * 진행 중인 한 타일 스텝의 화면 보간을 진행한다.
   * UI가 열려 있어도 반드시 호출해야 한다 — 스텝을 시작한 시점에 서버는 이미
   * 도착 타일을 기록했으므로 중간에 버리면 클라이언트/서버 위치가 어긋난다.
   */
  private updateStep(delta: number): void {
    if (!this.isStepping) return;

    // delta 누적은 참고용으로만 남긴다 (다른 연출이 쓴다). 진행도 t는 벽시계로
    // 잰다 — 이유는 stepStartedAt 주석 참조. delta 평활 오차가 섞이면 다음
    // C_MoveChar가 서버 하한(328.2ms)보다 빨리 나가 강제 접속 종료로 이어진다.
    this.stepElapsed += delta;
    const wallElapsed = performance.now() - this.stepStartedAt;
    const t = Math.min(1, wallElapsed / MS_PER_TILE);

    const x = this.stepFromX + (this.stepToX - this.stepFromX) * t;
    const y = this.stepFromY + (this.stepToY - this.stepFromY) * t;
    this.renderPlayerAt(x, y);

    if (t >= 1) {
      // 논리 타일 좌표 확정
      this.playerTileX = this.stepToTileX;
      this.playerTileY = this.stepToTileY;
      this.isStepping = false;
      this.stepElapsed = 0;
      // 보간이 끝났으니 더 이상 걷지 않는다. idle 프레임으로 되돌린다.
      this.playerIsWalking = false;
      this.playerFrame = 0;
      this.playerWalkFrameTimer = 0;
      this.applyPlayerTexture();
      this.refreshDepthOrigin();
      this.renderPlayerAt(this.stepToX, this.stepToY);
    }
  }

  /**
   * 새 타일 스텝을 시작할지 결정한다 (키보드 > 클릭 목적지 순).
   * 스텝이 진행 중이면 아무것도 하지 않는다 — 이동은 한 번에 한 타일씩이다.
   */
  private handleMovementInput(): void {
    if (this.isStepping) return;
    // 스텝이 끝났어도 서버 하한이 아직 안 지났으면 다음 걸음을 시작하지 않는다.
    // MS_PER_TILE이 어떤 값이든 C_MoveChar 송신 간격이 하한 밑으로 내려가지 못하게
    // 막는 안전망이다 (moveGateMs 주석 참조 — 지금 값에서는 아무 영향이 없다).
    if (!this.canStepNow()) {
      // 실제로 걸으려던 참일 때만 센다 (가만히 서 있는 프레임까지 세면 의미가 없다).
      if (this.moveTargetTile || this.readKeyboardHeading() !== null) this.moveGateHolds++;
      return;
    }
    // 사망·상태이상(마비/수면/빙결/스턴/속박)·텔레포트 중에는 키보드 이동도
    // 클릭 이동도 진행하지 않는다. 세 조건을 GameState가 한 줄로 판정해 준다.
    if (!gameState.canMove()) return;
    // 인벤토리는 열어둔 채로 계속 걸을 수 있어야 한다 — 창을 여는 순간 걸음이
    // 멈추면 안 된다. 창 위 클릭이 월드로 새는 것은 pointerdown 쪽에서 막는다.
    // 메뉴는 모달이고, 채팅 입력 중에는 방향키가 글자 입력이므로 그대로 막는다.
    if (this.isMenuVisible || this.chatBox.isInputActive()) return;

    const keyHeading = this.readKeyboardHeading();
    if (keyHeading !== null) {
      // 키보드 입력이 클릭 이동보다 우선한다. 직접 걷기 시작했으면 쫓던 것도 그만둔다.
      this.clearMoveTarget();
      this.clearChase();
      this.beginStep(keyHeading);
      return;
    }

    if (this.moveTargetTile) {
      const dx = this.moveTargetTile.tx - this.playerTileX;
      const dy = this.moveTargetTile.ty - this.playerTileY;
      if (dx === 0 && dy === 0) {
        this.clearMoveTarget();
        return;
      }

      if (!this.movePathActive) {
        // 폴백: 맵 통행 데이터가 아직 없어 경로를 만들지 못했다. 예전처럼 목적지
        // 방향(8방향 스냅)으로 한 타일씩 밀고 간다. 막히면 beginStep()이 목적지를
        // 취소한다. 데이터가 없다고 아예 못 움직이면 회귀다.
        this.beginStep(headingFromTileDelta(dx, dy));
        return;
      }

      const next = this.nextPathStep();
      if (!next) {
        // 경로를 다 걸었다. 부분 경로(도달 불가라 벽 앞까지만 만든 경로)여서
        // 목표 타일과 다른 곳에 서 있을 수 있으므로, 도착 판정은 좌표 일치가
        // 아니라 **경로 소진**을 기준으로 한다.
        this.clearMoveTarget();
        return;
      }

      // 다음 타일이 지금은 막혀 있다면 경로를 버리고 다시 만든다 (횟수 제한 있음).
      // 벽뿐 아니라 **엔티티 점유**까지 봐야 한다 — 경로를 만든 뒤에 몹이 그 자리로
      // 걸어들어오는 일은 흔한데, 그대로 beginStep에 맡기면 목적지째 취소된다.
      if (!this.canEnterTile(next.tx, next.ty)) {
        if (!this.recomputeMovePath()) {
          this.clearMoveTarget();
          return;
        }
        const retry = this.nextPathStep();
        if (!retry) {
          this.clearMoveTarget();
          return;
        }
        this.stepAlongPath(retry);
        return;
      }

      this.stepAlongPath(next);
    }
  }

  /**
   * 경로의 다음 타일로 한 스텝 나아간다.
   *
   * 서버 프로토콜상 한 번에 한 타일씩만 보낼 수 있으므로(beginStep 주석 참고)
   * 경로가 있어도 좌표를 건너뛰지 않는다. 스텝이 실제로 시작됐을 때만 경로를
   * 소비한다 — 엔티티에 막혀 실패하면 beginStep이 목적지를 취소한다.
   */
  private stepAlongPath(node: PathNode): void {
    const heading = headingFromTileDelta(node.tx - this.playerTileX, node.ty - this.playerTileY);
    if (this.beginStep(heading)) this.movePathIndex++;
  }

  /**
   * 아직 걷지 않은 경로의 다음 타일. 경로를 다 걸었으면 null.
   *
   * 서버 보정 등으로 이미 그 타일에 서 있게 됐다면 건너뛴다. 인접하지 않은 타일이
   * 나오면(경로가 어긋난 상태) 경로 자체를 버린다 — 건너뛰는 좌표를 보내면 서버와
   * 위치가 어긋난다.
   */
  private nextPathStep(): PathNode | null {
    while (this.movePathIndex < this.movePath.length) {
      const node = this.movePath[this.movePathIndex];
      const dx = node.tx - this.playerTileX;
      const dy = node.ty - this.playerTileY;
      if (dx === 0 && dy === 0) {
        this.movePathIndex++;
        continue;
      }
      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
        this.movePath = [];
        this.movePathIndex = 0;
        return null;
      }
      return node;
    }
    return null;
  }

  /**
   * 클릭/추격 목적지를 정하고 거기까지의 경로를 미리 만들어 둔다.
   *
   * 경로는 목적지를 정하는 이 시점에 **한 번만** 만든다. 매 프레임 다시 만들면
   * (60fps × 수천 노드) 메인 스레드가 버티지 못한다.
   *
   * @param notify   길이 없거나 부분 경로일 때 채팅으로 알릴지 (직접 클릭한 경우만)
   * @param maxNodes 탐색 노드 예산 (추격은 CHASE_MAX_PATH_NODES로 낮춘다)
   */
  private setMoveTarget(tx: number, ty: number, notify: boolean, maxNodes?: number): void {
    this.moveTargetTile = { tx, ty };
    this.movePath = [];
    this.movePathIndex = 0;
    this.movePathActive = false;
    this.movePathRetries = 0;
    this.movePathMaxNodes = maxNodes;

    // 통행 데이터가 아직 없으면 경로를 만들 수 없다 (findPath도 빈 배열을 돌려준다).
    // 목적지는 그대로 두고 그리디 폴백에 맡긴다 — 맵 로드 전에 못 움직이면 회귀다.
    if (this.currentMapId === null || !mapCollision.isLoaded(this.currentMapId)) return;

    const origin = this.pathOriginTile();
    const path = mapCollision.findPath(
      this.currentMapId,
      origin.tx,
      origin.ty,
      tx,
      ty,
      this.findPathOptions(maxNodes),
    );

    if (path.length === 0) {
      // 첫 걸음조차 없다(사방이 막혔거나 이미 목표에 서 있다). 목적지를 반드시
      // 비워야 매 프레임 재탐색이 돌지 않는다.
      this.clearMoveTarget();
      if (notify) this.chatBox.addSystemMessage('그곳으로 가는 길이 없습니다.');
      return;
    }

    this.movePath = path;
    this.movePathActive = true;

    const last = path[path.length - 1];
    if (notify && (last.tx !== tx || last.ty !== ty)) {
      this.chatBox.addSystemMessage('그곳까지 가는 길이 막혀 있어 갈 수 있는 데까지만 이동합니다.');
    }
  }

  /**
   * 현재 목적지까지의 경로를 다시 만든다. 목적지 하나당 MOVE_PATH_MAX_RETRIES회까지만
   * 허용한다 — 막힌 상태가 이어지는 동안 매 프레임 A*를 도는 것을 막는다.
   *
   * @returns 새 경로를 얻었으면 true. false면 호출자가 목적지를 포기해야 한다.
   */
  private recomputeMovePath(): boolean {
    if (this.movePathRetries >= MOVE_PATH_MAX_RETRIES) return false;
    this.movePathRetries++;

    const target = this.moveTargetTile;
    if (target === null || this.currentMapId === null) return false;

    const origin = this.pathOriginTile();
    const path = mapCollision.findPath(
      this.currentMapId,
      origin.tx,
      origin.ty,
      target.tx,
      target.ty,
      this.findPathOptions(this.movePathMaxNodes),
    );
    this.movePath = path;
    this.movePathIndex = 0;
    return path.length > 0;
  }

  /**
   * findPath에 넘길 옵션. 지형(통행 비트셋) 외에 **그 타일에 서 있는 엔티티**도
   * 막힌 것으로 알려준다.
   *
   * 이게 없으면 A*가 NPC/몹을 관통하는 경로를 내고, 첫 걸음에서 beginStep이
   * canEnterTile로 걸러 목적지를 통째로 버린다 — 마을처럼 NPC가 촘촘한 곳에서는
   * 경로 탐색이 있으나 마나가 된다 (NPC에게 말을 걸려고 클릭했을 때가 특히 그렇다:
   * 대상 자신이 목표 타일을 막고 있다).
   *
   * 목표 타일이 엔티티에 막혀 있으면 allowPartial(기본 true)이 그 **옆 타일**까지의
   * 경로를 돌려주므로, 추격은 사거리 안에 들어가 자연스럽게 끝난다.
   */
  private findPathOptions(maxNodes: number | undefined): FindPathOptions {
    return {
      maxNodes,
      isBlocked: (tx, ty) => this.findBlockingEntity(tx, ty) !== null,
    };
  }

  /**
   * 경로 탐색의 출발 타일.
   *
   * 스텝이 진행 중이면 **그 스텝의 도착 타일**이다. playerTileX/Y는 스텝이 끝나야
   * 갱신되므로, 걷는 도중(추격 대상이 움직여 다시 탐색할 때)에 그 값을 쓰면 한 타일
   * 뒤에서 출발하는 경로가 나오고, 스텝이 끝난 뒤에는 첫 노드가 인접하지 않아
   * (nextPathStep이 버려서) 추격이 그 자리에 멈춘다.
   */
  private pathOriginTile(): { tx: number; ty: number } {
    if (this.isStepping) return { tx: this.stepToTileX, ty: this.stepToTileY };
    return { tx: this.playerTileX, ty: this.playerTileY };
  }

  /** 클릭 이동 목적지와 경로를 함께 비운다 (목적지를 버리는 모든 자리에서 이것만 쓴다). */
  private clearMoveTarget(): void {
    this.moveTargetTile = null;
    this.movePath = [];
    this.movePathIndex = 0;
    this.movePathActive = false;
    this.movePathRetries = 0;
    this.movePathMaxNodes = undefined;
  }

  /**
   * 추격 상태를 비운다 (대상 + 재탐색 기준 타일).
   *
   * 미뤄둔 공격은 여기서 지우지 않는다 — 닿아서 치는 순간에도 clearChase()를
   * 부르기 때문에(updateAttackChase), 함께 지우면 그 한 대가 영영 안 나간다.
   * 예약은 자기 조건(대상 소멸·사망·사거리 이탈)으로만 풀린다.
   */
  private clearChase(): void {
    this.attackTargetId = null;
    this.chasePathGoal = null;
  }

  /**
   * 방향키 입력을 heading(0-7)으로 변환한다. 누른 키가 없으면 null.
   *
   * 아이소메트릭에서는 "화면 위"가 북(ty-1)이 아니다. 화면 방향을 먼저 구한 뒤
   * 타일 델타로 바꾼다:
   *   화면 위   = 타일 (-1, -1)
   *   화면 오른쪽 = 타일 (+1, -1)
   *   화면 아래  = 타일 (+1, +1)
   *   화면 왼쪽  = 타일 (-1, +1)
   * => tdx = sx + sy, tdy = sy - sx  (sx: 오른쪽 +1, sy: 아래 +1)
   */
  private readKeyboardHeading(): number | null {
    let sx = 0;
    let sy = 0;
    if (this.keys.up.isDown) sy -= 1;
    if (this.keys.down.isDown) sy += 1;
    if (this.keys.left.isDown) sx -= 1;
    if (this.keys.right.isDown) sx += 1;

    if (sx === 0 && sy === 0) return null;

    const tdx = sx + sy;
    const tdy = sy - sx;
    if (tdx === 0 && tdy === 0) return null; // 상반된 키를 동시에 누른 경우

    return headingFromTileDelta(tdx, tdy);
  }

  /**
   * 서버가 요구하는 걸음 최소 간격(ms) — MS_PER_TILE과 서버 하한 중 **큰 쪽**.
   *
   * `AcceleratorChecker.getRightInterval(MOVE)`는 `SprTable.getMoveSpeed(gfx, weapon)`을
   * 쓴다(공격과 달리 +1이 없다). 확인한 플레이어 gfx는 전부 640ms라 하한은
   * 640/1.95*1.03 = 338.1ms가 되고, MS_PER_TILE(340)이 그보다 크므로 **지금은
   * 실효값이 MS_PER_TILE 그대로다.** 이 함수는 MS_PER_TILE을 건드리는 대신, 보행이
   * 640보다 느린 gfx가 나타났을 때 자동으로 하한을 올려 주는 안전망이다.
   *
   * 두 값의 차가 1.9ms뿐이라는 점에 주의 — 서버 `CheckStrictness`를 다시 낮추면
   * (엄격하게 만들면) 곧바로 하한이 MS_PER_TILE을 추월해 걸음이 느려진다. 그때는
   * 이 함수가 접속 종료는 막아 주지만, MS_PER_TILE도 같이 올려야 화면 보간
   * (MS_PER_TILE 기준)과 실제 송신 주기(하한)가 어긋나지 않는다.
   *
   * 슬로우 상태는 여기서도 덮지 못한다 — 요구가 438ms로 늘지만 클라이언트는
   * 자기가 슬로우인지 알 수 없다 (ATTACK_GATE_MARGIN 주석 참조).
   *
   * 무기코드를 특정할 수 없는 사정은 공격과 같아서 후보 중 가장 느린 값을 쓴다
   * (attackActCandidates 주석 참조). 다만 확인한 플레이어 gfx 14종은 **모든**
   * 걷기 act_id가 640ms로 같아서, 현재 데이터에서는 어느 후보를 골라도 결과가
   * 같다 — 공격 쪽처럼 실제로 어긋나지는 않는다.
   */
  private moveGateMs(): number {
    const gfxId = gameState.getPlayerGfxId();
    let base: number | null = null;
    if (gfxId !== null) {
      // 이동은 act_id = 무기코드 그대로 (getCurrentWeapon()). 공격 후보에서 +1을 뺀다.
      for (const actId of this.attackActCandidates()) {
        const speed = moveSpeedOf(gfxId, actId - 1);
        if (speed === null) { base = null; break; }
        if (base === null || speed > base) base = speed;
      }
    }
    const needed = (base ?? SLOWEST_MOVE_MS) / SERVER_CHECK_STRICTNESS;
    return Math.max(MS_PER_TILE, needed * MOVE_GATE_MARGIN);
  }

  /** 지금 새 걸음(= C_MoveChar)을 시작해도 서버 판정을 통과하는가. */
  private canStepNow(): boolean {
    return performance.now() - this.stepStartedAt >= this.moveGateMs();
  }

  /**
   * 지정한 heading 방향으로 인접한 한 타일만큼 이동을 시작한다.
   *
   * 서버 프로토콜(C_MoveChar): 서버는 클라이언트가 보낸 (x, y)를 **이동 전 위치**로
   * 보고 heading 방향으로 1타일 더한 곳을 새 위치로 저장한다. 따라서 스텝을
   * 시작하는 이 시점에 "출발 타일 + heading"을 정확히 1회 보내야 한다.
   */
  private beginStep(heading: number): boolean {
    const vec = HEADING_VECTORS[heading];
    if (!vec) return false;

    const nextX = this.clampTile(this.playerTileX + vec.dx);
    const nextY = this.clampTile(this.playerTileY + vec.dy);

    this.currentHeading = heading;
    // 새 heading으로 SVG 텍스처를 미리 바꿔둔다 (frame은 update에서 advance).
    const newDirection = headingToDirection(heading);
    if (newDirection !== this.playerDirection || this.playerFrame !== 0) {
      this.playerDirection = newDirection;
      this.playerFrame = 0;
      this.playerWalkFrameTimer = 0;
      this.applyPlayerTexture();
    }

    if (!this.canEnterTile(nextX, nextY)) {
      // 막혔다 — 클릭 이동이었다면 목적지와 경로를 함께 포기한다.
      // 벽은 handleMovementInput이 스텝 전에 걸러 한 번 재탐색하므로, 여기까지
      // 오는 것은 주로 그 타일에 다른 엔티티가 서 있는 경우다. 경로를 다시 만들어도
      // findPath는 엔티티를 모르니 같은 경로가 나온다 — 그래서 재탐색하지 않는다.
      this.clearMoveTarget();
      return false;
    }

    // 보간 준비 (서버/GameState 갱신 전에 도착 타일을 확정해둬야
    // syncPlayerTileFromServer()가 자기 에코를 스냅으로 오인하지 않는다).
    const from = tileToWorld(this.playerTileX, this.playerTileY);
    const to = tileToWorld(nextX, nextY);
    this.stepFromX = from.x;
    this.stepFromY = from.y;
    this.stepToX = to.x;
    this.stepToY = to.y;
    this.stepToTileX = nextX;
    this.stepToTileY = nextY;
    this.stepElapsed = 0;
    // 아래 sendMovePacket()과 같은 시각을 기준으로 삼는다. 다음 스텝은 이 시각에서
    // MS_PER_TILE이 실제로 지난 뒤에야 시작되고, 그것이 곧 C_MoveChar 송신 간격이다.
    this.stepStartedAt = performance.now();
    this.isStepping = true;

    // 서버로는 "출발 타일 + heading"을 보낸다 (타일 좌표, 스텝당 정확히 1회).
    this.sendMovePacket(this.playerTileX, this.playerTileY, heading);

    // GameState/미니맵에는 도착 타일을 반영한다 (서버가 저장할 값과 동일).
    gameState.updatePlayerPosition(nextX, nextY, heading);
    this.miniMap.setPlayerPosition(nextX, nextY, heading);

    return true;
  }

  /**
   * 해당 타일로 진입할 수 있는지 (벽/장애물 + 다른 엔티티 점유 여부).
   * 타일 단위이므로 "그 타일에 누가 서 있으면 진입 불가"로 단순화한다.
   */
  private canEnterTile(tx: number, ty: number): boolean {
    if (this.currentMapId !== null && !mapCollision.isPassable(this.currentMapId, tx, ty)) {
      return false;
    }
    return this.findBlockingEntity(tx, ty) === null;
  }

  /**
   * 해당 타일에 서 있는 몬스터/NPC/다른 플레이어를 반환한다 (없으면 null).
   */
  private findBlockingEntity(tx: number, ty: number): EntitySprite | null {
    for (const entity of this.entitySprites.values()) {
      if (entity.tileX === tx && entity.tileY === ty) return entity;
    }
    return null;
  }

  /**
   * 이동 패킷 전송. (x, y)는 반드시 **서버 타일 좌표**이며 이동 전 위치다.
   * 타일 스텝마다 1회씩만 호출되므로 여기서 따로 쓰로틀하지 않는다 — 전송 주기는
   * 스텝 자체의 주기(MS_PER_TILE = 340ms, 하한이 더 크면 moveGateMs())가 정한다.
   */
  private sendMovePacket(x: number, y: number, heading: number): void {
    if (!this.connectionManager || !this.connectionManager.isConnected()) return;

    this.connectionManager.send({
      type: 'move',
      payload: {
        x: this.clampTile(x),
        y: this.clampTile(y),
        heading: heading
      }
    });
  }

  update(_time: number, delta: number): void {
    // 진행 중인 타일 스텝 보간 (UI 상태와 무관하게 항상 완주시킨다)
    this.updateStep(delta);

    // 쫓아가던 적에 닿았는지 확인 — 목적지를 정하므로 이동 판단보다 먼저 한다.
    this.updateAttackChase();

    // 게이트에 막혀 미뤄둔 공격이 있으면 열리는 순간 쏜다.
    // (updateAttackChase가 방금 예약했더라도 게이트가 닫혀 있으면 그냥 지나간다.)
    this.updatePendingStrike();

    // 주우러 걸어가던 아이템에 닿았는지 확인 (도착하면 걸음을 멈추고 줍는다).
    this.updatePendingPickup();

    // 새 스텝 시작 여부 판단
    this.handleMovementInput();

    // 다른 엔티티의 타일 간 이동 보간
    this.updateEntityMovement(delta);

    // 걷기 애니메이션 진행 (이동 중인 엔티티/플레이어의 frame을 advance)
    this.updateWalkAnimations(delta);

    // 카메라 부드러운 따라가기 (플레이어의 월드 좌표를 추적).
    // Phaser의 카메라 중심은 zoom과 무관하게 (scrollX + width/2, scrollY + height/2)
    // 이므로(worldView만 width/zoom으로 좁아진다) 줌을 걸어도 이 식은 그대로 맞다.
    const cam = this.cameras.main;
    const lerp = 0.1;
    cam.scrollX += (this.cameraTargetX - cam.scrollX - cam.width / 2) * lerp;
    cam.scrollY += (this.cameraTargetY - cam.scrollY - cam.height / 2) * lerp;

    // 타일 배경 갱신 (카메라가 같은 타일에 머무는 동안은 내부적으로 스킵됨)
    this.updateTileGrid();
  }

  /**
   * 다른 엔티티의 화면 위치를 목표 타일까지 보간한다.
   *
   * 논리 좌표는 이미 갱신돼 있고 여기서는 그림만 따라가게 한다.
   * 보간이 끝나면 상태를 지워 다음 프레임부터는 계산하지 않는다.
   */
  private updateEntityMovement(delta: number): void {
    this.entitySprites.forEach((entity) => {
      const move = entity.move;
      if (!move) return;

      move.elapsed += delta;
      const t = Math.min(1, move.elapsed / move.duration);
      const x = move.fromX + (move.toX - move.fromX) * t;
      const y = move.fromY + (move.toY - move.fromY) * t;

      entity.sprite.setPosition(x, y);
      entity.shadow.setPosition(x, y);
      entity.nameText.setPosition(x, y - ENTITY_LABEL_OFFSET);

      if (t >= 1) {
        entity.move = undefined;
        // 보간이 끝났으면 걷기도 끝이다.
        //
        // 서버는 "멈췄다"를 따로 알려주지 않는다 — 이동 패킷이 더 오지 않을 뿐이다.
        // 그래서 이동 패킷을 받을 때만 isWalking을 끄면, 한 번 걸은 몹은 제자리에서
        // 영원히 걷는 그림이 된다. 보간 시간은 직전 패킷 간격으로 잡혀 있으므로
        // 계속 걷는 중이라면 다음 패킷이 거의 동시에 도착해 다시 켜진다.
        entity.isWalking = false;
      }
    });
  }

  /**
   * 매 프레임 호출되어 이동 중인 모든 엔티티(자기 포함)의 walking frame을 진행시킨다.
   *
   * 각 엔티티는 자신의 walkFrameTimer에 delta를 누적하고, WALK_FRAME_INTERVAL_MS를
   * 넘기면 frame을 0→1→2→3→4→0 순으로 순환시킨다. 멈춘 엔티티는 isWalking=false로
   * 표시되어 있으므로 frame 0(idle)에서 변화가 없다.
   *
   * **자기 캐릭터와 몹이 같은 코드를 쓴다.** 프레임을 진행시키는 규칙은
   * advanceWalkFrame() 한 곳에 있고, "그 프레임을 어떤 그림으로 그리는가"만
   * 콜백으로 갈린다 (플레이어는 캐릭터 아틀라스, 엔티티는 applyEntityFrame이
   * 캐릭터/몹을 다시 분기).
   *
   * **사망 애니메이션이 우선이다.** deathAnim이 설정된 엔티티는 walking 대신
   * calcDeathFrame(startedAt, now)으로 frame을 계산하고 텍스처를 교체한다.
   * 마지막 frame(묘비)에 도달하면 그대로 고정된다. 다만 몹은 쓸 사망 그림이
   * 없으므로 프레임을 진행시키지 않고 tween 연출로 대신한다.
   */
  private updateWalkAnimations(delta: number): void {
    const now = this.time.now;

    // ===== 플레이어 =====
    if (this.playerDeathAnim) {
      // 사망 애니메이션 진행 중 — walking보다 우선.
      this.playerIsWalking = false;
      this.advancePlayerDeathFrame(now);
    } else {
      // 살아있는 상태: 한 타일 스텝이 진행 중이면 walking, 아니면 idle
      this.playerIsWalking = this.isStepping;
      if (this.playerIsWalking) {
        this.advanceWalkFrame(
          delta,
          () => this.playerFrame,
          () => this.playerWalkFrameTimer,
          (t) => { this.playerWalkFrameTimer = t; },
          (frame) => this.applyPlayerFrame(frame),
        );
      } else if (this.playerFrame !== 0) {
        this.applyPlayerFrame(0);
      }
    }

    // ===== 다른 엔티티들 (캐릭터든 몹이든 같은 경로) =====
    this.entitySprites.forEach((entity) => {
      if (entity.deathAnim) {
        // 몹의 쓰러짐은 startEntityDeathAnimation()의 tween이 끝까지 처리한다.
        if (!entity.deathAnim.mob) this.advanceEntityDeathFrame(entity, now);
        return;
      }
      if (entity.isWalking) {
        this.advanceWalkFrame(
          delta,
          () => entity.frame,
          () => entity.walkFrameTimer,
          (t) => { entity.walkFrameTimer = t; },
          (frame) => this.applyEntityFrame(entity, frame),
        );
      } else if (entity.frame !== 0) {
        this.applyEntityFrame(entity, 0);
      }
    });
  }

  /**
   * 플레이어의 프레임을 바꾸고 그림을 다시 붙인다.
   * 엔티티 쪽 applyEntityFrame()과 짝을 이루는, 플레이어용 단일 창구다.
   */
  private applyPlayerFrame(frame: number): void {
    this.playerFrame = frame;
    this.playerCurrentTextureKey =
      `char|${this.playerCharacterId}|${this.playerWeapon}|${this.playerDirection}|${frame}`;
    this.applyPlayerTexture();
  }

  /**
   * 플레이어의 사망 애니메이션 frame을 한 칸 진행시키고 텍스처를 교체한다.
   * 묘비(final frame)에서는 더 이상 advance하지 않고 그대로 고정한다.
   */
  private advancePlayerDeathFrame(now: number): void {
    const anim = this.playerDeathAnim;
    if (!anim) return;

    const frame = calcDeathFrame(anim.startedAt, now);
    const key = `${anim.charId}|${anim.direction}|${frame}`;

    if (anim.lastTextureKey !== key) {
      anim.lastTextureKey = key;
      if (applyDeathSprite(this, this.playerSprite, anim.charId, anim.direction, frame)) {
        this.playerSprite.setVisible(true);
        this.playerFallbackText.setVisible(false);
      } else {
        loadCharDeathAtlas(this, anim.charId)
          .then(() => {
            // 그 사이에 부활했거나 다른 텍스처로 바뀌었으면 적용하지 않는다.
            if (this.playerDeathAnim === anim
                && this.playerDeathAnim.lastTextureKey === key) {
              if (applyDeathSprite(this, this.playerSprite, anim.charId, anim.direction, frame)) {
                this.playerSprite.setVisible(true);
                this.playerFallbackText.setVisible(false);
              }
            }
          })
          .catch(() => {
            // 로드 실패 — fallback(P) 텍스트로 가시성이라도 유지.
            this.playerFallbackText.setText('P');
            this.playerFallbackText.setVisible(true);
          });
      }
    }
    // frame 4(묘비)에서 추가로 walking 텍스처로 되돌리지 않도록 여기서 종료.
  }

  /**
   * 일반 엔티티의 사망 애니메이션 frame을 한 칸 진행시키고 텍스처를 교체한다.
   * 엔티티의 deathAnim은 한 번 시작되면 그대로 두고, 묘비에서 고정한다.
   */
  private advanceEntityDeathFrame(entity: EntitySprite, now: number): void {
    const anim = entity.deathAnim;
    if (!anim) return;

    const frame = calcDeathFrame(anim.startedAt, now);
    const key = `${anim.charId}|${anim.direction}|${frame}`;

    if (anim.lastTextureKey === key) return;
    anim.lastTextureKey = key;

    if (applyDeathSprite(this, entity.sprite, anim.charId, anim.direction, frame)) {
      entity.sprite.setVisible(true);
      return;
    }
    loadCharDeathAtlas(this, anim.charId)
      .then(() => {
        // 살아있는 동안 다른 텍스처로 바뀐 경우나 제거된 경우 무시.
        if (entity.deathAnim === anim
            && entity.deathAnim.lastTextureKey === key
            && this.entitySprites.has(entity.objId)) {
          if (applyDeathSprite(this, entity.sprite, anim.charId, anim.direction, frame)) {
            entity.sprite.setVisible(true);
          }
        }
      })
      .catch(() => { /* fallback 유지 */ });
  }

  /**
   * 플레이어의 사망 애니메이션을 시작한다. GameState의 hp_update 또는
   * 테스트용 dev hook(`window.__testDeathPlayer`)에서 호출된다.
   */
  public startPlayerDeathAnimation(): void {
    if (this.playerDeathAnim) return; // 이미 진행 중이면 무시

    // 진행 중이던 클릭 이동은 무의미 — 사망 시 이동 차단은 handleMovementInput에서
    // gameState.isDead()로 이미 처리된다. 여기서는 시각 효과만 시작.
    this.clearMoveTarget();

    this.playerDeathAnim = {
      startedAt: this.time.now,
      charId: this.playerCharacterId,
      direction: this.playerDirection,
      lastTextureKey: null,
    };
    this.playerIsWalking = false;
    this.playerWalkFrameTimer = 0;
  }

  /**
   * 부활 시 사망 애니메이션 상태를 해제하고 다음 frame부터 idle로 돌린다.
   * 다음 update()에서 playerDeathAnim이 null이므로 walking/idle 경로를 탄다.
   */
  public clearPlayerDeathAnimation(): void {
    if (!this.playerDeathAnim) return;
    this.playerDeathAnim = null;
    this.playerFrame = 0;
    this.playerWalkFrameTimer = 0;
    this.applyPlayerTexture();
  }

  /**
   * 일반 엔티티의 사망 애니메이션을 시작한다 (테스트 또는 서버 브로드캐스트용).
   *
   * 몹은 쓸 사망 그림이 없다 — 사망 아틀라스는 캐릭터 14종뿐이라 그대로 쓰면
   * 몬스터가 죽는 순간 왕자 시체로 바뀐다. 그래서 몹은 마지막으로 보여주던
   * 프레임을 그대로 두고 기울이며 흐려지는 tween 하나로 끝낸다.
   */
  public startEntityDeathAnimation(objId: number): void {
    const entity = this.entitySprites.get(objId);
    if (!entity || entity.deathAnim) return;

    const isMob = entity.kind === 'mob';

    entity.deathAnim = {
      startedAt: this.time.now,
      charId: entity.characterId,
      direction: entity.direction,
      lastTextureKey: null,
      mob: isMob,
    };
    entity.isWalking = false;
    entity.walkFrameTimer = 0;
    entity.frame = 0;

    if (!isMob) return;

    // 왼쪽/오른쪽 어느 쪽으로 쓰러질지는 보고 있던 방향에 맞춘다.
    const fallAngle = directionNeedsFlip(entity.direction) ? -78 : 78;
    this.tweens.add({
      targets: entity.sprite,
      angle: fallAngle,
      alpha: 0.55,
      duration: DEATH_FRAME_DURATION_MS * DEATH_FRAME_COUNT,
      ease: 'Quad.easeIn',
    });
  }

  /**
   * 한 엔티티의 frame을 한 칸 진행시키고, 새 프레임을 그림에 반영한다.
   *
   * 프레임 진행 규칙(누적 시간 → 다음 프레임)만 여기 있고, "그 프레임을 어떤
   * 그림으로 그리는가"는 applyFrame 콜백이 정한다. 덕분에 플레이어(캐릭터
   * 아틀라스)와 엔티티(캐릭터 아틀라스 또는 몹 SVG)가 이 함수 하나를 공유한다.
   */
  private advanceWalkFrame(
    delta: number,
    getFrame: () => number,
    getTimer: () => number,
    setTimer: (n: number) => void,
    applyFrame: (frame: number) => void,
  ): void {
    const t = getTimer() + delta;
    if (t < WALK_FRAME_INTERVAL_MS) {
      setTimer(t);
      return;
    }
    setTimer(0);

    // 0→1→2→3→4→0. 캐릭터와 몹의 프레임 수는 같은 규약(5장)이다.
    applyFrame((getFrame() + 1) % FRAME_COUNT);
  }
}
